import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAnswerRetry } from './agents/useAnswerRetry'
import { settleRoster, agentPresent, newlyCreatedRow, sessionGone, terminalPresent } from './agents/settleRoster'
import { stageState, type Expecting } from './agents/stageState'
import { stopKeyFor } from './agents/stopKey'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from './transport/http'
import { InstallSectionLazy as InstallSection } from './install/InstallSectionLazy'
import {
  vaultOpen,
  agentMarkRead,
  projectMarkRead,
  agentSearch,
  getSecurityStatus,
  projectFileUpload,
  PROJECT_FILE_MAX,
  noteFileUpload,
  NOTE_FILE_MAX,
  type NoteAttachment,
  sendProjectMessage,
  getProjectMembers,
  getFleetIdentity,
  type AgentRow,
  type ProjectRow,
  type TerminalRow,
  terminalClose,
} from './transport/api'
import { CloseTerminalButton } from './terminal/CloseTerminalButton'
import { takeGroupLink } from './groups/groupLink'
import { takeInviteLink } from './friends/inviteLink'
import { InviteWelcome } from './friends/InviteSheet'
import { GroupLinkSheet } from './groups/GroupLinkSheet'
import { Icon } from './ui/Icon'
import { ServerPanel } from './shell/ServerPanel'
import { Sidebar } from './sidebar/Sidebar'
import { ConciseButton } from './sidebar/ConciseButton'
import { useSidebarResize } from './sidebar/useSidebarResize'
import { useViewport } from './shell/useViewport'
import { ViewportProbe, wantsViewportProbe } from './shell/ViewportProbe'
import { useStatus } from './shell/useStatus'
import { configureSbDrag, sbDrag } from './sidebar/sbDrag'
import { useThumbDrag } from './terminal/useTermGestures'
import { ProjectPanel } from './chat/ProjectPanel'
import { filesFromClipboard } from './chat/clipboardFiles'
import { useManagedFriendDm } from './chat/useManagedFriendDm'
import { dmOpenMessage } from './chat/dmOpenMessage'
import { AgentPanel } from './agents/AgentPanel'
import {
  asViewMode,
  stagesBeforeSending,
  stickyKind,
  viewForKind,
  type ViewMode,
} from './agents/viewMode'
import { SearchStrip } from './agents/search/SearchStrip'
import { closeSearch, openSearch, setSearchQuery, setSearchTarget, stepSearch } from './agents/search/searchStore'
import { keywordsOf } from './agents/search/useCleanSearch'
import { MessageResults } from './sidebar/MessageResults'
import {
  accountSearch,
  forgetAgentShares,
  linkSharedAgent,
  shareErrText,
  type AccountSearchResult,
  type IncomingAgentShare,
  type SharedKey,
} from './transport/relay'
import { pressShared, sendToShared, stopShared as stopSharedAgent } from './friends/sharedSend'
import { SharedAgentPanel } from './friends/SharedAgentPanel'
import { SharedFilesDock } from './friends/SharedFilesDock'
import { SharedAgentCard } from './friends/SharedAgentCard'
import { SharedTabCorner } from './friends/SharedTabCorner'
import { useAgentShares } from './friends/agentShares'
import { useSharedLive } from './friends/sharedSession'
import { swallowsTyping } from './agents/promptGuard'
import { useBusyHeartbeat } from './agents/useBusyHeartbeat'
import { PromptSheet } from './agents/PromptSheet'
import { loadKinds, saveKind } from './agents/kindMemory'
import { needsInputFor, presenceFor, useIdleStatus } from './agents/useIdleStatus'
import { conversationOnScreen, markReadRefusal, nextMarkRead } from './agents/markRead'
import { PixelPet } from './identity/PixelPet'
import { ProjectAvatar } from './identity/ProjectAvatar'
import { AvengentsMark } from './identity/AvengentsMark'
import { makeDevicesHandler } from './servers/devicesChanged'
import { PairApprove } from './pair/PairApprove'
import { onAccountRev, onDevices } from './servers/commandStream'
import { makeAccountRevHandler } from './servers/accountRev'
import { StartAgentSheet } from './servers/StartAgentSheet'
import { AgentMention, findMention, mentionRowsOfMembers, mergeRoster, pingTag, type MentionAgent } from './agents/AgentMention'
import { fetchSession } from './auth/googleSession'
/*
 * The project push lane is a MODULE-LOAD side effect, and it now lives in its own
 * leaf (chat/projectPushLane.ts) instead of in this file's body. Same import graph,
 * same registration moment — but the effect is NAMED, so a test that imports App can
 * mock one module instead of inheriting an anonymous global listener. This was the
 * census's example of why nothing could import App.tsx.
 */
import './chat/projectPushLane'
import { projectEcho } from './chat/projectEcho'
import { setSentEcho } from './agents/live/sentEchoStore'
import { onEdgeEvent } from './servers/commandStream'
import { KeysMenu } from './terminal/KeysMenu'
import { AgentCard } from './agents/AgentCard'
import { whyRestartFailed } from './agents/restartError'
import { ArtifactViewer, type ArtifactSubject } from './artifacts/ArtifactViewer'
import { GameHost } from './games/GameHost'
import { BoardScreen } from './board/BoardScreen'
import { RoomCorner, type RoomTool } from './board/RoomCorner'
import { BoardDrawer, RoomBoardDrawer } from './board/BoardDrawer'
import { useAgentRooms } from './board/agentBoards'
import { closeBoard, MAIN_BOARD, onBoardNav, openBoardId } from './board/boardNav'
import { AgentPagesDrawer, PageScreen, PagesDrawer } from './page/PageScreen'
import { closePages, onPageNav, onRoomPage, openPages, openPagesConv, pagesOn } from './page/pages'
import { resolvePing } from './agents/pingAgent'
import { PingContext, type PingContextValue } from './agents/pingContext'
import { ProjectDockFiles } from './shell/ProjectDockFiles'
import { productForKind } from './agents/agentKinds'
import { machineNameOf } from './agents/machineName'
import { agentKill } from './transport/api'
import { askAgentReadGuide } from './transport/api'
import { onPty, whenStreamTrusted } from './servers/commandStream'
import { useVoiceRecorder, MAX_RECORD_MS } from './voice/useVoiceRecorder'
import { registerMicHotkey, MIC_HOTKEY_LABEL } from './voice/micHotkey'
import { transcribeBlob } from './voice/transcribe'
import { insertAtCaret } from './voice/insertAtCaret'
import { caretAfterDictation } from './voice/caretAfterDictation'
import { micGate, recordCapMs, reportVoiceError } from './billing/voiceGate'
import { QuotaSheet } from './billing/QuotaSheet'
import { ShellDrawerLazy as ShellDrawer } from './terminal/ShellDrawerLazy'
import { getAgentsEverywhere, idleKey, mergeEverywhere, type Everywhere } from './agents/everywhere'
import { fleetForIdentity } from './agents/fleetIdentity'
import { NotePanel } from './notes/NotePanel'
import { BookmarkPanel } from './agents/bookmarks/BookmarkPanel'
import { roomJumpSession, toPanelRow, useRoomBookmarks } from './chat/useRoomBookmarks'
import { openBoardOn } from './board/boardNav'
import { useBookmarks } from './agents/bookmarks/useBookmarks'
import { jumpTo, shownFile } from './agents/bookmarks/jump'
import { NavBar, type NavTab } from './shell/NavBar'
import { ActivityPanel } from './activity/ActivityPanel'
import { MorePanel, type MorePage } from './shell/MorePanel'
import { MoreStage } from './shell/MoreStage'
import { AgentTabCorner } from './shell/AgentTabCorner'
import { agentKey as tabHolderKey } from './agents/lastTab'
import { activityUnread } from './activity/feed'
import { agentsWithUnread, postAppBadge } from './shell/appBadge'
import { noteAbsences, trackLiveSessions } from './notes/ghosts'
import { attachDispatchText, withheldNotice } from './notes/attachText'
import {
  getNotes,
  syncCursorTheme,
  postAgentSay,
} from './transport/api'
import { useDpad } from './terminal/useDpad'
import { FileBrowser } from './files/FileBrowser'
import { QueueBell } from './agents/QueueBell'
import { GuideGate } from './agents/GuideGate'
import { isGlobalSearchKey } from './shell/globalSearchKey'
import {
  guideAutoApprove,
  ignoreGuide,
  mentionNeedsGuide,
  setGuideAutoApprove,
  shouldPromptGuide,
} from './agents/guidePrompt'
import { FileViewer } from './files/FileViewer'
import { OpenDocContext, docKind, type OpenDoc } from './files/openDoc'
import { YouTubeViewer } from './ui/YouTube'
import type { YouTubeVideo } from './ui/youtubeLink'
import type { FsKind } from './transport/api'
import { getConfig, getProjects, getFriendRequests } from './transport/api'
import { onChatv1Frame, onChatv1Open } from './chatv1/ws'
import { listConversations, type ConversationRow } from './chatv1/transport'
import { activeDms, dmPreviewByEmail, dmUnreadByEmail, dmUnreadTotal, withDmCursor } from './chat/dmUnread'
import { roomTitles } from './chat/roomTitles'
import { atMostEvery } from './shell/atMostEvery'
import { chatv1SelfEmail } from './chatv1/auth'
import { relayTarget, setRelayTarget } from './transport/relay'
import { parse, useUrlState, type Selection } from './urlState'
import { registerPushOpener } from './push/pushNav'
import { refreshPushRegistration, setPushPref } from './push/pushOptIn'
import { PaneStrip } from './shell/PaneStrip'
import { isPaneFrame } from './shell/paneMode'
import { useSplitScreen } from './shell/useSplitScreen'
import { canSplit, closePane, maxPanes, placePane } from './shell/panes'
import { draftKey, readDraft, writeDraft } from './shell/drafts'
import type { TerminalSocket } from './transport/ws'
import { formatBytes } from './ui/bytes'
import { planSend } from './chat/sendPlan'
import { inOpenSheet } from './ui/enterSends'
import { limitRefusalIn } from './groups/limitText'

/**
 * Phase 1 in progress. Structure and ids mirror avengents-app/static/index.html;
 * layout is ported in src/shell/shell.css rather than reinterpreted.
 *
 * The composer is SHARED across terminal / DM / project views — those are view swaps
 * of the same box (body.dm-open / body.proj-open hide #term), not overlays. That is
 * why there is one composer, not three.
 */

export function App() {
  const [sbOpen, setSbOpen] = useState(true)
  /**
   * Emptiness of the (uncontrolled) composer — the ONLY thing the render needs
   * from the composer's live value. It is a BOOLEAN, not the text, so a keystroke
   * flips it at most once (empty↔non-empty) instead of re-rendering App on every
   * character.
   *
   * There used to be a `text` state beside it, and it was WRITE-ONLY: four call
   * sites set it and nothing ever rendered it, because the composer went
   * uncontrolled and the dictation path that fed it was never migrated. It went
   * with the voice stack on 2026-09-02. Nothing here should reintroduce a mirror
   * of the composer's value — the DOM node is the value. See the composer helpers below and the textarea's onChange:
   * making the box uncontrolled is the fix for mobile "輸入超卡" (each keystroke
   * used to re-render the whole ~1,500-line App on a phone CPU).
   */
  const [hasText, setHasText] = useState(false)
  /** When the composer was last typed into — the input half of the busy heartbeat. */
  const typedAt = useRef(0)
  const { state: url, write: writeUrl } = useUrlState()
  const qc = useQueryClient()
  /**
   * The conversation currently on screen, as a ref.
   *
   * The chat frame handler below needs to know it, and a ref keeps that handler
   * subscribed ONCE instead of tearing down and re-subscribing every time the
   * selection changes — a socket listener that churns is how a frame gets dropped
   * between the unsubscribe and the resubscribe. Written during render (see the
   * view-mode cache further down for the same pattern): idempotent, and it must not
   * schedule a render of its own.
   */
  const panelProjectId = useRef<string | null>(null)

  // When the server says this viewer's membership changed (added to a project by
  // someone's invite, or removed), refetch the list so it appears/disappears live —
  // the invited person does not have to reload to learn they were added.
  useEffect(() => {
    /*
     * Rate-capped, and it always progresses. The first version reset a timer on
     * every frame, which never fires while frames keep arriving — so the badge
     * starved for exactly as long as a burst lasted, the opposite of the "too many
     * requests" it was written to prevent. avg-latency read it and found that;
     * shell/atMostEvery.ts carries the full account.
     */
    const refetchDms = atMostEvery(250, () => {
      void qc.invalidateQueries({ queryKey: ['dm-conversations'] })
    })
    return onChatv1Frame((f) => {
      if (f.type === 'projects_changed') {
        void qc.invalidateQueries({ queryKey: ['projects'] })
        // Being added to / removed from a conversation changes the DM list too.
        void qc.invalidateQueries({ queryKey: ['dm-conversations'] })
        // …and who is in that room: the server now tells every member, so an open
        // roster on another device follows an add / remove / leave.
        void qc.invalidateQueries({ queryKey: ['proj-members', f.conv_id] })
      }
      /*
       * A message in a conversation this screen is NOT reading is exactly what the
       * Humans badge exists to report, and the count lives on the row, not on the
       * frame — so re-read the list rather than trying to increment it here. The
       * open room is excluded because its own hook already merged this message and
       * the mark-read effect is about to clear the row we would be refetching.
       *
       * RATE-CAPPED, because this fires for PROJECT traffic too — the socket carries
       * every room you are in, and a lively project would otherwise buy one refetch
       * per message to learn nothing about DMs. The frame does not say which kind it
       * is, and guessing from an id we may not hold yet would miss the case that
       * matters most (the first message of a brand-new DM), so the filtering is done
       * by the clock rather than by the frame.
       */
      if (f.type === 'message' && f.conv_id !== panelProjectId.current) refetchDms()
    })
  }, [qc])

  /*
   * RECONCILE THE LISTS WHEN THE SOCKET COMES BACK — owner, 2026-09-22: 使用者在機器A
   * 創建一個 Project Chat 的時候, 機器B 它裡面的 Project Chat 的那一個顯示的卡片並不會立刻
   * 的顯現出來, 而是那個卡片要等該機器裡面的 App 重開才會跳出來.
   *
   * The creation nudge is NOT missing. chat/lambda/http.ts:224-234 notifies the
   * CREATOR's own devices too — added 2026-09-15 for the previous round of this same
   * report. But `projects_changed` is a LIVE frame: notifyUser fans out to the sockets
   * that are open at that instant, and a device whose socket was closed (backgrounded
   * phone, sleeping laptop, a blip mid-create) is simply not in the fan-out. After
   * that nothing refetches the LIST — onChatv1Open was wired for message backfill
   * INSIDE an open room (chat/useProjectMessages.ts:164) and for busy state
   * (chat/useConvBusy.ts:52), never for the roster of rooms itself. So the row stays
   * missing until the query remounts, and remounting the app is exactly what
   * 「要等 App 重開」 describes.
   *
   * refetchOnWindowFocus cannot cover this: it is on globally and was MEASURED firing
   * nothing in the 700ms after a visibilitychange (sidebar/Sidebar.tsx:236-244). The
   * signal this codebase already trusts for "the connection is back" is this one.
   *
   * friend-requests rides along, and is the owner's second report the same day:
   * 使用者B的 humanist 那一個 menu 的按鈕並不會有提示的紅色數字跑出來. Its rows arrive over
   * the BFF rather than this socket, so this is not their push path — but a reopened
   * chat socket is the same "this device is live again" moment, and without it the
   * Group tab keeps showing whatever was true when the app went away.
   */
  /*
   * TELL THE SERVER THIS DEVICE'S CURRENT PUSH TOKEN, once per launch.
   *
   * HERE rather than in main.tsx beside armPushTaps, which is where it was first
   * proposed: this call is authenticated, and main.tsx runs before anyone is signed in,
   * so a boot-time call there would 401 for a cold start and never run again for
   * someone who signs in a moment later. App only renders inside <Authed>, so mounting
   * IS the signal that the session exists.
   *
   * It cannot prompt — pushOptIn.ts carries the argument — and it is a no-op write when
   * the token has not changed.
   */
  useEffect(() => {
    void refreshPushRegistration()
  }, [])

  useEffect(
    () =>
      onChatv1Open(() => {
        void qc.invalidateQueries({ queryKey: ['projects'] })
        void qc.invalidateQueries({ queryKey: ['dm-conversations'] })
        void qc.invalidateQueries({ queryKey: ['friend-requests'] })
      }),
    [qc],
  )

  /*
   * Devices, as the cloud changes them.
   *
   * The list is drawn first over HTTP — a socket that never opens must not mean a
   * blank screen — and this only ever replaces it. Both paths run the same mapping, so
   * the list drawn on open and the list drawn a second later cannot quietly disagree
   * about the same machines.
   */
  /*
   * The last device list we ACTED on, so a frame that changed nothing costs
   * nothing. See servers/devicesChanged.ts for the loop this closes.
   */
  /*
   * The handler owns the gate — see servers/devicesChanged.ts. It is one object
   * on purpose: the version with a separate `if (…) return` line in this effect
   * could have that line deleted, which restored the feedback loop with every
   * test still green. There is nothing left here to delete except the
   * subscription, and losing that stops the device list updating at all.
   */
  const devicesHandler = useRef(
    makeDevicesHandler({
      /*
       * This key holds a ServerList (from toServerList), NOT a device array —
       * it is the rail's live machine cache. The main menu once put its own
       * useQuery on this same key expecting {email, devices}, so a heartbeat
       * frame overwrote the rows (顯現一下就消失了) and later `.map`'d the
       * ServerList object (a white screen). The menu now owns a SEPARATE key;
       * this writer is restored to what it always was.
       */
      setDevices: (list) => qc.setQueryData(['account-devices'], list),
      refetchRail: () => {
        void qc.invalidateQueries({ queryKey: ['idle-status'] })
        void qc.invalidateQueries({ queryKey: ['agents'] })
        // Pins ride the same bell: a pin or a drag on another device pokes the
        // account watchers, the poke lands here as a devices frame, and the
        // Pinned section re-reads its order (owner, 2026-08-25: 跨 Device 同步).
        void qc.invalidateQueries({ queryKey: ['account-pins'] })
        /*
         * And so does the friend graph — owner, 2026-09-16: 在送出好友請求, 並且
         * 對方接受之後, friend 的頁面並不會自動更新. 狀態還停留在等待接受的狀態.
         * 除非跳轉之後再跳回來.
         *
         * A friendship is the one piece of account state that always changes for
         * TWO people at once, and until now only the client that acted knew: the
         * accepter invalidated its own list, the requester was told nothing and
         * sat on "Requested" until a remount happened to refetch it. The BFF now
         * pokes both sides on request/accept/reject/cancel; this is the ear.
         *
         * Not a new frame type on purpose. A bare poke means "re-read", it
         * already arrives here, and the pins above prove the path works —
         * inventing a `friends_changed` frame would have meant a new contract on
         * both sides to carry no more information than this.
         */
        void qc.invalidateQueries({ queryKey: ['friends'] })
        void qc.invalidateQueries({ queryKey: ['friend-requests'] })
        // Groups ring the same bell (routes_groups.py): an invitation, a request, a member in or out.
        void qc.invalidateQueries({ queryKey: ['groups'] })
        void qc.invalidateQueries({ queryKey: ['group'] })
      },
    }),
  )
  useEffect(() => onDevices((devices) => devicesHandler.current(devices)), [])

  /*
   * WHAT THE DEVICES FRAME DOES NOT SHOW — owner, 2026-10-07: 我發現當一個裝置 pin agent
   * 之後, 另一個裝置的狀態不會同步. 需要重啟才會同步.
   *
   * The invalidations of pins and friends inside refetchRail above only run when the
   * MACHINE list changed, and a pin changes no machine — so another device's pin was
   * heard and dropped (servers/accountRev.ts has the account). The cloud now moves
   * `account_rev` on exactly those writes; this is the ear.
   */
  const accountRevHandler = useRef(
    makeAccountRevHandler(() => {
      void qc.invalidateQueries({ queryKey: ['account-pins'] })
      void qc.invalidateQueries({ queryKey: ['friends'] })
      void qc.invalidateQueries({ queryKey: ['friend-requests'] })
      void qc.invalidateQueries({ queryKey: ['groups'] })
      void qc.invalidateQueries({ queryKey: ['group'] })
    }),
  )
  useEffect(() => onAccountRev((rev) => accountRevHandler.current(rev)), [])

  const [sending, setSending] = useState(false)
  /**
   * Why the last send failed, kept until it is resolved.
   *
   * This used to go to the status bar, which auto-hides after three seconds — so a
   * rejected send (a stale CSRF token after a server restart, say) looked exactly like
   * a button that does nothing. The text is already preserved; the REASON has to be
   * too, or the user has no way to find out what to do.
   */
  const [sendError, setSendError] = useState<string | null>(null)
  const { text: statusMsg, shown: statusShown, status } = useStatus()
  const resize = useSidebarResize()
  // The agent view owns the pty; App only needs to write into it.
  const termSock = useRef<TerminalSocket | null>(null)
  const [link, setLink] = useState<'connecting' | 'attached' | 'closed' | null>(null)
  /**
   * Clean View is where you arrive; the terminal is somewhere you go.
   *
   * The default was `terminal-view`, which showed a first-time reader a wall of pty
   * bytes for a product whose claim is that an agent is someone you talk to. Auto-
   * detect is the honest default rather than a specific rich view: it resolves per
   * agent from the kind the server reports, and it already falls back to the terminal
   * for anything it cannot name — a plain shell has no conversation to show, so
   * "default to Clean View" costs nothing where there is no clean view to give.
   *
   * A STORED value still wins, including `terminal-view`. Nothing writes that except
   * a tap on the chip, so finding one means the user chose it — which is exactly the
   * "unless they switch" half of the instruction. Changing the default must not
   * relitigate a decision somebody already made.
   */
  const [storedView, setStoredView] = useState<ViewMode>(() => {
    try {
      return asViewMode(localStorage.getItem('wt_view_mode')) ?? 'auto-detect'
    } catch {
      // localStorage unavailable (private mode, disabled): the default still applies.
      return 'auto-detect'
    }
  })
  // A `view` in the URL wins, so a shared link opens in the mode it names.
  const viewMode: ViewMode = asViewMode(url.view) ?? storedView


  const setViewMode = (m: ViewMode) => {
    setStoredView(m)
    /*
     * A PANE'S VIEW IS ITS OWN — owner, 2026-08-22: 視窗應該要記住當前的 Terminal /
     * Clean View 的狀態, 這樣在雙開的時候不會跑掉.
     *
     * `wt_view_mode` is one key on one origin, and every pane is the same origin — so
     * one pane switching to Clean View moved the FALLBACK for every other pane, and the
     * next one to reload came back in a mode nobody chose for it. The URL already wins
     * over the stored value, so a pane keeps its choice there and leaves the shared
     * preference to the shell, which is the surface it was written for.
     */
    if (!isPaneFrame()) {
      try {
        localStorage.setItem('wt_view_mode', m)
      } catch {
        /* preference just won't persist */
      }
    } else {
      // Tell the shell, so a pane recreated after a reload comes back in this mode.
      try {
        window.parent.postMessage({ wt: 'view', view: m }, window.location.origin)
      } catch {
        /* no shell to tell */
      }
    }
    writeUrl({ sel: url.sel, view: m }, 'replace')
  }

  const select = (sel: Selection) => {
    // The URL pins an agent by its stable (hash, device), not its mutable name.
    // Callers pass (session, host) — the handle they have — so resolve the
    // hash+device from the live agent list here, in ONE place, instead of threading
    // it through every opener. A selection that already carries a hash (a cold load
    // from a hash URL), or one with no matching row yet (a just-created session, or
    // a terminal, which has no channel hash), passes through unchanged.
    let next = sel
    if (sel?.kind === 'agent' && !sel.hash) {
      const row = agentsQ.data?.agents.find(
        (a) => a.session === sel.session && (a.host ?? null) === sel.host,
      )
      if (row) next = { ...sel, hash: row.hash, device: row.device_id ?? null }
    }
    /*
     * WHILE SPLIT, A TAP IN THE RAIL REPLACES THE FOCUSED PANE — owner, 2026-08-22:
     * 當分頁在 Focus 的時候, 如果使用者點擊其他的 Agent / Project Chat / Friend Chat
     * 則可以使用該頁面替換掉原本 focus 的分頁.
     *
     * Here rather than in each opener because `select` is the one chokepoint every one
     * of them already passes through — agents, projects and DMs alike — so there is no
     * fourth place to forget when a fourth kind arrives.
     *
     * Without this a rail tap while split wrote a URL the shell deliberately ignores
     * (its selection is cleared on the way into a split), so the tap did nothing at
     * all: the loudest possible way for a control to be broken.
     */
    if (split && activePane) {
      setPanes((ps) => ps.map((pane) => (pane.id === activePane ? { ...pane, sel: next } : pane)))
      return
    }
    writeUrl({ sel: next, view: url.view }, 'push')
  }
  const [filesOpen, setFilesOpen] = useState(false)
  const [projFilesOpen, setProjFilesOpen] = useState(false)
  /** The project's whiteboard, when the URL names it (`?board=<id>` — board/boardNav.ts). */
  const [boardConv, setBoardConv] = useState<string | null>(() => openBoardId())
  useEffect(() => onBoardNav(() => setBoardConv(openBoardId())), [])
  /** The room whose pages are open (?page=<conv>; page/pages.ts) — over the app, as its whiteboard is. */
  const [pagesConv, setPagesConv] = useState<string | null>(() => openPagesConv())
  useEffect(() => onPageNav(() => setPagesConv(openPagesConv())), [])
  const [projFileCount, setProjFileCount] = useState(0)
  const [rosterOpen, setRosterOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [gitOpen, setGitOpen] = useState(false)
  const [serverOpen, setServerOpen] = useState(false)
  /** A group ＋ → Group just made, for the Humans tab's Groups list to open (GroupsSection). */
  const [openGroupId, setOpenGroupId] = useState<string | null>(null)
  /** A group's link this browser was opened with (groups/groupLink.ts) — asked once, signed in. */
  const [groupLinkCode, setGroupLinkCode] = useState<string | null>(() => takeGroupLink())
  /** A person's invite link this browser was opened with (friends/inviteLink.ts) — claimed once, signed in. */
  const [inviteCode, setInviteCode] = useState<string | null>(() => takeInviteLink())
  // The note sheet's open-state sits with the overlays because conversationOnScreen reads
  // it (an open note covers the stage); the rest of the notes state is defined far below.
  // It lives here, not there, so the project mark-read effect can depend on it in scope.
  const [noteOpen, setNoteOpen] = useState(false)
  /** The bookmarks sheet (agents/bookmarks; owner, 2026-10-31) — beside the notes', for the same reason. */
  const [bmOpen, setBmOpen] = useState(false)
  /** The session whose whiteboard drawer is out (board/BoardDrawer; owner, 2026-11-01) — by agent, so it closes with
   *  the session; derived into `wbOpen` below, which conversationOnScreen reads on a phone. */
  const [wbFor, setWbFor] = useState<string | null>(null)
  /** The session whose pages drawer is out (page/PageScreen AgentPagesDrawer; owner, 2026-11-03) — as `wbFor`, into `pgOpen`. */
  const [pgFor, setPgFor] = useState<string | null>(null)
  /** The pairing camera has the screen. Kept beside serverOpen because it is opened
   *  from that panel, and read by conversationOnScreen so nothing is marked read
   *  while a full-screen camera covers the stage. */
  const [scanOpen, setScanOpen] = useState(false)
  /** The agent whose card is open, or null. Held here rather than in the sidebar so
   *  the sheet survives the rail being closed on a phone — the card is about an agent,
   *  not about the list it was opened from. */
  const [cardAgent, setCardAgent] = useState<AgentRow | null>(null)
  /**
   * The artifact open in the IN-APP BROWSER — owner, 2026-09-20: 「讓 Artifact 這一個
   * 欄位,使用者可以直接點擊並且使用 App 的 In-App Browser 打開」. It was a settings
   * sheet for one day; tapping a row now opens the page itself, and the settings
   * he asked for live in that viewer's header menu instead.
   *
   * ONE STATE, TWO SUBJECTS — owner, 2026-10-05: a friend's shared artifact opens in
   * this same viewer. It is either one of this account's own rows or one a friend
   * shares, and it stays ONE piece of state on purpose: everything that asks "is the
   * stage covered" (conversationOnScreen and both mark-read effects below) reads this
   * name, and a second state for the second subject would have to be remembered in
   * each of them.
   */
  const [viewArtifact, setViewArtifact] = useState<ArtifactSubject | null>(null)
  /**
   * The part of More open on the stage — owner, 2026-10-22: the rail holds one button per
   * part, and its list (then any item of it) is a page of the stage; a cover on a phone.
   * It covers the conversation the way the artifact viewer does, so it is read wherever
   * that is (conversationOnScreen and both mark-read effects).
   */
  const [morePage, setMorePage] = useState<MorePage | null>(null)
  /*
   * The kill lives HERE now, not in the sidebar, because the control that triggers it
   * does: the card outlives the rail on a phone, where opening a sheet closes the list
   * behind it. A mutation owned by an unmounted component cannot report its own
   * failure — the sheet would close on success and say nothing at all on refusal.
   */
  const killAgent = useMutation({
    mutationFn: ({
      session,
      host,
      device,
    }: {
      session: string
      host: string | null
      /** The machine it runs on. Without it the kill lands on whichever machine the
       *  relay is pointed at — see agentKill. */
      device?: string
      /*
       * TRUE here, and only here. This is the explicit "delete this agent" action,
       * which is what the owner was describing: 刪除 agent session 的時候應該也要考慮
       * 把 claude / codex / cursor / antigravity 的舊的 session 完整的刪除.
       *
       * The Terminals ✕ passes false — closing a leftover shell is not deleting an
       * agent, and that row has no conversation of its own to speak for.
       */
    }) => agentKill(session, host, device, true),
    onSuccess: (_r, v) => {
      /*
       * ITS "NOTIFY ME" SWITCH GOES WITH IT — owner, 2026-09-29: a deleted agent kept
       * pushing banners to his phone. The switch is keyed on (machine, hash) and the hash
       * is the NAME, so it outlived the agent: nothing could turn it off (the agent is no
       * longer in any list to open), and the next agent given that name would inherit
       * it. Best effort — the delete already happened; a failure here only leaves what
       * was there before.
       */
      const gone = qc
        .getQueryData<Everywhere>(['agents'])
        ?.agents.find((a) => a.session === v.session && (v.device ? a.device_id === v.device : true))
      const onDevice = v.device ?? gone?.device_id
      if (gone?.hash && onDevice) void setPushPref(onDevice, gone.hash, false).catch(() => {})
      /*
       * …AND SO DO ITS SHARES (owner, 2026-10-17: share an agent with a friend). Keyed by
       * the same name, they too would pass to the next agent given it — with a friend's
       * access to its conversation and folder. Same best effort, same reason.
       */
      if (onDevice && !v.host) {
        void forgetAgentShares(onDevice, v.session)
          .then(() => qc.invalidateQueries({ queryKey: ['agent-shares'] }))
          .catch(() => {})
      }
      /*
       * THE ROW LEAVES NOW. The machine has confirmed the kill, so removing the
       * row from the cache is not optimism, it is applying an answer already in
       * hand — waiting for a fleet sweep to repeat it was most of the ten
       * seconds the owner measured on delete (2026-08-30). settleRoster still
       * runs underneath as the verifier: if the session somehow survives, the
       * sweep puts the truth back.
       */
      qc.setQueryData<Everywhere>(['agents'], (prev) =>
        prev
          ? {
              ...prev,
              agents: prev.agents.filter(
                (a) => !(a.session === v.session && (v.device ? a.device_id === v.device : true)),
              ),
              terminals: prev.terminals.filter(
                (t) => !(t.session === v.session && (v.device ? t.device_id === v.device : true)),
              ),
            }
          : prev,
      )
      // Deleting now ends the tmux session outright, so the row must leave BOTH
      // columns — it used to survive as a respawned shell under Terminals, which
      // read as the delete not having taken.
      void settleRoster(qc, sessionGone(v.session))
      void qc.invalidateQueries({ queryKey: ['idle-status'] })
    },
  })
  /*
   * `guideChecked` used to live here — false until the roster had been re-asked for the
   * open `@`, so the sheet could not render from a cache that might be stale. Its stated
   * reason was sound (a wrong statement shown briefly is still a wrong statement) and
   * its cost was not: the re-ask is a fan-out to every paired machine, so the guard put
   * a multi-second wait in front of a dialog, measured at up to 5825ms. Owner, 2026-08-29:
   * 這應該是瞬間可以發生的事情. 但是我卻要等個好幾秒. The trade is now the other way —
   * draw from cache, correct underneath — and the reasoning lives at the effect that
   * does the correcting.
   */
  /**
   * The agent whose ARRIVAL prompt is open, by session — see agents/guidePrompt.
   *
   * Keyed by session rather than a boolean so that opening a second agent while the
   * first one's sheet is up replaces it instead of leaving a sheet that names the agent
   * you just left.
   */
  const [guidePromptFor, setGuidePromptFor] = useState<string | null>(null)
  /**
   * An upward swipe on ⌨ pops the raw-shell drawer, the same one Cmd/Ctrl+J opens.
   * `keysSwiped` survives until the click that follows, which it then swallows —
   * otherwise the gesture would also toggle the key row on the way past.
   */
  const keysStartY = useRef(0)
  const keysSwiped = useRef(false)
  /** Where a pull on #kbGrab began; 0 means no drag in progress. */
  const grabStartY = useRef(0)
  /** Where a pull on the rail's keyboard handle began; 0 means no drag. */
  const sbGrabY = useRef(0)
  /** Sidebar search. Names are matched locally; content comes from the server. */
  const [sbQuery, setSbQuery] = useState('')
  /*
   * THE GLOBAL MESSAGE SEARCH — owner, 2026-09-28: 改造 side panel 右下角的這一個搜尋按鈕,
   * 讓使用者可以按下這個搜尋按鈕, 就可以全域的搜尋他所有機器裡面所有的訊息. Typing keeps
   * filtering agents and projects as it always did (cheap, per keystroke); ENTER asks every
   * machine for the messages (each machine reads its conversations whole, seconds, not
   * milliseconds), and the answer is drawn at the top of the list.
   */
  const [msgQuery, setMsgQuery] = useState<string | null>(null)
  const msgSearch = useQuery({
    queryKey: ['account-search', msgQuery],
    queryFn: ({ signal }) => accountSearch({ keywords: keywordsOf(msgQuery ?? ''), limit: 50, max_text: 400 }, signal),
    enabled: !!msgQuery,
    staleTime: 60_000,
    retry: false,
  })
  /**
   * Debounced copy, because the content search greps tmux scrollback on every
   * mounted host. Names filter instantly off data already in the client; only the
   * server call waits.
   */
  const [sbQueryDebounced, setSbQueryDebounced] = useState('')
  useEffect(() => {
    const id = window.setTimeout(() => setSbQueryDebounced(sbQuery.trim()), 300)
    return () => window.clearTimeout(id)
  }, [sbQuery])

  /**
   * Phase 2: which agents have said this, from /agent/search.
   *
   * Two characters minimum. One character matches nearly every scrollback, so a
   * single letter would quietly turn the filter off while looking like it worked —
   * and that is the failure this whole field started as.
   */
  const contentQ = useQuery({
    queryKey: ['agent-search', sbQueryDebounced],
    queryFn: () => agentSearch(sbQueryDebounced),
    enabled: sbQueryDebounced.length >= 2,
    staleTime: 3_000,
  })
  const contentHits = useMemo(
    () => new Set((contentQ.data?.matches ?? []).map((m) => m.hash)),
    [contentQ.data],
  )
  /**
   * Files STAGED for sharing into the open project — attach / drop / paste all land
   * here first, exactly as the original does. Sending uploads them one request each,
   * with the composer text riding the first as its caption.
   */
  /**
   * Files waiting to be sent, each with its preview URL MADE ONCE.
   *
   * `url` is here rather than built in the JSX because it used to be
   * `<img src={URL.createObjectURL(s2.file)}>` — inside the render, with no
   * revoke anywhere in this file. Every re-render of App minted another blob for
   * the same staged image and abandoned the previous one; the browser holds the
   * bytes until the document goes away, so a staged photo leaked its own size per
   * render, on the surface most likely to be re-rendered (the composer).
   *
   * Made in stage(), released in dropStaged() and on unmount — one owner, one
   * lifetime, the same rule FileViewer's useObjectUrl follows.
   */
  type StagedFile = { file: File; id: string; pct: number; url?: string; err?: string }
  const [staged, setStaged] = useState<StagedFile[]>([])
  /** Mirror for the unmount sweep: an effect with [] deps cannot read live state. */
  const stagedRef = useRef<StagedFile[]>([])
  stagedRef.current = staged
  useEffect(
    () => () => {
      for (const s of stagedRef.current) if (s.url) URL.revokeObjectURL(s.url)
    },
    [],
  )
  /** Remove one staged file and release its preview. The ONLY way to drop one. */
  const dropStaged = (id: string) =>
    setStaged((prev) => {
      const going = prev.find((x) => x.id === id)
      if (going?.url) URL.revokeObjectURL(going.url)
      return prev.filter((x) => x.id !== id)
    })
  const projFileInput = useRef<HTMLInputElement>(null)

  const stage = (files: File[]) => {
    if (!files.length) return
    /*
     * Whose ceiling applies depends on where the file is going, and THE TWO ARE NOT THE
     * SAME. A note file chunks over the relay to the edge (100 MB); a project file is a
     * presigned S3 PUT through the chat service (25 MB). They were both 100 until
     * 2026-09-02, when it turned out the project constant had been mirroring the server
     * that M2 deleted — see PROJECT_FILE_MAX. Reading one while uploading to the other
     * admits files the far end will refuse.
     */
    /*
     * panelProject, NOT project — MY BUG, introduced this morning when the
     * paperclip learned about friend DMs.
     *
     * A DM uploads through projectFileUpload, the 25 MB presigned-S3 lane, while
     * this line measured it against the 100 MB NOTE ceiling. So a 40 MB file in a
     * friend chat staged happily and was refused by the far end at send time,
     * which is precisely the failure the comment above warns about — I widened the
     * button's gate and left the ceiling behind.
     */
    const max = panelProject ? PROJECT_FILE_MAX : NOTE_FILE_MAX
    setStaged((prev) => [
      ...prev,
      ...files.map((file, i) => ({
        file,
        id: `${Date.now()}-${i}-${file.name}`,
        pct: 0,
        // Only images are previewed, so only images get a blob — an object URL for a
        // PDF nothing renders would be a leak with no picture to show for it.
        ...(file.type.startsWith('image/') ? { url: URL.createObjectURL(file) } : {}),
        // formatBytes, not another 1024 ladder — this one predates ui/bytes.ts and is
        // the reason its guard also checks for a division that is not written `/= 1024`.
        ...(file.size > max ? { err: `larger than the ${formatBytes(max)} limit` } : {}),
      })),
    ])
  }
  /**
   * The rail's bottom nav (shell/NavBar).
   *
   * `navTab` is where the rail is; Activity is the only tab that opens a panel over
   * everything, so it is tracked separately — pressing Home has to be able to send
   * you back to the rail WITHOUT the panel still sitting on top of it, and a single
   * enum would have made "Home with Activity open" representable.
   */
  const [navTab, setNavTab] = useState<NavTab>('home')
  /*
    * ACTIVITY IS A TAB TOO, since 2026-09-20 — owner: 「把下方 Menu 的 Activity 欄位
    * 也使用整個把面板換成為它裡面所有內容的方法…就如同我們之前置換 More 這個按鈕一樣
    * 的行為」. It had its own flag for the reason written below, and that reason went
    * away with the sheet: a body cannot leave Home with "nothing to come back to",
    * because the rail is always showing exactly one body and `navTab` names it.
    */
  /*
   * MORE is a TAB, not a panel — it needs no state of its own.
   *
   * It was a panel for one day and the owner rejected the shape: 「按下 More 之後,
   * 你不應該使用彈跳出來一個新的蓋版頁面的方式,而是直接讓整個面板的底板換掉。因為
   * 我們之後會在 More 的面板裡面加上其他的功能,不只是 Artifact」. Activity earns its
   * separate flag because it is a notification surface you glance at and dismiss
   * back to wherever you were; More is somewhere you GO, and it will hold more
   * sections over time. So it swaps the rail's body, and `navTab` already says
   * which body the rail is showing.
   */
  /**
   * The search field is revealed by its tab rather than parked at the bottom of the
   * rail. It kept a row of height permanently for a control used in bursts; the tab
   * is both more discoverable (a labelled button, not a bare field) and gives the
   * list that row back.
   */
  const [searchOpen, setSearchOpen] = useState(false)
  const searchEl = useRef<HTMLInputElement>(null)
  /** The tab search was opened from — where its ✕ takes the rail back to. */
  const searchFrom = useRef<NavTab>('home')

  /** The answer-in-place sheet. Opens itself when the agent asks; see below. */
  const [promptOpen, setPromptOpen] = useState(false)
  const [newOpen, setNewOpen] = useState(false)
  /*
   * The two-step create is gone: the ＋ opens ONE sheet that asks for the machine
   * itself, so there is no "chosen server, now show the form" state between them.
   * The sheet reports which device it used when it succeeds.
   */
  const [keysOpen, setKeysOpen] = useState(false)
  /**
   * `keysLocked` pins ONLY our keys panel — never the phone's own keyboard. Persisted so
   * a tab that had it pinned comes back with the key row visible (but does NOT auto-pop
   * the soft keyboard on load).
   */
  const [keysLocked, setKeysLocked] = useState(() => {
    try {
      return localStorage.getItem('wt_keys_locked') === '1'
    } catch {
      return false
    }
  })
  // The d-pad is a narrow-screen affordance only; a desktop has real arrow keys.
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 760,
  )
  /**
   * Phone navigation: the rail is the ROOT screen and a selection pushes the
   * conversation over it, Slack-style. Deliberately a SEPARATE breakpoint from
   * `narrow` (760) because it must agree with the CSS at 900 — one breakpoint
   * driving the layout and another driving the behaviour is how you get a band
   * of widths where the two disagree and neither is wrong on its own.
   */
  const [phoneNav, setPhoneNav] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 900,
  )
  const [viewing, setViewing] = useState<{
    path?: string
    url?: string
    name: string
    kind: FsKind
    size: number | null | undefined
    /** A YouTube link playing in this cover panel instead of a file (ui/YouTube.tsx). */
    youtube?: YouTubeVideo
  } | null>(null)

  /** Open a project attachment in the same viewer the file browser uses. */
  /**
   * Open a file in the cover-panel FileViewer (same box as #term), NOT a new tab
   * — owner, 2026-08-14: 點擊檔案 / image 直接用與 agent terminal 一樣的方式蓋板
   * 顯示. Every kind the viewer can render is routed here now, so an image and a
   * markdown file land in the same in-app surface instead of image/video escaping
   * to `window.open`. The FileViewer already branches on FsKind and takes a `url`
   * override for a blob a caller already holds (project attachments), so this only
   * had to widen the kind it computes.
   */
  // useCallback: it is also the OpenDocContext value around Clean View, and a fresh
  // function per render would re-render every memoized transcript block on every
  // App render — the exact churn Msg's memo exists to prevent.
  const openDoc = useCallback((a: Parameters<OpenDoc>[0]) => {
    // ONE cover panel, so everything that asks "is the stage covered" already counts it.
    if (a.youtube) {
      setViewing({ url: a.url, name: a.name, kind: 'video', size: undefined, youtube: a.youtube })
      return
    }
    setViewing({ url: a.url, name: a.name, kind: docKind(a), size: undefined })
  }, [])
  /*
   * SPLIT STAGE — owner, 2026-08-22: 從 Side Panel 拖到主顯示欄位, 選擇位置 (左, 右),
   * 造成雙開, 或是多開.
   *
   * `paneFrame` is this document being ONE of those panes: the rail and the backdrop
   * belong to the shell around it. Read once from the URL, because it cannot change
   * without a navigation.
   */
  const paneFrame = isPaneFrame()
  useEffect(() => {
    // A class on <body>, not a prop threaded through the stage: the rules it switches
    // off are the shell's own (the rail's gutter, the backdrop), and they are spread
    // across a stylesheet rather than owned by one component.
    document.body.classList.toggle('pane-frame', paneFrame)
  }, [paneFrame])
  const [winW, setWinW] = useState(() => (typeof window === 'undefined' ? 0 : window.innerWidth))
  /*
   * SPLIT SCREEN moved to shell/useSplitScreen.ts in M7 — state, dock, transitions
   * and all of their comments. `winW` stays here because the resize listener below
   * refuses a second subscriber; the hook's foldIfNarrow rides that one listener.
   */
  const {
    panes, setPanes,
    activePane, setActivePane,
    railDrag, setRailDrag, railDragSel,
    setDockPaneId, dockPane,
    split, foldIfNarrow,
  } = useSplitScreen({ paneFrame, urlSel: url.sel, writeUrl })

  /*
   * THE RELAY FOLLOWS THE SELECTION, NOT ONLY THE CLICK.
   *
   * `openAgent` points the relay at the machine it opens — and it was the ONLY place
   * that did. Every selection that arrives some other way skipped it: a deep link, a
   * reload, back/forward, and above all a PANE, which is this whole app pointed at one
   * conversation BY ITS URL. So a pane simply inherited whatever machine was in the
   * shared `av_relay_device` key — localStorage is one key per ORIGIN and every pane is
   * the same origin, so that value belonged to whichever window wrote it last.
   *
   * Owner, 2026-08-28: 雙開無法顯示第二個視窗 Agent 的 Terminal. The screenshot showed a
   * login shell on `aowl` that had typed `tmux attach -t 'web-train-board-ec2'
   * 2>/dev/null` — that session lives on TBC-web-tools, the attach found nothing here,
   * and `2>/dev/null` swallowed the one sentence that would have explained the blank.
   *
   * Read from the URL rather than from the resolved row, deliberately: the selection
   * carries (hash, device) already, so this lands BEFORE the agent list arrives — the
   * terminal that mounts with it is never pointed at the wrong machine even briefly.
   *
   * RESTORED 2026-09-03: M7's split-screen extraction (6e8f4c7) swept this effect into
   * the deletion along with the adjacent dockPane→relay effect it meant to move, and
   * the commit claimed it "stayed with the shell" when it was gone entirely. That
   * dropped the ONLY setter a URL-restored pane iframe runs, so a freshly-dragged pane
   * inherited the last window's relay target and every relayed read/upload went to the
   * wrong machine — Clean View showed "No Claude transcript / 0 blocks", and image+text
   * uploads landed on the wrong edge (the owner's two bugs, both this one effect). It
   * lives here, unconditional, because a single pane frame and every deep link need it
   * too — never gate it on split or paneFrame.
   */
  useEffect(() => {
    const dev = url.sel?.kind === 'agent' ? (url.sel.device ?? null) : null
    if (dev) setRelayTarget(dev)
  }, [url.sel])

  /*
   * A PANE ANNOUNCES ITS OWN FOCUS. The shell cannot see a click inside an iframe, and
   * the trick of watching the parent's `blur` only catches the FIRST entry into a pane
   * — going straight from one pane to another never blurs the parent again. Capture
   * phase, because the pane's own handlers may stop propagation on the way down.
   */
  useEffect(() => {
    if (!paneFrame) return
    const tell = () => {
      try {
        window.parent.postMessage({ wt: 'focus' }, window.location.origin)
      } catch {
        /* no parent, or a different origin — then there is no shell to tell */
      }
    }
    document.addEventListener('pointerdown', tell, true)
    document.addEventListener('focusin', tell, true)
    return () => {
      document.removeEventListener('pointerdown', tell, true)
      document.removeEventListener('focusin', tell, true)
    }
  }, [paneFrame])

  /*
   * `?files=1` — this frame IS the dock, so it opens the browser as soon as it has a
   * conversation to browse. Waiting for the agent to resolve rather than firing on
   * mount: the browser needs a session, and opening it against none renders the empty
   * state, which then never refills.
   */
  const dockOpened = useRef(false)
  useEffect(() => {
    if (!paneFrame || dockOpened.current) return
    if (!new URLSearchParams(location.search).has('files')) return
    if (!agentSelRef.current && !panelProjectId.current) return
    dockOpened.current = true
    if (panelProjectId.current) setProjFilesOpen(true)
    else setFilesOpen(true)
  })

  const footbar = useRef<HTMLDivElement>(null)
  const composerEl = useRef<HTMLTextAreaElement>(null)
  /** Non-null while an `@mention` is being typed: where it starts and what's typed. */
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null)
  /*
   * The composer is UNCONTROLLED (no `value={text}`): the textarea owns its own
   * value, so typing never runs `setText` and never re-renders App — that
   * per-keystroke re-render of the whole shell was the mobile jank. What the
   * render still needs from the live value is only emptiness (`hasText`, for the
   * Send button + has-text class); the full text is read from the DOM at submit /
   * @-pick / put-in. Every programmatic write to the box routes through
   * setComposerValue so it also grows the box and reflects emptiness.
   */
  const hasTextRef = useRef(false)
  /*
   * SEARCH MODE — owner, 2026-09-11: 按下搜尋按鈕的時候整個 text input 會變為輸入搜尋
   * keyword 的功能. The composer is borrowed as the keyword box for Clean View. The
   * draft is held HERE, not in storage, while the box is borrowed; the query goes to
   * the search store (throttled, read from the DOM — still no per-keystroke render).
   */
  const [searchMode, setSearchMode] = useState(false)
  const searchModeRef = useRef(false)
  const heldDraft = useRef('')
  /*
   * WHICH CONVERSATION THE BOX BELONGS TO — owner, 2026-08-22: 文字輸入到一半的
   * Text edit 不會跑到其他的 Session 而是就地有記憶。之後可以回來繼續完成內容.
   *
   * One textarea serves every destination, so a half-written line used to follow the
   * person to whoever they opened next. Held in a ref as well as read from render,
   * because the save runs from the uncontrolled `onChange` — which must not close over
   * a render's value or it would write yesterday's draft under today's key.
   */
  const dKey = draftKey(url.sel)
  /*
   * OPENING SOMETHING ELSE CLOSES THE ARTIFACT.
   *
   * On a wide screen the viewer covers the stage and leaves the rail showing,
   * which is the point — owner, 2026-09-21: 「這樣可以讓使用者可以迅速的跳轉」.
   * But a jump you cannot see is not a jump: tapping an agent while an artifact
   * is up would select a conversation and leave the artifact sitting over it,
   * and the rail row would look like it did nothing.
   *
   * Keyed on the draft key rather than `url.sel`, which is a fresh object every
   * render — that would close the viewer on the first repaint after opening it.
   * Opening an artifact does not touch the selection, so this only ever fires
   * for a real navigation.
   */
  useEffect(() => {
    setViewArtifact(null)
    // More's page too, for the same reason: a jump from the rail lands on what was chosen.
    setMorePage(null)
  }, [dKey])
  const dKeyRef = useRef<string | null>(dKey)
  const growComposer = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto'
    const max = Number.parseFloat(getComputedStyle(el).maxHeight)
    el.style.height = `${Number.isFinite(max) ? Math.min(el.scrollHeight, max) : el.scrollHeight}px`
    const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight)
    if (el.scrollTop > maxScroll) el.scrollTop = maxScroll
    el.classList.toggle('overflowing', el.scrollHeight > el.clientHeight + 1)
  }
  const reflectHasText = (value: string) => {
    const now = value.length > 0
    if (hasTextRef.current !== now) {
      hasTextRef.current = now
      setHasText(now) // re-render ONLY on the empty↔non-empty transition
    }
  }
  const setComposerValue = (next: string) => {
    const el = composerEl.current
    if (el) {
      el.value = next
      growComposer(el)
    }
    reflectHasText(next)
    /*
     * Every PROGRAMMATIC write lands here — a send clearing the box, an @-pick, a note
     * put in — so the stored draft follows them without a second place to remember.
     * Typing does not come through here (that is the whole point of the uncontrolled
     * box); it is saved from `onChange`, throttled.
     */
    writeDraft(dKeyRef.current, next)
  }
  /** Write the box WITHOUT touching the stored draft: search borrows the box and must give it back untouched. */
  const setBoxOnly = (next: string) => {
    const el = composerEl.current
    if (el) {
      el.value = next
      growComposer(el)
    }
    reflectHasText(next)
  }
  // The keyword reaches the store at most every 120 ms, read from the DOM at that
  // moment — the same shape as the draft save below, for the same reason.
  const pushQuerySoon = useRef(atMostEvery(120, () => setSearchQuery(composerEl.current?.value ?? ''))).current
  const enterSearch = () => {
    if (searchModeRef.current) return
    heldDraft.current = composerEl.current?.value ?? ''
    searchModeRef.current = true
    setSearchMode(true)
    setMention(null)
    openSearch()
    setBoxOnly('')
    composerEl.current?.focus()
  }
  const exitSearch = () => {
    if (!searchModeRef.current) return
    searchModeRef.current = false
    setSearchMode(false)
    closeSearch()
    setBoxOnly(heldDraft.current)
    heldDraft.current = ''
    composerEl.current?.focus()
  }
  /*
   * Save at most once per window while typing. A keystroke must not touch storage —
   * `atMostEvery` opens a window on the first poke and absorbs the rest, so a burst
   * costs one write and the last one always lands.
   */
  const saveDraftSoon = useRef(
    atMostEvery(400, () => writeDraft(dKeyRef.current, composerEl.current?.value ?? '')),
  ).current
  /*
   * SWITCHING CONVERSATIONS: put away what is in the box, take out what was left here.
   * Both halves in one effect, in that order, because the composer is a single element
   * — read it before it is overwritten or the outgoing draft is gone.
   */
  useEffect(() => {
    const prev = dKeyRef.current
    if (prev === dKey) return
    // Mid-search the box holds a keyword, not the draft: the HELD draft is what goes away.
    if (prev) writeDraft(prev, searchModeRef.current ? heldDraft.current : composerEl.current?.value ?? '')
    if (searchModeRef.current) {
      searchModeRef.current = false
      setSearchMode(false)
      heldDraft.current = ''
      closeSearch()
    }
    dKeyRef.current = dKey
    setComposerValue(readDraft(dKey))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dKey])
  // The document listener is bound once, so it reads through refs.
  const sendRef = useRef<(bodyOverride?: string) => Promise<void>>(async () => {})
  /** True while attached files are being uploaded — see terminalSubmit. */
  const uploading = useRef(false)
  const inChatRef = useRef(false)
  const submitFromControlRef = useRef<() => void>(() => {})
  /** The open agent as a note receiver — read from callbacks bound outside render. */
  const agentSelRef = useRef<{ host: string | null; session: string } | null>(null)

  /** Shared display name, owned by the server so every connected UI agrees. */
  const config = useQuery({ queryKey: ['config'], queryFn: getConfig })
  const serverName = config.data?.name ?? config.data?.hostname ?? '…'

  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => api.get<{ version?: string; port?: number }>('/health'),
  })

  const vault = useQuery({
    queryKey: ['security-status'],
    queryFn: getSecurityStatus,
  })

  // ONE spelling of "is the vault open", shared with the Sidebar — see vaultOpen.
  const unlocked = vaultOpen(vault.data)

  /*
   * Who the browser is, by Google — fetched once, cached for the tab's life.
   * Authed checked this already but keeps only the boolean; project sends need
   * the address itself (the vaultless edge session has none to offer).
   */
  const whoami = useQuery({
    queryKey: ['auth-me'],
    queryFn: fetchSession,
    staleTime: Infinity,
  })
  const myEmail = whoami.data?.kind === 'in' ? whoami.data.identity.email : null
  /**
   * WHO AM I, for the chat surfaces — ONE answer, passed to every consumer.
   *
   * There were three. ProjectPanel got `projectOnDynamo() ? chatv1SelfEmail() : vault.email`,
   * ActivityPanel got a bare `vault.data?.email`, and the welcome line had its own copy of
   * the ternary. On the chat lane the vault is unconfigured — /security/status answers
   * `configured:false` — so ActivityPanel's copy was empty, and it could not tell the
   * viewer's own rows from anyone else's while Project Chat could. A found bug, not a
   * tidy-up: two surfaces answering one question differently, with no test able to see it.
   *
   * The edge lane's deletion on 2026-09-02 removed the branch, so this is now one call —
   * and it must STAY one const. `myEmail` above is deliberately separate: that is the
   * Google session identity used for optimistic sends, this is the chat service's.
   */
  const selfEmail = chatv1SelfEmail()
  // The signed-in Google identity — carried into an optimistic project send so the
  // sender's own row shows their name + photo instantly (the server never echoes it back).
  const myName = whoami.data?.kind === 'in' ? whoami.data.identity.name : null
  const myPicture = whoami.data?.kind === 'in' ? whoami.data.identity.picture : null

  // Same query keys as the sidebar, so react-query serves these from one cache entry.
  const paneScope = paneFrame && url.sel?.kind === 'agent' ? (url.sel.device ?? null) : null
  const agentsQ = useQuery({
    queryKey: ['agents', paneScope ?? 'all'],
    /*
     * A PANE ASKS ONE MACHINE. It shows one conversation, its URL names the machine
     * that conversation is on, and it draws no rail — every other machine it asked was
     * a round trip whose answer it would not render. Measured before the change: 5.6s
     * from the drop to the conversation appearing, most of it this.
     *
     * The key carries the scope, so the shell's full sweep and a pane's single-machine
     * answer are not the same cache entry pretending to be each other.
     */
    queryFn: () => getAgentsEverywhere(paneScope),
  })
  /*
   * RE-ASK FROM WHERE THE STAGE IS. The rail retries its own `['agents']` query,
   * but the stage reads this scoped one and, on a phone, is a full-screen push
   * that unmounts the rail — so a selected agent whose machine was slow sat on
   * "Waiting for <machine> to answer" until a manual reload. This lives at App
   * level, which is always mounted, and re-asks on the stream reopening (the
   * mobile half-open-socket case) and on a bounded ladder.
   */
  useAnswerRetry(
    agentsQ.data?.failed?.length ?? 0,
    agentsQ.dataUpdatedAt,
    () => void agentsQ.refetch(),
  )
  /*
   * The account FLEET, for naming cross-machine senders in Clean View.
   * Event-driven like everything else (the no-heartbeat guard caught the first
   * draft's refetchInterval): fetched once, refreshed on window focus once
   * stale. Identities change on attach/rename, not per keystroke, and the
   * failure mode is only "a hash stays a hash until the next look".
   */
  const fleetIdQ = useQuery({
    queryKey: ['fleet-identity'],
    queryFn: getFleetIdentity,
    staleTime: 60_000,
  })
  /**
   * Polled like the agent list, and for the same reason. This used to be a one-shot
   * fetch, which made a transient failure PERMANENT: `?project=…` on a cold load
   * resolves against this list, and with the query stuck in `error` the panel simply
   * never mounted. Worse, an errored query is no longer `isPending`, so the UI fell
   * through to "that session isn't here any more" — reporting a network failure as a
   * deleted conversation, with no retry behind it. Refreshing into a project was a
   * coin flip; navigating away and back refetched and "fixed" it.
   */
  const projectsQ = useQuery({
    queryKey: ['projects'],
    queryFn: getProjects,
    // Project Chat is a CLOUD service with its own Cognito token — it does NOT depend
    // on the machine vault. A chat-only account (no paired machine, so the vault never
    // unlocks) must still see its projects. The `|| unlocked` half was the edge lane's,
    // deleted 2026-09-02.
    retry: 3,
  })

  /**
   * The DM list — one row per friend conversation, carrying the same `unread` and
   * `last_seq` the project rows carry.
   *
   * Fetched for the BADGE, not for a screen: the Humans tab is a roster of friends
   * with no conversation rows in it, so without this nothing on the rail could say
   * that a friend had written. It is also what lets an open DM clear itself — the
   * cursor advance needs `last_seq`, and useManagedFriendDm resolves an id and
   * nothing else (a friend pair always maps to the same conv_id, so it is cached
   * forever and deliberately knows no counts).
   *
   */
  const dmConvsQ = useQuery({
    queryKey: ['dm-conversations'],
    queryFn: () => listConversations('dm'),
    retry: 3,
  })
  const dmRows = activeDms(dmConvsQ.data?.conversations)

  /**
   * INCOMING FRIEND REQUESTS, FETCHED FOR THE BADGE — owner, 2026-09-22: 使用者A將使用者B
   * 加入好友之後, 使用者B的 humanist 那一個 menu 的按鈕並不會有提示的紅色數字跑出來, 這樣子的話,
   * 使用者B就很難知道自己被加好友了.
   *
   * The refresh half already worked: the BFF pokes both accounts on
   * request/accept/reject/cancel and makeDevicesHandler re-reads this key on that bell
   * (friends/friendsPushRefresh.test.ts pins it). What could not work is the COUNT,
   * because this query lived only inside FriendsTab — the component that mounts when
   * the Humans tab is already open. While the tab was closed nothing fetched it, so
   * there was no number for the rail to draw, and the one moment the badge exists to
   * cover is precisely the one where the tab is closed.
   *
   * That is the rule the NavBar's own comment states: the badges have to be right
   * while every panel is closed and their queries are not running. Same reason, same
   * shape, and the same one line of justification as the DM list above.
   *
   * The key is shared with FriendsTab deliberately — one list, one cache entry, so the
   * tab and the badge can never disagree about how many requests are waiting.
   */
  const friendReqQ = useQuery({
    queryKey: ['friend-requests'],
    queryFn: getFriendRequests,
    retry: 3,
  })
  const incomingFriendReqs = friendReqQ.data?.incoming?.length ?? 0

  /**
   * The URL names a selection; these resolve it to the real objects once the lists
   * arrive. Until then both are null, so a cold load with ?agent=… simply shows the
   * empty stage for a moment rather than rendering a half-built agent.
   */
  const sel = url.sel
  /** The selection, resolved against the roster's AGENTS. */
  const rosterAgent: AgentRow | null =
    sel?.kind === 'agent'
      ? (agentsQ.data?.agents.find((a) =>
          // Match by the stable (hash, device) when the selection carries it (a hash
          // URL, or an in-app open enriched by `select`); fall back to (session, host)
          // for legacy `?agent=<name>` links.
          sel.hash
            ? a.hash === sel.hash && (a.device_id ?? null) === (sel.device ?? null)
            : a.session === sel.session && (a.host ?? null) === sel.host,
        ) ?? null)
      : null
  /**
   * The rail's Terminals row this selection names, when no agent does — matched by the
   * MACHINE too when the selection carries one: `term-…` names are per machine, and two
   * machines' leftover `0` sessions are two different terminals.
   */
  const terminalRow: TerminalRow | null =
    !rosterAgent && sel?.kind === 'agent'
      ? (agentsQ.data?.terminals?.find(
          (x) =>
            x.session === sel.session &&
            (x.host ?? null) === sel.host &&
            (!sel.device || !x.device_id || x.device_id === sel.device),
        ) ?? null)
      : null
  /*
   * Agent-less tmux sessions (the sidebar's Terminals rows) open the same stage — a terminal is a session without a
   * detected agent, not a different kind of thing.
   *
   * ONE OBJECT PER TERMINAL, NOT ONE PER RENDER (owner, 2026-11-04: 功能變多了之後 … UI 有時候會變得很卡). Built inline it
   * was a new object every render, and the status effect below depends on `agent`: while the terminal was not attached
   * (its machine away, the live connection down) each render showed "terminal closed…" again, which renders again —
   * measured on app-dev, about 400 renders of the whole app a second, for as long as the link stayed down. Keyed on what
   * the row says, so a roster sweep that hands back an equal row in a new object keeps this one too.
   */
  const tSession = terminalRow?.session
  const tHost = terminalRow?.host ?? null
  const tCwd = terminalRow?.cwd ?? null
  const tDevice = terminalRow?.device_id
  const tDeviceName = terminalRow?.device_name
  const terminalAgent = useMemo<AgentRow | null>(
    () =>
      tSession === undefined
        ? null
        : {
            session: tSession,
            host: tHost,
            hash: tSession,
            onboarded: false,
            cwd: tCwd,
            // The machine it lives on: the terminal, the files and the close all go there.
            ...(tDevice ? { device_id: tDevice } : {}),
            ...(tDeviceName ? { device_name: tDeviceName } : {}),
          },
    [tSession, tHost, tCwd, tDevice, tDeviceName],
  )
  const agent: AgentRow | null =
    rosterAgent ??
    (sel?.kind === 'agent'
      ? (terminalAgent ??
        /*
         * A SYNTHETIC ROW FOR A JUST-CREATED SESSION USED TO SIT HERE, and it was
         * unreachable: `pending` was declared `const [pending] = useState(null)` with the
         * setter dropped when the create flow became attach-only, so nothing could ever
         * write it and this branch could never be taken. The comment kept it for "the
         * returning create contract", and that contract arrived by another route —
         * StartAgentSheet's onCreated uses settleRoster + newlyCreatedRow. Deleted
         * 2026-09-02; agents/settleRoster.ts is where that behaviour lives now.
         */
        null)
      : null)
  /**
   * The stage holds a bare terminal: the roster has no agent for this selection, so the
   * row above was built from `terminals`. Read from WHICH branch answered, not from the
   * row: a terminal has no card — "no card, no queue and no identity beyond a name" (the
   * rail's terminal rows end themselves inline) — so the bar draws no settings gear for
   * one.
   */
  const agentIsTerminal = !!agent && !rosterAgent

  const project: ProjectRow | null =
    sel?.kind === 'project'
      ? (projectsQ.data?.projects.find((p) => p.project_id === sel.id) ?? null)
      : null

  // The friend DM's identity: the email for an in-app open / legacy link, or the
  // opaque dmKey a URL carries — useManagedFriendDm reverses a key to its email.
  const dmPeer: string | null = sel?.kind === 'dm' ? (sel.peer ?? sel.key ?? null) : null
  /** Which friend at that address, when the row opened said (two accounts, one email). */
  const dmAccount: string | null = sel?.kind === 'dm' ? (sel.account ?? null) : null
  /*
   * A FRIEND'S AGENT — owner, 2026-10-17: 朋友 Agent 的 session ui 可以用右開的方式。完全參照目前一般
   * Agent 的 UI. 有小鍵盤, @, 檔案迴紋針 ... mic 按鈕, 以及送出。不過 Agent 小藥丸需要下標 Agent 的 owner.
   *
   * It opens the way an agent of your own does — pushed over the rail on a phone, on the stage on
   * a wide screen — with this header, this pill and this composer around its conversation. What it
   * is not is one of `agent`'s: that name is this account's own agent, and every path keyed on it
   * (the pty, the notes, the settings, the relay target) is a path a friend has no business on. So
   * it is its own subject, named by the SHARE, and each control that serves it says so below.
   *
   * The last share seen stays on screen when the list drops it (taken back, run out, ended), so
   * the conversation can say which, rather than vanishing into a generic sentence.
   */
  const sharedId: string | null = sel?.kind === 'shared' ? sel.id : null
  // Read while a friend's agent is open — and while your own agent's @ picker is, which offers
  // the agents friends shared with you (phase 2).
  const agentSharesQ = useAgentShares(!!sharedId || (!!agent && !!mention))
  const sharedNow: IncomingAgentShare | null = sharedId
    ? (agentSharesQ.data?.incoming.find((x) => x.share_id === sharedId && x.status === 'accepted') ?? null)
    : null
  const lastShared = useRef<IncomingAgentShare | null>(null)
  if (sharedNow) lastShared.current = sharedNow
  const shared: IncomingAgentShare | null =
    sharedNow ?? (sharedId && lastShared.current?.share_id === sharedId ? lastShared.current : null)
  const sharedLive = useSharedLive(sharedId)
  /** The composer can still speak to it: the list has it, and nothing has said it is over. */
  const sharedOpen = !!sharedNow && !sharedLive.gone
  /** The sharer gave this friend the keys row (小鍵盤 B), and the share is still on. */
  const sharedKeys = sharedOpen && !!sharedNow?.keys
  const sharedName = shared ? shared.agent.label || shared.agent.session : ''
  const sharedOwner = shared ? shared.from.name || shared.from.email || 'Your friend' : ''
  const [sharedFilesOpen, setSharedFilesOpen] = useState(false)
  const [sharedCardOpen, setSharedCardOpen] = useState(false)
  /** The reader's own agents @-pinged in a friend's agent's composer (hash → device), for the say. */
  const sharedPings = useRef(new Map<string, string>())
  useEffect(() => {
    setSharedFilesOpen(false)
    setSharedCardOpen(false)
    sharedPings.current.clear()
  }, [sharedId])
  // A friend DM is now a MANAGED, locked 2-human conversation on the chat service (the same
  // rail Project Chat rides), not the old E2E relay. It resolves to a conv_id and then flows
  // through the SAME project render / composer / mark-read below — which is what retires the
  // "Encrypted messaging unavailable — they haven't opened encrypted messaging yet" gate.
  const friendDm = useManagedFriendDm(dmPeer, dmAccount)
  /*
   * Carry the open DM's unread + newest seq onto the row the panel renders.
   *
   * Without this the badge could light and never go out. The mark-read effect below
   * advances the read cursor to `last_seq`, and dynamoProjectMarkRead treats a
   * missing seq as a no-op — so a DM, whose row until now was just {project_id},
   * was READ on screen and still counted as unread everywhere else. That is the
   * exact failure the project path already refuses ("a badge that lies is worse"),
   * arriving through the one conversation kind that had no list behind it.
   *
   * Assigned only when the list has the row (exactOptionalPropertyTypes: a missing
   * unread must be ABSENT, not undefined).
   */
  const dmProject: ProjectRow | null = withDmCursor(friendDm.row, dmRows)
  /*
   * A DM THAT WILL NOT OPEN SAYS WHY — owner, 2026-09-16: 我好友那邊無法進入
   * friend chat. 「無法進入」 rather than an error is precisely the defect: the
   * server was answering his friend with a specific 404 on every attempt, the
   * hook handed it back as `error` (its docstring even says the caller should
   * show it), and nothing here read it. The stage just stayed empty, so trying
   * again looked identical to not trying, and the cause had to be found in the
   * database rather than on the screen.
   *
   * Routed into #sendErr because that banner already exists for exactly this
   * job — it sits OUTSIDE the stage, so it shows even when the stage has
   * nothing to draw, it does not auto-hide, and it is dismissible.
   */
  useEffect(() => {
    if (friendDm.error) setSendError(dmOpenMessage(friendDm.error))
  }, [friendDm.error])
  // The conversation the stage renders and the composer sends into: a real project, or the
  // managed friend DM standing in as a locked one.
  const panelProject: ProjectRow | null = project ?? dmProject
  /*
   * REPLYING UNDER A MESSAGE in a group's chat (owner, 2026-10-20: 巢狀的回復, 只有一層; chat/Thread.tsx). The panel
   * names the thread; the composer says "Replying to …" over itself and sends into that thread. Tied to its room —
   * open another chat and it no longer applies.
   */
  const [replyTo, setReplyTo] = useState<{ conv: string; seq: number; name: string; text: string } | null>(null)
  const replying = replyTo && panelProject && replyTo.conv === panelProject.project_id ? replyTo : null
  // Leaving the room ends the reply: coming back later must not find a half-forgotten "Replying to …" waiting.
  const panelConv = panelProject?.project_id ?? null
  useEffect(() => {
    setReplyTo((r) => (r && r.conv !== panelConv ? null : r))
  }, [panelConv])
  // Escape lets go of it, as it lets go of every other pending thing.
  useEffect(() => {
    if (!replyTo) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setReplyTo(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [replyTo])
  panelProjectId.current = panelProject?.project_id ?? null
  const panelLocked = !project && !!dmProject

  /**
   * The list the URL's selection has to be resolved against, and whether it has ever
   * actually arrived.
   *
   * `isPending` is NOT the right test: a query that has failed is neither pending nor
   * loaded, and treating "not pending" as "loaded" is what let a failed fetch render
   * as a missing conversation. Only the presence of `data` proves the list is real —
   * that also covers the disabled-while-locked case, where there is nothing to resolve
   * against yet either.
   */
  const listQ = sel?.kind === 'project' ? projectsQ : sel?.kind === 'agent' ? agentsQ : null
  const listLoaded = listQ ? listQ.data !== undefined : true
  // A shared agent resolves against its own list and says so itself (the stage below).
  const unresolved = !!sel && !agent && !project && !dmPeer && !sharedId

  /**
   * A MACHINE THAT DID NOT ANSWER CANNOT TESTIFY THAT A SESSION IS GONE.
   *
   * `getAgentsEverywhere` is best-effort by design: a machine that is down, slow or
   * mid-reconnect "contributes nothing" and lands in `failed`, and its agents vanish
   * from the list for that tick. `data` is present either way — so a PARTIAL failure
   * read as a complete answer, and a selection whose machine happened to be slow was
   * reported as "That session isn't here any more".
   *
   * The comment above already names this family — a failed query is neither pending nor
   * loaded — and fixes it for the whole query. This is the same fault one machine down:
   * the list loaded, and it is not the whole list.
   *
   * The owner met it through the split, because EVERY PANE IS A COLD LOAD from a URL:
   * the shell reaches a conversation by clicking a row that was in the list a moment
   * ago, so it almost never asks this question. A pane asks it every time it opens.
   *
   * Scoped as tightly as the evidence allows: if the selection names the device it
   * lives on, only THAT machine's silence counts. Without one, any silence does.
   */
  /**
   * A session THIS APP just created or forked, until the roster lists it (or the
   * expectation expires). Read by stageState; set by the create/fork handlers below.
   */
  const [expecting, setExpecting] = useState<Expecting | null>(null)
  const everywhere = sel?.kind === 'agent' ? agentsQ.data : null
  const silent = everywhere?.failed ?? []
  const askedEveryone =
    !silent.length ||
    (sel?.kind === 'agent' && !!sel.device && !silent.some((f) => f.device_id === sel.device))
  /*
   * WHAT THE STAGE SAYS about a selection the list cannot show — decided in
   * agents/stageState.ts, because the owner met the wrong sentence on his phone
   * (2026-09-06): a session he had forked three seconds earlier was announced as
   * "isn't here any more". A session the app itself just created or forked is
   * STARTING until the roster lists it or the expectation expires; a list still
   * being re-asked is RESOLVING; MISSING is reserved for an answered list with
   * nothing expected and nothing found.
   */
  const stageMode = stageState({
    unresolved,
    listLoaded,
    askedEveryone,
    fetching: listQ?.isFetching ?? false,
    expecting,
    selected: sel?.kind === 'agent' ? { session: sel.session ?? '', host: sel.host ?? null } : null,
    now: Date.now(),
  })
  /** The list hasn't landed yet — still loading, retrying, or waiting on the vault. */
  const resolving = stageMode === 'resolving'
  /** The app itself just started this session; the roster has not listed it yet. */
  const starting = stageMode === 'starting'
  /** The list DID load, everyone answered, nothing was expected, and it isn't there. */
  const missing = stageMode === 'missing'
  /** Which machine's silence is holding the answer up, for the resolving line. */
  const selDevice = sel?.kind === 'agent' ? (sel.device ?? null) : null
  const silentName = silent.find((f) => !selDevice || f.device_id === selDevice)?.name ?? null

  /**
   * Opening anything from the rail is a PUSH on a phone: the rail is the root
   * screen there, so the conversation has to come over it. On a wide screen the
   * rail is docked beside the stage and closing it would just take space away
   * for no reason.
   */
  const pushDetail = () => {
    if (phoneNavRef.current) setSbOpen(false)
  }

  const openProject = (p: ProjectRow) => {
    setRosterOpen(false)
    pushDetail()
    select({ kind: 'project', id: p.project_id })
  }
  const openAgent = (a: AgentRow) => {
    /*
     * POINT THE RELAY AT THE MACHINE THIS AGENT IS ON.
     *
     * Everything the next screen asks — the terminal, the transcript, the notes,
     * the files — is relayed to whichever machine the relay currently addresses.
     * With one paired machine that was always the right one. With two, opening an
     * agent on the Mac and then reading `aowl`'s filesystem is a wrong answer that
     * looks entirely plausible, which is the kind this project keeps producing.
     *
     * `device_id` is absent only for rows that predate the fan-out; leaving the
     * target alone is right for those, because there was only one machine to mean.
     */
    if (a.device_id) setRelayTarget(a.device_id)
    pushDetail()
    /*
     * CARRY THE STABLE IDENTITY. Without hash+device the selection resolves by
     * (session, host), and two agents that share a name across machines both have
     * host=null — so `.find` returns whichever is first in the array, not the row
     * that was clicked (web-train-board on two machines, 2026-08-24). The resolver
     * already prefers (hash, device) when the selection carries them; this is the
     * open path finally handing them over.
     */
    select({ kind: 'agent', session: a.session, host: a.host, hash: a.hash, device: a.device_id ?? null })
  }
  /*
   * A GLOBAL SEARCH RESULT OPENS THAT AGENT AT THAT MESSAGE (owner, 2026-09-28). Opening
   * the agent switches conversations, and the switch puts any open search away — so the
   * search is not started here but held, and started once that agent is the one on
   * screen (the effect after startSearch below): same keyword, landing on this block.
   */
  const pendingConvSearch = useRef<{ hash: string; device: string; query: string; index: number } | null>(null)
  const openMessage = (r: AccountSearchResult) => {
    const row =
      agentsQ.data?.agents?.find((a) => a.hash === r.hash && a.device_id === r.device_id) ??
      ({ session: r.session, host: null, hash: r.hash, device_id: r.device_id } as AgentRow)
    pendingConvSearch.current = { hash: r.hash, device: r.device_id, query: msgQuery ?? '', index: r.index }
    openAgent(row)
  }
  /** A friend's agent, from Humans: the same push as any conversation. */
  const openShared = (id: string) => {
    pushDetail()
    select({ kind: 'shared', id })
  }
  const openDm = (email: string, account?: string | null) => {
    pushDetail()
    select({ kind: 'dm', peer: email, ...(account ? { account } : {}) })
  }
  const closeSelection = () => select(null)
  /** A bare terminal is gone: off the stage, and the rail asked until it agrees. */
  const leaveTerminal = (session: string) => {
    closeSelection()
    void settleRoster(qc, sessionGone(session))
  }
  /** The bar's Close: a ＋ → Terminal by its own route; an older bare session as the rail's ✕ ends it. */
  const closeTerminal = async (row: AgentRow, plain: boolean) => {
    try {
      if (plain) await terminalClose(row.session, row.device_id)
      else await agentKill(row.session, row.host, row.device_id, false)
      leaveTerminal(row.session)
    } catch (e) {
      status(e instanceof Error ? `Could not close the terminal: ${e.message}` : 'Could not close the terminal')
    }
  }

  /**
   * The bottom nav.
   *
   * Home is not a screen of its own — it is the app as it already was, so it CLOSES
   * what the other tabs opened rather than navigating anywhere. That is the owner's
   * rule ("Home 按鍵就預設為目前的 Project chat + Agent 的畫面") and it is also the
   * only definition that leaves no state where two tabs are half-open at once.
   *
   * Every tab opens the rail first, because on a phone the rail IS the root screen
   * and a tab that changed something behind a closed rail would look like it did
   * nothing at all.
   */
  const pickNav = (t: NavTab) => {
    setSbOpen(true)
    /*
     * On a phone More's page and the artifact viewer stop above this bar so it can be
     * pressed while they are up (owner, 2026-10-27: 這一排按鈕要在它們展開之後還是顯示在最上層
     * … 不需要一直按著那個回到上一頁的按鈕) — and a press goes where it says: the tab's own
     * list, never the page still standing over it. More itself included, which is its
     * way back to its three buttons. A wide screen keeps them: there they sit on the
     * stage beside the rail, and a tab changes the rail, not the stage.
     */
    if (phoneNavRef.current) {
      setMorePage(null)
      setViewArtifact(null)
    }
    // Any nav pick leaves the main menu: the tabs address the LIST the menu is
    // standing in front of.
    setServerOpen(false)
    if (t === 'search' && navTab !== 'search') searchFrom.current = navTab
    setNavTab(t)
    if (t === 'search') {
      setSearchOpen(true)
      // Focus after the row exists. Same frame would find nothing to focus.
      requestAnimationFrame(() => searchEl.current?.focus())
      return
    }
    /*
     * More takes the search field away with it, exactly as Home does. The field
     * filters the rail's LIST, and More replaces that list — leaving it up would
     * park a box on screen that cannot change anything you can see, which is the
     * control-that-does-nothing this app has already been caught shipping once.
     */
    if (t === 'home' || t === 'more' || t === 'activity') {
      setSearchOpen(false)
      setSbQuery('')
      setMsgQuery(null)
    }
  }

  /*
   * `?humans=1` — where a tapped "shared an agent with you" notification lands (BFF
   * routes_agent_share.py, owner 2026-10-17): the Humans tab, where the offer waits for an
   * answer. Read once on arrival and then dropped from the address, so a reload does not
   * keep re-opening it.
   */
  useEffect(() => {
    const q = new URLSearchParams(window.location.search)
    if (!q.has('humans')) return
    q.delete('humans')
    const rest = q.toString()
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`)
    pickNav('group')
    // Once, on arrival: pickNav is a fresh closure every render and must not re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * THE ✕ — owner, 2026-09-29: 直到使用者按下搜尋框右邊的那個 X, 搜尋結果清空, 並且恢復
   * 原本 Side Panel 的樣貌. The one way out of search, and Escape is the same key: the
   * results and the words go, the bar comes back, and the rail is on the tab it was on
   * when search opened.
   */
  const closeRailSearch = () => {
    setSearchOpen(false)
    setSbQuery('')
    setMsgQuery(null)
    setNavTab(searchFrom.current)
    ;(document.activeElement as HTMLElement | null)?.blur?.()
  }

  /**
   * SEND THE QUERY: ask every machine for the MESSAGES matching what is typed. The
   * field's Enter (the phone keyboard's Search key) and the ↑ inside the pill (owner,
   * 2026-09-29: 在 Search 匡的右側加入一個向上的箭頭按鈕 … 按下該按鈕就會送出需要搜尋的
   * Query) both do exactly this. The keyboard goes away so the answer can be read.
   */
  const sendRailSearch = () => {
    const q = sbQuery.trim()
    if (!q) return
    setMsgQuery(q)
    searchEl.current?.blur()
  }

  /**
   * auto-detect, resolved. The idle poll's `kind` names what is actually running in
   * the session (claude-code / cursor / codex / screen); deriving the view from it
   * each render IS the original's autoViewRedetect — when the agent changes, the next
   * poll flips the view, no imperative re-open needed. Unknown or plain sessions fall
   * back to the raw terminal, which is always correct.
   */
  const idle = useIdleStatus()
  /*
   * Remembered per agent, so a dropped host does not change which view is on screen.
   * A ref rather than state: this is a cache, writing it must not schedule a render,
   * and it is idempotent under React's double-invoked render.
   */
  const lastKind = useRef(loadKinds())
  const agentKey = agent ? idleKey(agent) : null
  const polledKind = agentKey ? idle.byKey.get(agentKey)?.kind : undefined
  if (agentKey && polledKind) lastKind.current = saveKind(lastKind.current, agentKey, polledKind)
  const agentKind = agentKey ? stickyKind(polledKind, lastKind.current[agentKey]) : undefined
  /**
   * Is the open agent working right now?
   *
   * presenceFor — THE SAME CALL the rail makes, not the same idea re-derived.
   *
   * This read the fetched row's status field directly (byKey → status) under a
   * comment claiming "same source and same test the rail uses". True when
   * written; then the rail moved to presenceFor's switch-first rule (the edge
   * pushes busy sets; the fetched row is only the fallback for a pre-switch
   * edge) and this line stayed on the fetched lane. The fetch refreshes on
   * user-shaped events only, so `status:'active'` outlives the turn — and the
   * owner watched the two disagree (2026-08-30): rail avatar idle, session
   * chip still sparking. A comment asserting agreement is a scar, not a bond;
   * only sharing the CALL keeps two screens answering with one voice
   * (chipPresence.test.ts now pins exactly that).
   */
  const agentWorking = agent ? presenceFor(idle.byKey, agent) === 'working' : false
  /** The Stop button's hold after a press that must not be repeated quickly (stopKey.ts). */
  const [stopHeld, setStopHeld] = useState(false)
  /**
   * Is the agent stopped at a prompt, waiting to be answered?
   *
   * The server has reported this as `needs_input` all along and nothing read it. It
   * means the pane's tail holds a permission/approval prompt, a workspace-trust
   * confirm, or a numbered yes/no menu — a state where the CLI is consuming keys as
   * MENU NAVIGATION, not as text.
   *
   * Which is why typing vanished. Staging text into a pane in that state does not
   * queue it: the characters are eaten as selection keys, the composer clears
   * because staging "succeeded", and in a rich view the prompt that ate them is not
   * even on screen to explain it.
   *
   * Up to 5s stale, since it rides the idle poll. That is the argument for warning
   * and refusing rather than silently rewriting the input: a stale TRUE costs one
   * tap to override by switching view, while a wrong send costs the message.
   */
  const agentNeedsInput = agent ? needsInputFor(idle.byKey, agent) : false
  const effectiveMode: ViewMode =
    viewMode !== 'auto-detect' ? viewMode : viewForKind(agentKind)
  /*
   * BOOKMARKS — owner, 2026-10-31: 在 Agent 對話的 Session 裡面, CleanView 上面 … 每一個 Message Block 下面有一個書簽的按鈕
   * … 右側欄位 … 可以打開的小抽屜, 就像 Note 一樣 … 另外一種顏色 … 寬螢幕的時候 … 右下角點擊展開書籤 List. Offered where
   * the conversation is drawn (Clean View), for an agent whose machine keeps bookmarks (agents/bookmarks/useBookmarks).
   * The list is offered in the terminal too (owner, 2026-11-01: 在 Terminal 的狀態下面, 也要顯示這個書籤的按鈕 … 不管是在窄螢幕還是
   * 寬螢幕) — for an agent that HAS a conversation (a kind with a Clean View, as search asks); going to a bookmark from
   * there switches to the conversation first.
   */
  const bmAgent = agent && !agentIsTerminal && viewForKind(agentKind) !== 'terminal-view' ? agent : null
  const bookmarks = useBookmarks(bmAgent)
  const bmOffered = !!bmAgent && bookmarks.supported
  /*
   * A PROJECT'S BOOKMARKS — owner, 2026-11-01: 我們在 project 裡面也可以加入書籤 … 這個書籤就只有該使用者自己可以看到. The
   * viewer's own, kept by the chat service (chat/useRoomBookmarks), in the same corner button, handle and drawer; a
   * project's room only — not a group's, not a friend's chat (ProjectPanel offers the buttons by the same rule).
   */
  const bmRoomConv = !agent && project && !project.group_id ? project.project_id : null
  const roomBm = useRoomBookmarks(bmRoomConv)
  const roomBmOffered = !!bmRoomConv && roomBm.supported
  const bmCount = roomBmOffered ? roomBm.list.length : bookmarks.list.length
  /*
   * A bookmark gone to from More › Bookmarks lands in its agent's CONVERSATION — the message is there. The view switches
   * after the selection has (an effect): setViewMode writes the URL with the selection it was rendered with, and in the
   * same handler as the select that would put the previous agent back.
   */
  const [cleanViewFor, setCleanViewFor] = useState<string | null>(null)
  // Read at call time (ui/useBlobUrl.ts): setViewMode is a new function every render.
  const setViewModeRef = useRef(setViewMode)
  setViewModeRef.current = setViewMode
  useEffect(() => {
    if (!cleanViewFor || agent?.session !== cleanViewFor) return
    setCleanViewFor(null)
    if (viewMode === 'terminal-view') setViewModeRef.current('auto-detect')
  }, [cleanViewFor, agent?.session, viewMode])
  useEffect(() => {
    if (!bmOffered && !roomBmOffered) setBmOpen(false)
  }, [bmOffered, roomBmOffered])
  /*
   * THE AGENT'S WHITEBOARD — owner, 2026-11-01: 在一個 Session 裡面新增一個抽屜, 使用者拉出來的時候可以看到這個 Agent 他所在的
   * 白板. Offered for an agent in at least one of this person's rooms (board/agentBoards.ts), with the notes' and the
   * bookmarks' kind of handle and corner button. Out, it covers the session (the rail stays beside it on a wide screen),
   * so it is never out with the file browser: opening it closes the files, and opening the files closes it.
   */
  const wbAgent = agent && !agentIsTerminal ? agent : null
  const wbRooms = useAgentRooms(projectsQ.data?.projects ?? [], wbAgent?.hash ?? null)
  const wbOffered = wbRooms.length > 0
  const wbKey = wbAgent ? tabHolderKey(wbAgent) : null
  const wbOpen = wbOffered && wbKey !== null && wbFor === wbKey && !filesOpen
  const openWb = () => {
    setFilesOpen(false)
    setGitOpen(false)
    setPgFor(null)
    setWbFor(wbKey)
  }
  /*
   * THE AGENT'S PAGES — owner, 2026-11-03: 在 Agent session 新增一個 page icon (如同 whiteboard icon). 讓使用者能夠直接從
   * Agent session 裡面瀏覽/打開 project chat 裡的 page. The same rooms as its whiteboard's, the same kind of drawer, button
   * and handle, over the whiteboard's. One drawer at a time: each closes the other, and the files close both.
   */
  const pgOffered = wbOffered && pagesOn()
  const pgOpen = pgOffered && wbKey !== null && pgFor === wbKey && !filesOpen
  const openPg = () => {
    setFilesOpen(false)
    setGitOpen(false)
    setWbFor(null)
    setPgFor(wbKey)
  }
  useEffect(() => {
    if (!filesOpen) return
    setWbFor(null)
    setPgFor(null)
  }, [filesOpen])
  /*
   * A PROJECT ROOM'S WHITEBOARD AND PAGES, AS DRAWERS — owner, 2026-11-02: 幫我將 白板, Pages (筆記本). 都放在 Bookmark 的
   * 上方 … (比放在 header 更好). 然後手機窄版頁面也用抽屜打開的方式 … a column of buttons on the right hand side. Projects
   * and groups, not a friend chat (board/RoomCorner). Out, a drawer covers the room — so another room closes it.
   */
  const roomToolsConv = project ? project.project_id : null
  const [roomDrawer, setRoomDrawer] = useState<{ conv: string; tool: RoomTool; board: string; page?: string; n?: number } | null>(null)
  const roomDrawerOpen = !!roomDrawer && roomDrawer.conv === roomToolsConv
  useEffect(() => {
    setRoomDrawer((d) => (d && d.conv !== roomToolsConv ? null : d))
  }, [roomToolsConv])
  /*
   * A page a chat message names (its chip): in this room, its drawer on that page (a new drawer, if one is out); with a
   * full-screen board or pages over everything, or from another room, the full screen.
   */
  const roomPageAt = useRef({ conv: roomToolsConv, screens: false })
  roomPageAt.current = { conv: roomToolsConv, screens: !!boardConv || !!pagesConv }
  useEffect(
    () =>
      onRoomPage((conv, page) => {
        const at = roomPageAt.current
        if (conv === at.conv && !at.screens) setRoomDrawer({ conv, tool: 'pages', board: MAIN_BOARD, page, n: Date.now() })
        else openPages(conv, page)
      }),
    [],
  )

  /*
   * The sheet does NOT open itself.
   *
   * It did, and that was the interference the owner reported: a modal appearing over
   * someone mid-sentence takes the focus their next keystroke was going to, and the
   * Escape that dismisses it is also the key that cancels a selection. A prompt is
   * not an emergency — it is a thing to be told about. So the announcement is a
   * BUTTON that lights up, and opening stays a decision.
   */


  useEffect(() => {
    document.body.classList.toggle('sb-open', sbOpen)
  }, [sbOpen])

  // The drag lives outside React (it mutates transform directly for 1:1 tracking), so it
  // needs a way to read and commit the open state.
  const phoneNavRef = useRef(phoneNav)
  phoneNavRef.current = phoneNav
  const sbOpenRef = useRef(sbOpen)
  sbOpenRef.current = sbOpen
  useEffect(() => {
    configureSbDrag({
      isOpen: () => sbOpenRef.current,
      onCommit: setSbOpen,
      // Right-to-left with the rail already closed: the gesture the rail drag
      // never used, which the owner asked to open the note panel.
      onLeftSwipeClosed: () => setNoteOpen(true),
    })
  }, [])

  useEffect(() => {
    const onResize = () => {
      setNarrow(window.innerWidth < 760)
      setPhoneNav(window.innerWidth < 900)
      // The split gate reads the same event rather than adding a second listener —
      // two listeners on one resize is two chances to disagree about the width.
      setWinW(window.innerWidth)
      foldIfNarrow(window.innerWidth)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
    // foldIfNarrow is useCallback([]) — listed honestly, still mounts once.
  }, [foldIfNarrow])

  /**
   * Grow the composer with its text, to the ceiling set in CSS, then let it scroll.
   *
   * A textarea does not size itself, so this box sat at its one-line minimum and
   * scrolled internally from the second line on — you could not see what you had
   * written. Height is cleared to `auto` first because scrollHeight only shrinks back
   * if the element is not already holding itself open at the taller size.
   *
   * The cap is READ from the computed max-height rather than repeated here. Writing
   * the number twice is how the JS and the stylesheet quietly stop agreeing.
   *
   * This is the shared composer, so it covers the terminal, project chat and DMs at
   * once — and growing it changes the footer's height, which the observer below turns
   * into a new --foot-total so the terminal refits instead of being covered.
   */
  useEffect(() => {
    const el = composerEl.current
    if (!el) return
    el.style.height = 'auto'
    const max = Number.parseFloat(getComputedStyle(el).maxHeight)
    el.style.height = `${Number.isFinite(max) ? Math.min(el.scrollHeight, max) : el.scrollHeight}px`

    /**
     * Clamp the scroll position to what the content can actually justify.
     *
     * Reported from an iPhone: the box at its 132px ceiling showing a couple of
     * lines at the top, the line above them cut off, and everything below empty —
     * a scrollTop past the end of the content. A browser re-clamps that when the
     * content or the box changes; iOS evidently does not always, and the height
     * assignment above is exactly such a change.
     *
     * NOT reproducible in Chromium at any length, with or without trailing blank
     * lines, so this is written to make the state impossible rather than aimed at a
     * cause I have confirmed. It costs one assignment and can only ever move the
     * view back onto real content.
     */
    const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight)
    if (el.scrollTop > maxScroll) el.scrollTop = maxScroll

    /**
     * The composer doubles as the d-pad, which needs `touch-action: none` or the
     * browser claims the swipe — and that also forbids scrolling the box by finger.
     * Fine while everything fits; the moment it does not, the text you cannot see is
     * unreachable, which is what the same report described.
     *
     * So the d-pad keeps the vertical axis only while there is nothing to scroll.
     * Once there is, dragging scrolls and ↑↓ come from the ⌨ key row instead — the
     * arrows are still there, and text you cannot reach is the worse of the two.
     */
    el.classList.toggle('overflowing', el.scrollHeight > el.clientHeight + 1)
    // Typing no longer feeds `text` (uncontrolled composer), so keystroke growth
    // is driven imperatively by growComposer() in onChange; this effect now only
    // re-fits on mount and when the staged-file tray changes the available height.
  }, [staged.length])

  /**
   * Keep --foot-total equal to the footer's REAL height.
   *
   * The panes are geometrically bounded by it (`#term { bottom: calc(var(--foot-total)
   * + var(--foot-gap) …) }`), and the footer grows as the textarea wraps or the keys
   * menu opens. The 52px in theme.css is only a first-paint default — left unmeasured,
   * the terminal extends underneath the composer and its last rows are covered, which
   * is exactly what happened.
   */
  useEffect(() => {
    const el = footbar.current
    if (!el) return
    let pending = false
    const sync = () => {
      if (pending) return
      pending = true
      requestAnimationFrame(() => {
        pending = false
        document.documentElement.style.setProperty(
          '--foot-total',
          `${el.getBoundingClientRect().height}px`,
        )
        // The terminal is anchored above the footer, so it must refit with it.
        window.dispatchEvent(new Event('wt-refit'))
      })
    }
    sync()
    const ro = new ResizeObserver(sync)
    ro.observe(el)
    // The FIRST measurement can run before web fonts and emoji glyphs lay out, and the
    // textarea can wrap later. The observer catches wraps; these catch the initial
    // font settle, so --foot-total is right from load rather than one frame late.
    window.addEventListener('load', sync)
    void document.fonts?.ready?.then(sync)
    return () => {
      ro.disconnect()
      window.removeEventListener('load', sync)
    }
  }, [])

  // Keyboard show/hide changes the visual viewport; the terminal refits to match.
  useViewport(() => window.dispatchEvent(new Event('wt-refit')))

  // The project view is a swap of the main area, driven by a body class exactly as the
  // original does — that class is what hides #term and lifts #footbar above the panel.
  useEffect(() => {
    document.body.classList.toggle('proj-open', !!project)
  }, [project])

  useEffect(() => {
    document.body.classList.toggle('agent-open', !!agent || !!shared)
    // terminal-view renders INTO #term, so it must not be hidden. Only the transcript
    // views overlay the main area. A friend's agent has no terminal: always the conversation.
    document.body.classList.toggle('clean-open', (!!agent && effectiveMode !== 'terminal-view') || !!shared)
  }, [agent, effectiveMode, shared])

  useEffect(() => {
    document.body.classList.toggle('shared-open', !!shared)
    // Over: the conversation says why, and there is nothing left to write to.
    document.body.classList.toggle('shared-over', !!shared && !sharedOpen)
  }, [shared, sharedOpen])

  useEffect(() => {
    document.body.classList.toggle('dm-open', !!dmPeer)
  }, [dmPeer])

  // Any conversation open — agent, project, or DM. Drives the ONE header-bar style
  // (frosted buttons + a frosted-capsule title), owner 2026-08-15: 對話系統 header
  // 都歸一化為 agent chat 的樣式.
  useEffect(() => {
    document.body.classList.toggle('chat-open', !!agent || !!project || !!dmPeer || !!shared)
  }, [agent, project, dmPeer, shared])

  // A bare terminal: no composer — owner, 2026-10-18 (＋ → Terminal): 也不需要有文字輸入框.
  // One typing surface, the terminal itself (shell.css, body.term-open).
  useEffect(() => {
    document.body.classList.toggle('term-open', agentIsTerminal)
    return () => document.body.classList.remove('term-open')
  }, [agentIsTerminal])


  /**
   * The session you are looking at counts as read.
   *
   * Port of the original's sbRefreshUnread rule: on every idle poll, if the OPEN
   * session still shows unread, clear it. Re-running per poll is deliberate — messages
   * that arrive while you are watching must clear too, not just the backlog at open.
   * The server owns the cursor, so this also clears the badge on your other devices.
   *
   * The decision lives in nextMarkRead, with the reasoning: it has to suppress the
   * repeat POST without suppressing the NEXT message, and the previous version only
   * managed the first of those.
   */
  const markedRead = useRef<string | null>(null)
  /**
   * Is the selected conversation actually in front of the user?
   *
   * Assigned during render like the other refs in this file, and READ from the
   * effects below — because the flags it is built from are declared further down
   * this component, so naming them up here would read them in their temporal dead
   * zone. A ref assignment happens after render, when they exist.
   *
   * ⚠️ READING IT IS NOT ENOUGH TO RE-RUN ANYTHING, and this comment used to say it
   * was: "those effects already re-run on every idle poll, so the ref is re-read
   * every 5s and the moment the conversation comes back on screen the mark fires on
   * the next beat." THERE IS NO 5s IDLE POLL. It was deleted under the no-heartbeat
   * rule (useIdleStatus.ts: "NOT A POLL, and there is no interval here on purpose"),
   * noHeartbeat.test.ts keeps it deleted, and only this explanation survived it —
   * which is how the agent badge came to have no way of clearing for a quiet agent
   * (owner, 2026-09-21, agy stuck at 2).
   *
   * So both effects below list the on-screen flags in their OWN dependency arrays,
   * and this ref is only how the body reads values it cannot name at this point in
   * the file. If you add a flag to conversationOnScreen, it goes in both arrays too
   * — the ref will not carry it for you.
   */
  const onScreen = useRef(true)

  useEffect(() => {
    const key = agent ? idleKey(agent) : null
    const unread = key ? (idle.byKey.get(key)?.unread ?? 0) : 0
    /*
     * Not reading it ⇒ not read.
     *
     * Skipping BEFORE nextMarkRead, so the guard is left untouched: recording this
     * count as handled while declining to post would mean the badge never cleared
     * once the user did come back to it.
     *
     * This is the owner's report, diagnosed independently by three of us from the
     * same numbers — their own agent's read cursor sat exactly equal to its message
     * count (14/14) while two other agents sat at 0/10 and 9/11. The count was
     * never the problem. Selecting a conversation was being treated as reading it,
     * so a message arriving for the agent they had open was marked read within one
     * round trip of landing, and the badge's entire life was that round trip:
     * visible in the data, never on screen.
     *
     * The fix is NOT "mark once on open", which was suggested and would bring back
     * an older bug this file already carries a comment about: a message arriving
     * while you watch has to clear too, or the badge reappears mid-read and stays.
     * The rule has to be about VISIBILITY, not about a moment — while the pane is
     * in front of you, everything landing in it is read; while it is behind the
     * rail, a sheet, or another app, nothing is.
     */
    if (!onScreen.current) return
    const { post, seen } = nextMarkRead(markedRead.current, key, unread)
    markedRead.current = seen
    if (!post || !agent) return
    void agentMarkRead(agent.session, agent.host)
      .then(() => qc.invalidateQueries({ queryKey: ['idle-status'] }))
      .catch((e: unknown) => {
        /**
         * Retry only what retrying could fix.
         *
         * Clearing the guard on EVERY failure means a POST every 5s for as long as
         * the session is open, and one failure here is permanent by design: a
         * remote agent whose host has no live mount answers `host_not_mounted`,
         * because the server refuses to advance a cursor on the wrong machine. It
         * was doing exactly that in production — the badge cannot clear and we were
         * asking twelve times a minute.
         *
         * A 4xx is the server declining, and it will decline identically in five
         * seconds. Leaving the guard set means the count is treated as handled;
         * nextMarkRead re-arms on its own when the count changes or the session
         * does, so a remount followed by any new message recovers without this.
         */
        /*
         * ONE RULE FOR BOTH BADGES. This inlined `4xx is permanent` while the project
         * effect below called the tested markReadRefusal, which carves out one case:
         * a 403 whose message names CSRF is a STALE TOKEN, not a refusal — retryable,
         * and the status that hands out a fresh one is worth refetching.
         *
         * The consequence of the two disagreeing was not hypothetical. agentMarkRead is
         * an api.post and http.ts attaches CSRF to every post, and http.ts:121-124
         * predicts this failure in as many words: "a request that omits a necessary one
         * fails as a 403 that the mark-read retry split treats as permanent". So a stale
         * token wedged the AGENT badge permanently while the project badge healed itself
         * — and a badge stuck ON is, in this file's own words, the version nobody thinks
         * to report.
         *
         * The rule already had a test (agents/markRead.test.ts). It was being applied on
         * the path that did not need it.
         */
        const err = e instanceof ApiError ? e : null
        const { permanent, refreshCsrf } = markReadRefusal(err?.status ?? null, err?.message ?? '')
        if (!permanent) markedRead.current = null
        if (refreshCsrf) void qc.invalidateQueries({ queryKey: ['security-status'] })
      })
    /*
     * THE ON-SCREEN FLAGS ARE DEPENDENCIES, exactly as the project effect below has
     * had them since 2026-08-17 — owner, 2026-09-21, with a screenshot of agy
     * holding 2: 「不管是在 terminal 還是 CleanView 上面都沒有辦法讓這個小紅點消失」.
     *
     * `onScreen` is a REF, and a ref cannot re-run an effect. So bringing the
     * conversation in front of the person — closing the rail, tapping the row that
     * is already selected, switching Terminal <-> Clean View — changed nothing this
     * effect depended on, and the post that clears the badge was never issued. Not
     * "issued and refused": the edge's own durable command store shows the last
     * mark_read for agy at 19:30:40 with count 8, then the two messages landing at
     * 19:48:37, and nothing after — while other agents were marked read at 22:02.
     * The server was never asked.
     *
     * WHY THIS LOOKED LIKE ONE AGENT'S BUG. The effect still re-runs whenever
     * `idle.byKey` changes, and a BUSY agent churns the roster constantly, so its
     * badge cleared by accident on the next unrelated roster change. agy is idle and
     * quiet; nothing churned, so nothing re-fired. Every quiet agent had this bug
     * and only the quiet one could show it.
     *
     * AND THE EXEMPTION THIS EFFECT WAS GRANTED HAS BEEN DELETED. 6e32fcf fixed
     * exactly this for the project badge and wrote down why the agent half was left
     * alone: "there is no list poll to re-run it, unlike the agent effect's
     * idle.byKey dep". That was true while a 5s idle poll remade the map. The poll
     * is gone (useIdleStatus.ts: "NOT A POLL, and there is no interval here on
     * purpose"), noHeartbeat.test.ts keeps it gone, and the justification above at
     * `const onScreen = useRef(true)` still cites it verbatim. A comment outlived
     * the mechanism it was describing, and the exemption outlived both.
     */
  }, [
    agent,
    idle.byKey,
    qc,
    // Everything conversationOnScreen reads, so false -> true re-fires this.
    // markReadDeps.test.ts holds this list equal to that block.
    phoneNav,
    sbOpen,
    noteOpen,
    filesOpen,
    wbOpen,
    pgOpen,
    roomDrawerOpen,
    serverOpen,
    drawerOpen,
    scanOpen,
    viewing,
    viewArtifact,
    morePage,
    cardAgent,
  ])

  /**
   * The same cursor advance for the open project, reusing nextMarkRead so both halves
   * of the sidebar clear their badge under one rule.
   *
   * The cursor is the server's, not this browser's, matching agentMarkRead above: the
   * owner reads on the phone and looks at the desktop, and a badge that clears on only
   * one of them is worse than one that never clears — it looks correct.
   */
  const projMarkedRead = useRef<string | null>(null)
  useEffect(() => {
    const key = panelProject?.project_id ?? null
    const unread = typeof panelProject?.unread === 'number' ? panelProject.unread : 0
    // Same visibility rule as the agent path above, and for the same reason: the
    // two halves of the sidebar must not disagree about what "read" means.
    if (!onScreen.current) return
    const { post, seen } = nextMarkRead(projMarkedRead.current, key, unread)
    projMarkedRead.current = seen
    if (!post || !key) return
    void projectMarkRead(key, panelProject?.last_seq)
      .then(() => {
        /*
         * OPTIMISTIC, and that is not a shortcut. The cursor advances over the WebSocket
         * fire-and-forget and unacked, so an immediate refetch would race the write and
         * re-light the badge with the stale count. The advance is reliable (frames queue
         * and flush on connect; the write is monotonic), so this card's badge clears now
         * and window-focus / projects_changed reconcile it later, by which time the
         * server agrees.
         *
         * The edge lane marked read with a synchronous POST and could simply invalidate;
         * it was deleted on 2026-09-02 and the branch went with it.
         */
        qc.setQueryData<{ ok: boolean; projects: ProjectRow[] } | undefined>(['projects'], (old) =>
          old
            ? {
                ...old,
                projects: old.projects.map((p) =>
                  p.project_id === key ? { ...p, unread: 0 } : p,
                ),
              }
            : old,
        )
        /*
         * The same optimistic clear for the DM list, because the key may name a DM
         * and the two lists are separate reads of the same cursor. Written blind
         * (no branch on which kind it was): the map only touches a row whose
         * conv_id matches, so on a project this is a no-op over a short list, and
         * a branch here is one more place for the two halves to disagree.
         */
        qc.setQueryData<{ conversations: ConversationRow[] } | undefined>(
          ['dm-conversations'],
          (old) =>
            old
              ? {
                  ...old,
                  conversations: old.conversations.map((c) =>
                    c.conv_id === key ? { ...c, unread: 0 } : c,
                  ),
                }
              : old,
        )
      })
      .catch((e: unknown) => {
        /**
         * Same split as the agent path — a 4xx is the server declining and it will
         * decline again in twenty seconds — with one carve-out the agent path does
         * not need.
         *
         * Every POST under /project/* requires X-CSRF-Token, and that token goes
         * stale. A stale one is a 403, which the rule above would file as permanent:
         * the cursor would stop advancing and the badge would never clear again, with
         * nothing on screen to say so. That is the failure mode this whole feature
         * exists to avoid, arriving by the back door — a badge that lies is worse
         * than no badge, and one stuck ON is the version nobody thinks to report.
         *
         * So a CSRF refusal re-arms and refetches the status that hands out the
         * token. It cannot spin: the effect re-runs when `project` changes identity or
         * the on-screen state flips, and it stops the moment a fresh token works.
         */
        const err = e instanceof ApiError ? e : null
        const { permanent, refreshCsrf } = markReadRefusal(err?.status ?? null, err?.message ?? '')
        if (!permanent) projMarkedRead.current = null
        if (refreshCsrf) void qc.invalidateQueries({ queryKey: ['security-status'] })
      })
    // Re-run when the project comes ON-SCREEN, not only when `project` changes identity.
    // On a phone the rail IS the screen, so opening a selected project flips sbOpen (and
    // thus onScreen) false→true WITHOUT changing `project`; the same holds for closing an
    // overlay. Without these deps a returning user who resumes onto ?project= with the rail
    // up never marks read and the badge sticks forever — there is no list poll to re-run
    // this (unlike the agent effect, which its idle.byKey poll re-fires). onScreen.current
    // is recomputed in render before this effect commits, so the ref read above is fresh.
    //
    // PANELPROJECT, NOT `project` — the DM half of the same lesson (owner, 2026-08-25:
    // 朋友的聊天…讀取之後沒有辦法取消小紅點). For a friend DM `project` is null and never
    // changes: the row this effect actually reads is `panelProject = project ?? dmProject`,
    // and dmProject is assembled from TWO queries that resolve after the open —
    // useManagedFriendDm (the conv_id) and dm-conversations (unread + last_seq). Keyed on
    // `project`, the effect ran once against a half-built row (no key, or unread 0) and
    // nothing ever re-ran it when the counts landed — so the DM was read on screen and the
    // cursor never advanced; the friend row and the Humans tab kept their dot forever.
    // Keyed on `panelProject`, every resolution step re-runs it, and nextMarkRead's
    // signature dedupe keeps that from ever posting twice for one count.
    /*
     * SAME COMPLETE LIST AS THE AGENT EFFECT ABOVE. This one has carried the
     * on-screen flags since 6e32fcf — it is the precedent that fix was taken from —
     * but only the five that existed then. scanOpen, viewing, viewArtifact and
     * cardAgent were added to conversationOnScreen later and never added here, so
     * closing an artifact page or the agent card could not re-fire this either.
     * Same defect as the agent badge, one effect down, found by the same pass.
     */
  }, [
    panelProject,
    qc,
    phoneNav,
    sbOpen,
    noteOpen,
    filesOpen,
    wbOpen,
    pgOpen,
    roomDrawerOpen,
    serverOpen,
    drawerOpen,
    scanOpen,
    viewing,
    viewArtifact,
    morePage,
    cardAgent,
  ])

  /**
   * THE NUMBER ON THE APP ICON — how many AGENTS have something unread.
   *
   * Owner, 2026-09-21: 現在在 App 裡面有三個 agent 還沒有被讀取到他最新的訊息。那麼小
   * 紅點在 App icon 上面就應該要顯示為三. Agents, not messages — see appBadge.ts for
   * why that is a different number from the one on the nav bar right above it.
   *
   * Counted from `idle.byKey`, the same map the rail's dots come from, so the icon
   * and the list can never disagree. On a shell with no badge handler (a browser, or
   * the 1.0 store build) postAppBadge does nothing and says so.
   *
   * FIRES ON CHANGE AND ON MOUNT, which the dependency gives for free — and unlike
   * the mark-read effects above it may do so from anywhere, because the icon is
   * about the whole account rather than about what is in front of the person.
   */
  const badgeCount = agentsWithUnread(idle.byKey.values())
  useEffect(() => {
    postAppBadge(badgeCount)
  }, [badgeCount])

  /**
   * AND AGAIN THE MOMENT HE LEAVES.
   *
   * avg-devapp-appstore asked for this specifically, and it is not redundant with
   * the effect above: while the app is being backgrounded iOS is the only reader of
   * that number, so the last value posted has to be the true one at exactly that
   * instant. Re-posting on `hidden` costs nothing — the native side ignores a
   * duplicate — and closes the window where a count changed in the same tick the app
   * went away.
   *
   * The count is read through a ref so this listener is bound ONCE. Binding it with
   * `badgeCount` in the deps would add and remove a window listener on every change,
   * which is the shape that leaks one behind under React's StrictMode double-invoke.
   */
  const badgeCountRef = useRef(badgeCount)
  badgeCountRef.current = badgeCount
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') postAppBadge(badgeCountRef.current)
    }
    document.addEventListener('visibilitychange', onHide)
    return () => document.removeEventListener('visibilitychange', onHide)
  }, [])

  // A file preview also replaces the main area.
  useEffect(() => {
    document.body.classList.toggle('file-open', !!viewing)
  }, [viewing])

  // Opening one conversation closes the other by construction: the URL holds exactly
  // one selection.

  /**
   * Terminal two-stage submit (docs/UI-COMPONENTS.md §5.5).
   *
   * Agent CLIs have their own input box, so the flow is: first Cmd/Ctrl+Enter types the
   * text into that box WITHOUT pressing Enter, letting you read it back inside the
   * terminal; the composer clears, so the second Cmd/Ctrl+Enter sends a bare Enter and
   * submits it. Long prompts and phone typos are the reason this exists.
   */
  const terminalSubmit = async () => {
    /*
     * An agent at a prompt is no longer a refusal — it is a queue.
     *
     * This used to keep the text and tell the owner to switch to the terminal, which
     * was the best available answer when the only way in was the pty: characters typed
     * at a menu are eaten as selection keys, so the message would have vanished AND
     * answered the question.
     *
     * `/agent/say` holds it instead, on the same queue and the same flush loop as
     * every other delivery, and the server decides — not this five-second-stale poll.
     * Refusing here now would be strictly worse than doing nothing: it would reject a
     * message the server was willing to hold.
     *
     * Still true in the TERMINAL view, where the guard never fired anyway
     * (`swallowsTyping` is false there): you can see the prompt, and the keystrokes
     * are the answer.
     */
    /*
     * A second press while an upload is in flight must not start another one.
     *
     * This is not a rare race: the two-stage submit TEACHES a double press — stage one
     * types the text, stage two sends the Enter — so the natural rhythm is exactly
     * what would upload every staged file twice and put two sets of tags in the
     * agent's box. A ref rather than the `sending` state because both presses can land
     * inside one render, and the second would read a stale false.
     */
    if (uploading.current) return
    const sock = termSock.current
    /*
     * Only the TERMINAL path needs the pty. A rich view speaks over HTTP now, so a
     * dropped background socket no longer refuses its sends — which used to be an
     * invisible failure, because Clean View renders its transcript from HTTP and went
     * on looking alive while every send bounced.
     */
    if (stagesBeforeSending(effectiveMode) && !sock?.ready) {
      /*
       * The terminal view only, now — and the text is kept, which was always the
       * right half.
       *
       * This used to catch Clean View too, where the pty is a background socket and a
       * drop is invisible: the transcript renders from HTTP and went on looking alive
       * while every send bounced. That whole failure mode is gone, because a rich view
       * no longer needs the socket to send. Here the terminal IS the thing on screen,
       * so a dead link is visible and the message names it.
       *
       * The kick is still worth it: the socket retries on its own, and this asks it to
       * try NOW rather than at the end of a backoff nobody can see.
       */
      /*
       * A SEND ONTO A FROZEN SEAT IS RESUME INTENT — owner, 2026-09-15: 我剛剛嘗試輸入文字
       * 給你的 Terminal, 卻卡住, 需要先切換到別的 Agent session 再切回來才能夠成功將文字信息
       * 送出. Switching away and back was a retake by another name, and it worked for
       * exactly that reason.
       *
       * `ready` is false for two opposite situations. A DROPPED link reconnects on its
       * own, so a kick and "press again" is right. A seat another window TOOK never comes
       * back until somebody asks for it — so "reconnecting… press send again" was both
       * untrue and a loop, and the text sat there however many times it was pressed.
       *
       * The terminal surface has always known this: typing on a frozen screen calls the
       * retake (useTerminalConnection, term.onData — "any key on a frozen surface IS the
       * resume intent"). The composer is the other input path, the one that same file
       * calls THE input path on mobile, and it was the half that did not.
       */
      if (sock?.frozen) {
        sock.retake?.()
        status('Live view was in another window — taking it back. Your text is kept; press send again.')
        return
      }
      window.dispatchEvent(new Event('wt-agent-socket-kick'))
      status('Terminal link is down — reconnecting. Your text is kept; press send again.')
      return
    }
    // Uncontrolled composer: the live text is the DOM value, never React state.
    let body = (composerEl.current?.value ?? '').trim()

    /*
     * Attached files are uploaded HERE, not when they were dropped.
     *
     * Same order as the project chat's: staging is a decision you can undo, and
     * uploading on drop would put bytes on the server for a file you removed from the
     * tray a second later. It also means the upload's failure lands where the user is
     * looking — at the moment they pressed send — instead of a minute earlier.
     *
     * The paths go into the agent's own input box rather than being executed, which is
     * the same two-stage rule the text follows: nothing reaches the agent until a
     * second press, so a wrong file is still a wrong file you can see first.
     */
    const items = staged.filter((f) => !f.err)
    if (items.length) {
      uploading.current = true
      setSending(true)
      try {
        const done: NoteAttachment[] = []
        for (const item of items) {
          const r = await noteFileUpload(item.file, (pct) =>
            setStaged((prev) => prev.map((x) => (x.id === item.id ? { ...x, pct } : x))),
          )
          if (r.error || !r.meta) throw new Error(r.error ?? `${item.file.name} did not upload`)
          done.push(r.meta)
          dropStaged(item.id)
        }
        const out = attachDispatchText(body, done, agent?.host ?? null)
        body = out.text
        if (out.withheld) status(withheldNotice(out.withheld, agent?.host ?? 'that host'))
      } catch (e) {
        /*
         * Staged files survive a failure, exactly as typed text does — retrying is
         * one press, and silently dropping them would lose the user's selection.
         *
         * The lock is worth translating rather than passing through. A terminal agent
         * is reachable while the vault is locked (`/agent/*` sits outside the gate),
         * so this is the one place in the app where everything on screen works and
         * attaching does not — and the server's word for that is the token
         * `vault_locked`, which tells a reader nothing about what to do next.
         */
        const raw = String(e)
        status(
          raw.includes('vault_locked')
            ? 'Unlock the vault to attach files — the terminal itself does not need it.'
            : (limitRefusalIn(raw) ?? raw),
        )
        return
      } finally {
        // `finally`, so a thrown upload cannot leave the guard stuck on and every
        // later press silently do nothing — a deadlock that looks like a dead button.
        uploading.current = false
        setSending(false)
      }
    }

    if (!stagesBeforeSending(effectiveMode)) {
      /*
       * One press in a rich view. Nothing is staged because nothing could be read
       * back: the agent's input box is not on screen here.
       *
       * An empty composer sends nothing at all, rather than the bare Enter that
       * completed stage two in the terminal. With one stage there is no second half
       * to complete, and a stray Enter into an agent this view cannot show is a
       * keystroke with an invisible effect — the exact thing the prompt guard above
       * exists to prevent.
       */
      if (body.length === 0) return
        /*
         * Through /agent/say, not the pty.
         *
         * The pty cannot HOLD anything: characters land in whatever the CLI has on
         * screen, so text typed while an agent was busy went into its input box
         * rather than the queue. The owner found that by using the product, after I
         * had reported the rule satisfied — I measured every path through `inject()`
         * and called it every path, and the owner's own typing is the one that never
         * went through it.
         *
         * `queued` is a SUCCESS, not a failure: the message is accepted and held, and
         * the only thing this must not do is imply the agent has seen it. So the
         * composer clears either way — the text is somewhere real — and the status
         * line says which of the two happened.
         *
         * The terminal view keeps the pty below, deliberately: there the input box is
         * on screen and staging is the point, and a stage is not a message. It is
         * half of one, and half a message has nothing to queue.
         */
        try {
          const r = await postAgentSay(agent!.session, body, agent!.host)
          /*
           * ON SCREEN NOW, not when the agent finishes — owner, 2026-09-16:
           * 使用者輸入的文字就全部都被吃掉了 … 當該 Agent 開始輸出之後, 使用者的文字
           * 以及 Agent 本身的文字又可以被看到.
           *
           * There is no stored row to echo here the way project chat echoes one: an
           * agent's input goes into the pty, and claude writes its transcript when the
           * message COMPLETES. So the client holds what it just sent and Clean View
           * draws it until the real record lands (agents/live/sentEchoStore.ts).
           *
           * AFTER the await, so it claims only what the server accepted — and for a
           * QUEUED send too, because held is accepted: the message exists, it is just
           * waiting, and that is exactly the case where the wait is longest and a blank
           * screen is least bearable.
           */
          setSentEcho(agent!.session, body)
          if (r.queued) {
            status(r.detail ?? 'Agent is busy — held, and delivered when it is free.')
          }
        } catch (e) {
          // Kept, exactly as an upload failure keeps it: retrying is one press, and
          // discarding the text would be a worse bug than the one being reported.
          status(`Could not reach the agent — your text is kept. ${String(e)}`)
          return
        }
      setComposerValue('')
      return
    }

    // Reachable only through the terminal branch above, which already returned
    // when the socket was not ready. Stated rather than asserted with `!`, so a
    // future reorder fails loudly here instead of writing into nothing.
    if (!sock) return
    /*
     * READY IS NOT ALIVE (owner, 2026-09-05: 有時候 … cmd+enter 輸入 會有卡住無法輸出
     * 的問題, refresh 網頁之後就好了). The terminal rides the account socket, and a
     * half-open account socket reads OPEN for minutes while every keystroke written
     * into it vanishes: nothing echoes, `ready` stays true, the reconnect code stands
     * down, and only a reload built a new socket. The starving READ got its probe on
     * 2026-08-25; a send had none. So, when the socket has been silent a while, ask
     * it first — one round trip on a live pipe; on a corpse the verdict closes it,
     * the facade shuts, and the line below says so and keeps the text.
     */
    await whenStreamTrusted()
    if (!sock.ready) {
      window.dispatchEvent(new Event('wt-agent-socket-kick'))
      status('Terminal link was dead — reconnecting. Your text is kept; press send again.')
      return
    }
    if (body.length === 0) {
      sock.write('\r') // stage 2: submit what is already in the agent's input box
      return
    }
    sock.write(body) // stage 1: stage the text, no Enter
    setComposerValue('')
  }

  /*
   * VOICE INPUT — owner, 2026-09-05: 在前端增加一個麥克風按鈕(放置於 Text edit 輸入按鈕的左邊).
   *
   * Three parts, deliberately in three files, so the VAD phase can replace the FIRST
   * without touching the other two: useVoiceRecorder owns the microphone and hands
   * over a clip; transcribeBlob takes a clip to the voice service (direct, same
   * origin, bearer token — never through the BFF, by the owner's ruling); the words
   * land at the CARET, because a person who tapped mid-sentence meant "here". Every
   * failure is one English sentence on the status line — the service writes its own
   * `message` for the person and it is shown verbatim.
   */
  const putWordsAtCaret = (words: string) => {
    const el = composerEl.current
    const value = el?.value ?? ''
    const start = el?.selectionStart ?? value.length
    const end = el?.selectionEnd ?? value.length
    const next = insertAtCaret(value, start, end, words)
    if (searchModeRef.current) {
      // Dictated into the keyword box: it is a query, not a draft.
      setBoxOnly(next.value)
      setSearchQuery(next.value)
    } else setComposerValue(next.value)
    if (!el) return
    /*
     * NO focus() HERE — owner, 2026-09-15: 不要讓小鍵盤自動跳出來 … 如果使用者沒有去
     * 點擊 text editor, 那麼小鍵盤就不該跳出來, 直接藏著, 並且在 text editor 裡面顯示
     * 文字就好了. On a phone, focusing the box IS raising the keyboard, so dictating
     * with the keyboard down — the whole point of dictating — used to throw half the
     * screen away the moment the words arrived. caretAfterDictation moves the caret
     * only when the box already had focus, and otherwise leaves the page alone.
     */
    caretAfterDictation(el, next.caret)
  }
  const voice = useVoiceRecorder({
    onClip: async (blob, durationMs) => {
      try {
        const r = await transcribeBlob(blob, durationMs)
        if (r.text.trim()) putWordsAtCaret(r.text)
        else status('No speech was detected.')
      } catch (e) {
        // A quota refusal opens the plan sheet; every other failure is a sentence.
        reportVoiceError(e, status)
      }
    },
    onProblem: (message) => status(message),
    maxMs: recordCapMs,
  })
  // The tap and the hotkey share one gate: when today's allowance is known to be
  // gone, the press opens the plan sheet instead of the microphone (billing/voiceGate).
  const micPress = () => {
    if (micGate(voice.state)) return
    voice.toggle()
  }
  /*
   * Ctrl+M toggles this microphone — owner, 2026-09-06. Registered once; the
   * toggle is read through a ref because the recorder hands out a new one per
   * render, and the hotkey must reach the CURRENT state (start when idle, stop
   * when recording), not the one from the render that registered it.
   */
  const voiceToggleRef = useRef(micPress)
  voiceToggleRef.current = micPress
  useEffect(() => registerMicHotkey(() => voiceToggleRef.current()), [])

  /** Routes to whichever conversation is open. Terminal view has its own flow. */
  const send = async (bodyOverride?: string) => {
    const body = bodyOverride ?? (composerEl.current?.value ?? '').trim()
    /*
     * The DECISION lives in sendPlan.ts; this function only carries it out. The old
     * inline check counted ERRORED staged files, so an oversize chip plus a typed
     * caption took the files branch, uploaded nothing, and cleared the composer —
     * text destroyed, nothing sent, nothing said. planSend counts only sendable
     * files, so that press now goes out as an ordinary message while the errored
     * chip stays on screen wearing its error.
     */
    const plan = planSend({ body, staged, inProject: !!panelProject })
    if (plan.kind === 'nothing' || sending || !panelProject) return
    setSending(true)
    setSendError(null)
    // Clears only on SUCCESS — a failed send keeps the text.
    const clearComposer = () => {
      if (bodyOverride !== undefined) return // already cleared by the caller
      setComposerValue('')
    }
    try {
      if (plan.kind === 'files') {
        for (const id of plan.ids) {
          const item = staged.find((x) => x.id === id)
          if (!item) continue
          await projectFileUpload(
            panelProject.project_id,
            item.file,
            // Only the caption-bearer carries the text, or a three-photo drop
            // repeats its caption three times.
            id === plan.captionOn ? body : '',
            (pct) =>
              setStaged((prev) => prev.map((x) => (x.id === item.id ? { ...x, pct } : x))),
            // Sender identity for the uploader's own optimistic row (else "unknown").
            myEmail,
            myName,
            myPicture,
          )
          dropStaged(id)
        }
        // Any text was consumed as the caption; don't also post it as a message. Files are not threaded — they went
        // to the room — so the reply is over too, or the next words would land in a thread the files did not.
        if (replying) setReplyTo(null)
        clearComposer()
      } else {
        // Real projects AND the managed friend DM send through the same rail — a friend DM
        // is just a locked conversation now, so there is no separate E2E send path here.
        const sent = await sendProjectMessage(
          panelProject.project_id,
          body,
          myEmail,
          myName,
          myPicture,
          replying?.seq,
        )
        // The stored row, into the transcript NOW — see projectEcho.ts.
        if (sent.message) projectEcho(panelProject.project_id, sent.message)
        if (replying) setReplyTo(null)
        clearComposer()
      }
    } catch (e) {
      // Both: the status bar for a glance, and a persistent line that survives it.
      const why = e instanceof ApiError && e.message === 'csrf'
        ? 'Session token went stale — reload the page and send again.'
        : (limitRefusalIn(String(e)) ?? String(e))
      setSendError(why)
      status(why)
    } finally {
      setSending(false)
    }
  }

  /*
   * A FRIEND'S AGENT: the composer's send, through the share. The words and any attached files go
   * the narrow road (routes_agent_share) — each file up the owner's own upload lane, asked of their
   * machine by the cloud, and the message naming the files by the ids the cloud handed back. One
   * press, as in your own agent's Clean View; a held message is a delivery, and says so.
   *
   * What uploaded before a failed say is kept for the next press, beside the kept text: the tray
   * has already let those files go, and dropping them would make a retry send the words alone.
   */
  const sharedKept = useRef<{ id: string; files: string[] } | null>(null)
  const sharedSubmit = async () => {
    if (!shared || sending || uploading.current) return
    if (!sharedOpen) {
      status(sharedLive.gone ?? 'This agent is no longer shared with you.')
      return
    }
    const id = shared.share_id
    const text = (composerEl.current?.value ?? '').trim()
    const items = staged.filter((f) => !f.err)
    const kept = sharedKept.current?.id === id ? sharedKept.current.files : []
    if (!text && !items.length && !kept.length) return
    uploading.current = true
    setSending(true)
    setSendError(null)
    try {
      // The reader's own agents whose tags are still in the words (phase 2).
      const pings = [...sharedPings.current]
        .filter(([h]) => text.includes(`<ping-agent:${h}`))
        .map(([hash, device_id]) => ({ hash, device_id }))
      const r = await sendToShared(
        id,
        text,
        items,
        kept,
        {
          progress: (itemId, pct) => setStaged((prev) => prev.map((x) => (x.id === itemId ? { ...x, pct } : x))),
          uploaded: (itemId, held) => {
            sharedKept.current = { id, files: held }
            dropStaged(itemId)
          },
        },
        pings,
      )
      sharedKept.current = null
      sharedPings.current.clear()
      setComposerValue('')
      if (r.queued) status('It is busy — your message reaches it when it is free.')
    } catch (e) {
      const why = shareErrText(e)
      const files = sharedKept.current?.id === id ? sharedKept.current.files.length : 0
      setSendError(`Not sent — your words${files ? ' and files' : ''} are kept. ${why}`)
      status(why)
    } finally {
      uploading.current = false
      setSending(false)
    }
  }
  /** The friend's Stop: the cloud asks their machine for the owner's own key, while it works. */
  const stopShared = async () => {
    if (!shared) return
    setStopHeld(true)
    window.setTimeout(() => setStopHeld(false), 3000)
    try {
      status(await stopSharedAgent(shared.share_id))
    } catch (e) {
      status(shareErrText(e))
    }
  }

  /**
   * True only when a MESSAGE-based conversation owns the composer.
   *
   * An agent does NOT count: its input goes into the pty through the two-stage flow,
   * even in claude-view. Including `agent` here was a bug — Cmd+Enter routed to the
   * chat sender, which returns early on empty text, so the second press did nothing.
   */
  const inChat = !!project || !!dmPeer

  /**
   * Where a submit goes — ONE function, every trigger.
   *
   * The send button and Cmd/Ctrl+Enter each used to make this choice for themselves:
   * the button from live values, the shortcut from refs. They agreed, but nothing
   * made them agree, and the owner's report ("only the button sends directly") is
   * what a suspected divergence costs even when there is none — I had to read three
   * call sites to establish they were the same.
   *
   * Now there is one, so the question cannot be asked again.
   */
  const submitFromControl = () => {
    // Mid-search the send gesture (the button, Cmd+Enter) steps to an older match;
    // the keyword must never leave for the agent as a message.
    if (searchModeRef.current) {
      stepSearch(-1)
      return
    }
    if (inChat) void send()
    else if (agent) void terminalSubmit()
    else if (sharedId) void sharedSubmit()
  }

  sendRef.current = send
  inChatRef.current = inChat
  submitFromControlRef.current = submitFromControl
  agentSelRef.current = agent ? { host: agent.host, session: agent.session } : null
  // Search lives in Clean View: when the view goes (terminal view, a chat, no agent),
  // the box is given back the same way Esc gives it back.
  const cleanViewOpen = (!!agent && !inChat && effectiveMode !== 'terminal-view') || !!shared
  useEffect(() => {
    if (!cleanViewOpen && searchModeRef.current) exitSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanViewOpen])
  /*
   * WHERE A SEARCH CAN START: an agent whose kind HAS a Clean View, outside a chat.
   * Terminal View counts (owner, 2026-09-12: the button shows there and jumps to Clean
   * View); a kind that resolves to the raw terminal does not — nothing there to search.
   */
  // A friend's agent: what its screen holds (SharedAgentPanel searches no further).
  const searchAvailable = (!!agent && !inChat && viewForKind(agentKind) !== 'terminal-view') || !!shared
  /** The button's press and Cmd/Ctrl+F: open the search, switching to Clean View first if needed. */
  const startSearch = () => {
    if (searchModeRef.current) {
      composerEl.current?.focus()
      return
    }
    // The same switch the agent chip's tap makes; one render carries both the new view
    // and the search mode, so the "search lives in Clean View" effect sees them together.
    // Not for a friend's agent: it has only its conversation, and the switch is YOUR view preference.
    if (!shared && viewMode === 'terminal-view') setViewMode('auto-detect')
    enterSearch()
  }
  // The held global-search landing (see openMessage): start it once its agent is on screen,
  // a frame after the conversation switch has put the previous search and draft away.
  useEffect(() => {
    const p = pendingConvSearch.current
    if (!p || !agent || agent.hash !== p.hash || (agent.device_id ?? null) !== p.device) return
    pendingConvSearch.current = null
    const id = requestAnimationFrame(() => {
      startSearch()
      setBoxOnly(p.query)
      setSearchQuery(p.query)
      setSearchTarget(p.index)
    })
    return () => cancelAnimationFrame(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.hash, agent?.device_id, dKey])
  // The document listener below is bound once and reads through refs.
  const searchAvailableRef = useRef(false)
  const startSearchRef = useRef(startSearch)
  searchAvailableRef.current = searchAvailable
  startSearchRef.current = startSearch
  /*
   * SEARCH HOTKEYS — owner, 2026-09-12: 讓使用者按下 Esc 可以跳出 Search 狀態; 讓使用者按下
   * Ctrl+F 或是 Cmd+F 可以開啟搜尋功能.
   *
   * Capture phase, like Cmd+J: the browser binds Cmd/Ctrl+F to its own find bar, and
   * xterm would otherwise eat both keys. Cmd/Ctrl+F opens the search (or, already
   * open, puts the caret back in the keyword box); Esc closes it from wherever focus
   * went — the strip's buttons, a block the reader clicked, the view itself — not only
   * from the box. Two things are left alone: Ctrl+F while the terminal pane itself has
   * focus (readline's forward-char; Cmd+F is taken everywhere), and Esc while a sheet
   * is open above the search — that Esc is the sheet's.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (!searchModeRef.current || document.querySelector('[role="dialog"]')) return
        /*
         * Nor Esc typed in the RAIL's search field — that Esc is the rail search's ✕.
         * A global-search tap leaves the conversation's search open, so going back to
         * the field and pressing Esc closed the conversation's search, which the
         * reader was not looking at, and left the rail's up (2026-09-29, found by the
         * phone-size battery).
         */
        if ((document.activeElement as HTMLElement | null)?.id === 'sbSearchInput') return
        e.preventDefault()
        e.stopPropagation()
        exitSearch()
        return
      }
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey || e.code !== 'KeyF') return
      if (!searchAvailableRef.current) return
      if (e.ctrlKey && !e.metaKey && (document.activeElement as HTMLElement | null)?.closest?.('.xterm')) return
      e.preventDefault()
      e.stopPropagation()
      startSearchRef.current()
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
   * GLOBAL SEARCH, FROM ANYWHERE — owner, 2026-09-29: 加入一個 Global hot key
   * `shift + ctrl + F` 讓 webapp, mac app 可以直接開啟 Global search.
   *
   * Ctrl+Shift+F (Cmd+Shift+F on a Mac; the rule is shell/globalSearchKey.ts) opens the
   * rail's search exactly as its search circle does — pickNav('search') opens the rail
   * first, so a phone-width layout shows it too — and selects what is already typed, so
   * the next keys replace it. Capture phase for Cmd+J's reason: the terminal has focus
   * most of the time and xterm would otherwise take the key. The Mac app is this same
   * page in a WKWebView, and no item in its menus claims this key, so it arrives here.
   */
  const openGlobalSearchRef = useRef(() => {})
  openGlobalSearchRef.current = () => {
    pickNav('search')
    // After pickNav's own focus, in the frame the search row exists.
    requestAnimationFrame(() => searchEl.current?.select())
  }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isGlobalSearchKey(e)) return
      e.preventDefault()
      e.stopPropagation()
      openGlobalSearchRef.current()
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [])

  /**
   * Connection state is ANNOUNCED, not parked. The strip overlaps the terminal's bottom
   * rows, so anything permanent there is covering live output — which is why the original
   * fades every message. State you need at a glance lives in the sidebar instead.
   */
  useEffect(() => {
    if (health.isPending) return
    /*
     * The vault is mentioned ONLY where one exists — owner, 2026-08-08: 底部
     * terminal notification 還是時常跑出來 vault locked 的信息 … 需要修掉這個
     * legacy bug. On this deployment no vault is ever configured, yet every
     * health refresh re-printed "vault locked" into the status line (and flashed
     * it before /security/status had even answered, when `unlocked` is still
     * false-by-default). configured:false means the word has nothing to refer
     * to; a vault that exists still reports its lock state.
     */
    // And nothing before the answer: while /security/status is still in flight,
    // claiming "locked" is a guess printed as a fact — the flash the owner kept
    // seeing. No data, no segment.
    const vaultNote = !vault.data
      ? ''
      : vault.data.configured === false
        ? ''
        : unlocked
          ? ' · vault unlocked'
          : ' · vault locked'
    status(
      health.isError
        ? 'backend unreachable'
        : `${health.data?.version ?? '?'} · :${health.data?.port ?? '?'}${vaultNote}`,
    )
  }, [
    health.isPending,
    health.isError,
    health.data?.version,
    health.data?.port,
    unlocked,
    vault.data,
    status,
  ])

  // Keyed on WHICH agent, not on its object: a render must never be what shows this again (see terminalAgent above).
  const linkFor = agent ? `${agent.hash}|${agent.device_id ?? ''}` : null
  useEffect(() => {
    if (!linkFor || !link) return
    if (link !== 'attached') status(`terminal ${link}…`)
  }, [linkFor, link, status])

  /** Re-derive the pending @mention from the text and caret after any edit. */
  const syncMention = (el: HTMLTextAreaElement) => {
    setMention(findMention(el.value, el.selectionStart ?? el.value.length))
  }

  /** Replace the `@query` under the caret with the agent's ping tag. */
  const insertMention = (a: MentionAgent) => {
    const el = composerEl.current
    if (!el || !mention) return
    /*
     * A FRIEND'S AGENT (phase 2): link it with the agent open here first — the tag then carries the
     * link's address, which only this agent can use. Put where the `@query` still is when the
     * cloud answers (the box may have moved on), else at the end.
     */
    if (a.shareId) {
      const at = mention
      setMention(null)
      if (!agent?.hash || !agent.device_id) {
        status('Open one of your own agents to talk to a friend’s.')
        return
      }
      void linkSharedAgent(a.shareId, { hash: agent.hash, device_id: agent.device_id })
        .then((r) => {
          const box = composerEl.current
          if (!box) return
          const tag = `<ping-agent:${r.address}:${r.label}${r.owner ? ` (${r.owner}’s)` : ''}> `
          const word = `@${at.query}`
          const v = box.value
          setComposerValue(
            v.slice(at.start, at.start + word.length) === word
              ? `${v.slice(0, at.start)}${tag}${v.slice(at.start + word.length)}`
              : `${v}${v && !/\s$/.test(v) ? ' ' : ''}${tag}`,
          )
        })
        .catch((e) => status(shareErrText(e)))
      return
    }
    // In a friend's agent's composer: remember which of the reader's agents this tag names.
    if (shared && a.hash && a.device_id) sharedPings.current.set(a.hash, a.device_id)
    const before = el.value.slice(0, mention.start)
    const after = el.value.slice(mention.start + 1 + mention.query.length)
    const tag = pingTag(a)
    const next = `${before}${tag}${after}`
    setComposerValue(next)
    setMention(null)
    // Put the caret after the inserted tag so typing continues naturally.
    const caret = before.length + tag.length
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(caret, caret)
    })
  }

  /**
   * Footer buttons must NOT steal focus from the composer: a blur drops the soft
   * keyboard and jumps the layout. Every one preventDefaults its mousedown.
   */
  const keepFocus = (e: React.MouseEvent) => e.preventDefault()

  /**
   * The ⌨ button — shows/hides the extra-keys row, and NOTHING else.
   *
   * Owner, 2026-08-14: 小鍵盤不要與虛擬鍵盤的彈出掛勾 — the row no longer drags the
   * phone's soft keyboard up or down with it. The keyboard follows focus of the text
   * input ALONE (tap the composer to raise it; the ⌨ swipe-down and the pull-down
   * handle still put it away), so the extra keys can sit over the terminal WITHOUT the
   * keyboard, or beside it. The two are independent now, which is why keysBtn keepFocus’s
   * its mousedown like every sibling: tapping it for a Tab must not blur a live keyboard.
   */
  const toggleKeys = () => {
    const next = !keysOpen
    setKeysOpen(next)
    // Closing the row by hand also releases the pin, so a dismissed row does not spring
    // back on the next load.
    if (!next && keysLocked) {
      setKeysLocked(false)
      try {
        localStorage.setItem('wt_keys_locked', '0')
      } catch {
        /* preference just won't persist */
      }
    }
  }

  const agentList = agentsQ.data?.agents ?? []

  /*
   * The @ list belongs to the ROOM when a chat owns the composer (owner, 2026-08-27:
   * 朋友也可以使用 @ 來 ping owner 的 agent). A friend has no account roster for
   * someone else's agents, so their picker was empty in a room full of them — and my
   * own account roster lists agents that are not in this room at all. Project open →
   * the conversation's attached agents (mine enriched from my roster by hash); no
   * project → my roster, exactly as before. Same query key as ProjectPanel's members
   * sheet, so the two share one fetch.
   */
  const roomMembersQ = useQuery({
    queryKey: ['proj-members', panelProject?.project_id ?? ''],
    queryFn: () => getProjectMembers(panelProject!.project_id),
    enabled: !!panelProject,
  })
  /*
   * A PANE'S ROSTER IS ONE MACHINE (agentsQ's `paneScope`) — but the `@` picker and
   * the note sheet's attach list mean the whole account, and both read agentList, so
   * inside a split every agent on another machine was absent from them (owner,
   * 2026-09-10: 寬螢幕雙開 Agent 的時候 @ 選單只會出現部分的 Agent). The fleet is
   * asked only while one of those two is open — the pane's cold-load latency win
   * stays — and merged under this machine's rows so nothing waits on the sweep.
   * Same key as the shell's full sweep, so a pane opened from a shell that already
   * asked is served from the cache.
   */
  const wantFleet = paneFrame && (mention !== null || noteOpen)
  const fleetRosterQ = useQuery({
    queryKey: ['agents', 'all'],
    queryFn: () => getAgentsEverywhere(null),
    enabled: wantFleet,
    staleTime: 30_000,
  })
  const rosterList = paneFrame ? mergeRoster(fleetRosterQ.data?.agents, agentList) : agentList
  /*
   * FRIENDS' AGENTS, in your own agent's @ — owner, 2026-10-17: 也可以在自己的 Agent session 裡面使用 `@`
   * 來 ping 好友分享出來的 Agent. 讓他們對話. Picking one links it with the agent open here (insertMention).
   * Not in a friend's agent's own @: there the picker is the reader's own agents, and only theirs.
   */
  const sharedMentionRows: MentionAgent[] =
    agent && !sharedId
      ? (agentSharesQ.data?.incoming ?? [])
          .filter((x) => x.status === 'accepted')
          .map(
            (x) =>
              ({
                hash: x.agent.hash || x.share_id.slice(0, 12),
                session: x.agent.label || x.agent.session,
                host: null,
                ownerName: x.from.name || x.from.email || null,
                shareId: x.share_id,
              }) as MentionAgent,
          )
      : []
  const mentionAgents: MentionAgent[] = panelProject
    ? mentionRowsOfMembers(roomMembersQ.data?.members ?? [], rosterList)
    : [...rosterList, ...sharedMentionRows]

  /**
   * WHO A `ping-agent:` TAG IS ABOUT, and where a tap on it goes.
   *
   * Owner, 2026-09-21: the tag must read as the agent's name at its machine —
   * or at its OWNER when it is a friend's — in Clean View, Project Chat and
   * Friend Chat, and tapping your own agent opens that agent's session.
   *
   * Built here because both halves live here: `rosterList` is what makes an
   * agent MINE (and names its machine), the room's members are what put a
   * friend's name on one that is not, and `select` is the jump. Handed down as
   * a context for OpenDocContext's reason — the chips are rendered by the
   * markdown extension several levels below two different surfaces.
   */
  const jumpRef = useRef<(a: { session: string; host: string | null }) => void>(() => {})
  jumpRef.current = ({ session, host }) => {
    setRosterOpen(false)
    pushDetail()
    select({ kind: 'agent', session, host })
  }
  const pingCtx = useMemo<PingContextValue>(
    () => ({
      resolve: (hash, label) =>
        resolvePing(hash, label, {
          roster: rosterList,
          members: roomMembersQ.data?.members ?? [],
          localName: config.data?.name ?? config.data?.hostname ?? null,
        }),
      // The same move the busy-chip jump makes, for the same reason: the agent's
      // conversation replaces whatever is on the stage, and on a phone the rail
      // slides away so the conversation is what you see.
      //
      // READ AT CALL TIME, through a ref (the useBlobUrl pattern): `select` is
      // rebuilt every render, so naming it as a dependency would hand every
      // consumer of this context a new value on every render of App — for a
      // callback whose behaviour never changes. The ref is written below on each
      // render, so a tap always runs the current one.
      open: (a) => jumpRef.current(a),
    }),
    [rosterList, roomMembersQ.data, config.data],
  )

  /*
   * Has the agent you are typing TO read the guide?
   *
   * The SENDER is the question: `@` here composes something this agent will have to act
   * on. Whether the target has read anything is not this gate's business — gating on
   * that would make one unread agent unmentionable from every composer in the app.
   */
  const guideUnread =
    !!agent && mentionNeedsGuide(agentList.find((a) => a.session === agent.session))

  /*
   * Agreeing ASKS. It does not flip the flag — the agent still has to run the command,
   * and the flag is written by the CLI when the guide prints. So the picker stays shut
   * after a successful send, and re-opens on the next poll that reports it read. A
   * client that closed the gate on a 200 would hand back a picker for an agent that
   * still cannot use it.
   */
  /*
   * WHILE THE GUIDE DIALOG IS UP, re-ask when the agent's pane says something.
   *
   * `read_guide` flips when the AGENT runs the command, and nothing pushes that to this
   * client — so the dialog kept reappearing for an agent that had already read it until
   * the page was reloaded (owner, 2026-08-07: 已經讀完了 […] 直到我重新整理頁面才放行).
   *
   * My first fix was a `refetchInterval` while the dialog was open, and the repo's
   * no-heartbeat guard caught it. It was right to: a timer asking the same question
   * again is exactly the thing that was ruled out, and "only while a modal is open" is
   * the sort of qualifier every heartbeat starts with.
   *
   * The event was already there. Running `avengents readme` PRINTS THE GUIDE into the
   * agent's pane, so its pty output is the signal — the same debounce-on-a-real-write
   * shape useIdleStatus uses. No timer repeats; the settle timer fires once per burst.
   */
  /*
   * ASK FRESH THE MOMENT AN `@` OPENS — however it was opened.
   *
   * `read_guide` only changes on the edge, and nothing pushes it here, so a roster
   * fetched once keeps showing the dialog for an agent that has already complied until
   * the page is reloaded (owner, twice).
   *
   * My first attempt put this on the `@` BUTTON's onClick, which covered one of the two
   * ways in — typing `@` goes through `syncMention` and never touched it, and typing is
   * what the owner does. Same mistake as gating the picker at its entry points instead
   * of its render, in the same file, an hour apart: `mention` is the choke point, so the
   * refresh belongs on `mention`.
   */
  const mentionOpen = !!mention
  /*
   * Depends on mentionOpen, NOT on `mention`. `findMention` returns a fresh object on
   * every keystroke, so keying on the object refetches the whole roster per character —
   * a request storm while someone types a name, from a line whose comment says it fires
   * "the moment an @ opens".
   */
  /*
   * THE ANSWER IS DRAWN FROM WHAT WE ALREADY KNOW; THE ASK ONLY CORRECTS IT.
   *
   *   owner, 2026-08-29: 點擊 '@' 之後彈出 README 提示的 Latency 太高. 基本上這應該是
   *   瞬間可以發生的事情. 但是我卻要等個好幾秒.
   *
   * Measured on dev before changing anything (scripts/e2e-guide-prompt.mjs's ancestor):
   * `@` → the sheet on screen took 1189ms, 5825ms and 0ms on three runs of the same
   * journey. The spread IS the bug. This effect used to hold `guideChecked` false until
   * `invalidateQueries` SETTLED, and that promise waits for the roster refetch — which
   * is `getAgentsEverywhere()`, a fan-out to every paired machine, three at a time. So
   * a question about ONE agent was paying for an answer about the whole fleet, and what
   * the person waited for was whichever machine was slowest that second.
   *
   * Now the sheet renders from the cached roster immediately and this refetch only
   * CORRECTS it, off the critical path. The sweep still costs what it costs — what
   * changed is that nobody watches a blank composer while it runs.
   *
   * The stale direction that mattered is still handled, by two things rather than by
   * waiting: this refetch (which closes the sheet for an agent that has since read),
   * and the pty-settle effect below (which closes it when the agent reads it while the
   * sheet is open — the case the owner reported twice). Both now happen UNDER an open
   * sheet instead of before it, so the worst outcome is a sheet that closes itself
   * rather than a picker that never opens.
   *
   * `guideChecked` is gone: it existed only to hide the sheet during that wait.
   */
  useEffect(() => {
    if (!mentionOpen) return
    void qc.refetchQueries({ queryKey: ['agents'], type: 'active' })
  }, [mentionOpen, qc])

  /*
   * ASK ON ARRIVAL — owner, 2026-08-29: 新建 Agent 以及 Fork Agent 在使用者進入 Agent
   * session 的畫面就直接彈出來 README 的提示 […] 只要 Agent 的 readme hash 落後, 那麼
   * 操作如 1.
   *
   * Deliberately NOT hooked to create and fork. Those are two of the ways a person
   * arrives at a session and there are others — a link, the rail, a restored URL — and
   * the last time a guide check was wired to entry points instead of to the state
   * itself, typing `@` (the way the owner actually opens it) went ungated for an hour.
   * The state is the choke point: whichever way you got here, this is the agent on
   * screen and this is what it has read. A fresh or forked agent has read nothing, so
   * covering the state covers the owner's two cases by construction.
   *
   * The hash rule needs nothing extra either: `read_guide` is false for BOTH "never
   * read" and "read an older guide" (the edge compares hashes, guideRead.ts), so an
   * agent whose guide moved under it arrives here exactly as a new one does.
   *
   * Keyed on `hash`, not on the row: the roster object is rebuilt by every refetch, so
   * depending on it would reopen a sheet the person had just ignored, every few seconds.
   */
  const arrivedHash = agent?.hash ?? null
  useEffect(() => {
    if (!arrivedHash) {
      setGuidePromptFor(null)
      return
    }
    setGuidePromptFor(shouldPromptGuide(agent) ? agent?.session ?? null : null)
    // `agent` is read, not depended on — see the note above about refetch identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivedHash])

  /*
   * A GUIDE READ IS AN EVENT, AND THE EDGE NOW SAYS SO — owner, 2026-08-30:
   * 新 agent 閱讀完之後, 重進 session 又要他再讀一次. `avengents readme` records
   * its read in a state file the edge only read lazily; nothing told this
   * client, so the cached roster row kept read_guide:false and re-entering
   * judged the arrival prompt on it. The edge watches that file and emits
   * `guide_read` (guideReadFeed.ts); this re-reads the ONE machine that spoke
   * and merges — same hint-vs-verdict split as every other lane.
   */
  useEffect(() => {
    return onEdgeEvent((event, data) => {
      if (event !== 'guide_read') return
      const device = typeof data['device_id'] === 'string' ? (data['device_id'] as string) : null
      if (!device) return
      void getAgentsEverywhere(device, {
        onMachine: (m) => {
          qc.setQueryData<Everywhere>(['agents'], (prev) => mergeEverywhere(prev, m))
        },
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
   * …and an OPEN arrival prompt closes itself the moment the row says read.
   * Without this the freshly-merged truth waited for the next arrival to be
   * consulted — the sheet the person was looking at stayed up.
   */
  useEffect(() => {
    if (!guidePromptFor) return
    const row = agentList.find((a) => a.session === guidePromptFor)
    if (row && row.read_guide !== false) setGuidePromptFor(null)
  }, [agentList, guidePromptFor])


  useEffect(() => {
    if (!(mention && guideUnread)) return
    let settle: ReturnType<typeof setTimeout> | null = null
    const off = onPty((frames) => {
      // `act` included for the same reason as useIdleStatus: a quiet stream
      // reports the fact of output instead of its bytes.
      if (!frames.some((f) => f.op === 'out' || f.op === 'act')) return
      if (settle) clearTimeout(settle)
      settle = setTimeout(() => {
        settle = null
        void qc.invalidateQueries({ queryKey: ['agents'] })
      }, 600)
    })
    return () => {
      off()
      if (settle) clearTimeout(settle)
    }
  }, [mention, guideUnread, qc])

  const askGuide = useMutation({
    mutationFn: () => askAgentReadGuide(agent?.session ?? '', agent?.host ?? null),
    onSuccess: () => {
      setMention(null)
      /*
       * Closed WITHOUT being remembered as ignored. Agreeing is the opposite answer to
       * ignoring, and recording it as one would silence the prompt for a guide the
       * agent may never actually get around to reading — the ask can sit unread behind
       * the agent's own permission prompt, which this sheet says out loud.
       */
      setGuidePromptFor(null)
      void qc.invalidateQueries({ queryKey: ['agents'] })
    },
    /*
     * The dialog closes on success and STAYS OPEN with the reason on failure. It had
     * neither, and the first failure was an endpoint that had never been deployed: the
     * POST 404'd and the button did nothing observable. That is the worst case for this
     * particular control, because doing nothing observable is also what success looks
     * like — the picker is meant to stay shut either way.
     */
  })

  /**
   * "Automatically approve next time" — owner, 2026-09-21: 都需要 Approve 這個動作。
   * 有點麻煩…這樣子的話就不需要讓使用者每次都手動去按這個按鍵了.
   *
   * With the box ticked the arrival sheet never opens: the ask goes out and the
   * answer is recorded against THIS guide for THIS agent, which is precisely what
   * pressing Agree did by hand.
   *
   * RECORDED BEFORE IT IS SENT, and that ordering is the whole safety of this. The
   * agent has to actually run `avengents readme`, which takes as long as it takes —
   * so `read_guide` stays false meanwhile, this effect's own condition stays true,
   * and without the record it would re-ask on every roster change for as long as the
   * agent took to get to it. `ignoreGuide` is what makes it fire once per guide; it
   * is the same store the manual Agree path writes, so the two cannot disagree about
   * whether this agent has been answered.
   */
  useEffect(() => {
    if (!guidePromptFor || !agent || guidePromptFor !== agent.session) return
    if (!guideAutoApprove()) return
    ignoreGuide(agent.session, agent.host, agent.guide_hash)
    setGuidePromptFor(null)
    askGuide.mutate()
  }, [agent, guidePromptFor, askGuide])
  // The d-pad must not fight the mention picker for the same gesture.
  // AND IT IS THE TERMINAL VIEW'S ALONE (owner, 2026-10-07: 當使用者開啟 Clean View 的時候,
  // 需要取消手勢滑動輸入方向鍵的功能). In Clean View the terminal is not on screen, so a
  // swipe sent an arrow key to a prompt nobody was looking at — useDpad.ts has what one
  // stray Left did.
  const dpad = useDpad(termSock, narrow && !!agent && !inChat && !mention && !cleanViewOpen)

  /**
   * Panels that sit ABOVE #term swallow its gesture, so the rail could be opened from the
   * terminal but not from a conversation — and closing it would be impossible while one
   * was open. Same axis-locked horizontal drag, listening via bubbling on a wrapper
   * around ALL the overlay views (transcript included — the first version missed it).
   */
  const stageRef = useRef<HTMLDivElement>(null)
  useThumbDrag(stageRef, !!project || !!dmPeer || !!shared || (!!agent && viewMode !== 'terminal-view'))

  const [themeLight, setThemeLight] = useState(
    () => document.documentElement.classList.contains('light'),
  )

  /* ── notes ───────────────────────────────────────────────────────────────── */
  // noteOpen is declared with the overlay open-states up top (conversationOnScreen +
  // the project mark-read effect both need it in scope there).
  /**
   * Sessions judged gone. Recomputed from the roster poll the app already runs —
   * see notes/ghosts.ts for the three rules that keep this from flagging a live
   * queue during an agent restart.
   */
  const [ghosts, setGhosts] = useState<Set<string>>(new Set())
  /** Touch origin for the edge handle's own pull. */
  const handleX = useRef(0)
  const bmHandleX = useRef(0)
  const wbHandleX = useRef(0)
  const pgHandleX = useRef(0)
  const sbGuard = useRef<HTMLDivElement>(null)
  const [noteDraft, setNoteDraft] = useState('')
  /**
   * Dictation has TWO destinations now, and which one is live is decided by
   * whether the note panel is open. Read through a ref because the mic's
   * callbacks are bound once, outside React's render cycle — reading the state
   * directly would route by whatever was true when recording started.
   */
  const noteOpenRef = useRef(noteOpen)
  noteOpenRef.current = noteOpen

  /*
   * The three ways the selected conversation can be off screen. Anything ADDED here
   * that covers the stage belongs in this list — the decision itself and why it
   * exists are in agents/markRead.ts conversationOnScreen.
   *
   * `visibilityState` is read at render rather than kept in state: the idle poll
   * stops while the tab is hidden and refetches on the way back, so a render always
   * follows the return to the foreground and the ref is current by the time the
   * next beat reads it.
   *
   * THE NATIVE SHELL CONTRIBUTES NOTHING TO THIS LIST TODAY, audited by
   * avengents-app-builder (96ab075) rather than assumed — the failure mode here is
   * silent, so "we would probably notice" was not good enough:
   *   - the QR scanner is a full-screen native camera, but it lives on the connect
   *     screen, before the app has loaded and while there is no stage to cover.
   *     Moving it inside the app would put it in this list.
   *   - the Face ID sheet covers everything, but only fires while the vault is
   *     locked, when the terminal is not visible anyway.
   * Their standing commitment: if that count stops being zero it is their change,
   * so telling us is their job. One assumption underneath it is UNVERIFIED and
   * currently harmless — whether the system biometric sheet flips
   * `visibilityState`. Anything that wants Face ID while UNLOCKED has to check that
   * before relying on it.
   */
  onScreen.current = conversationOnScreen({
    documentVisible:
      typeof document === 'undefined' || document.visibilityState === 'visible',
    // On a phone the rail is not a drawer beside the stage, it IS the screen.
    railCovers: phoneNav && sbOpen,
    /*
     * ACTIVITY LEFT THIS LIST when it stopped being a sheet (2026-09-20), and
     * that is a behaviour change, not a tidy-up. As an overlay it covered the
     * conversation, so the conversation was NOT on screen and must not be marked
     * read. As a body of the rail it covers only the rail: on a phone `railCovers`
     * above already says so, and on a wide screen the conversation is genuinely
     * visible beside it — so reading Activity there now clears the badge, which is
     * what a person who can see the messages would expect.
     */
    overlayOpen:
      noteOpen ||
      filesOpen ||
      // The session's whiteboard or pages cover it, on every screen; so does a room's whiteboard or pages drawer.
      wbOpen ||
      pgOpen ||
      roomDrawerOpen ||
      serverOpen ||
      drawerOpen ||
      // The camera. app-builder's audit said in as many words that moving the QR
      // scanner inside the app would put it in this list; it is inside the app now.
      scanOpen ||
      !!viewing ||
      /*
       * BOTH OF THESE WERE MISSING, and they are mine — added 2026-09-20 without
       * being added here, which is precisely what the note above this block tells
       * whoever adds a stage-covering surface to do.
       *
       * The artifact viewer is a full page over the stage (it even takes railOpen /
       * onToggleRail, so it owns the screen), and the agent card is a sheet. While
       * either is up the conversation is invisible, yet onScreen still answered
       * true — so anything arriving in the open session was marked read without
       * being seen. That is the failure this file's own header calls the one nobody
       * reports: a badge that clears too eagerly leaves nothing to notice.
       *
       * Opposite direction from the agy bug above, same list, one mechanism.
       */
      !!viewArtifact ||
      // More's page on the stage (2026-10-22): the same full page over the conversation.
      !!morePage ||
      !!cardAgent,
  })
  // …which the corner of the session reads too: no picture of a tab for a conversation nobody sees.
  const stageOnScreen = onScreen.current

  /*
   * Ghost tracking rides the roster poll rather than adding one. A session that
   * dies through the UI is detached by the server; one that dies any other way —
   * manual tmux, a reboot — is only visible from here, because this is the only
   * side that knows what is currently alive.
   */
  const notesQ = useQuery({
    queryKey: ['notes', agent?.host ?? null, agent?.session ?? null],
    queryFn: () => getNotes(agent!.session, agent!.host),
    enabled: !!agent,
    retry: false,
  })
  /** Pending count for THIS agent — what the desktop badge and the edge hint show. */
  const notePending = useMemo(() => {
    const rows = notesQ.data?.notes ?? []
    if (!agent) return 0
    return rows.filter((n) => {
      if (n.archived) return false
      const r = (n.receivers ?? []).find(
        (x) => x.session === agent.session && (x.host ?? null) === (agent.host ?? null),
      )
      return r?.state === 'pending' && !r.detached_ts
    }).length
  }, [notesQ.data, agent])

  /*
   * Unlocking the vault has to invalidate the note queries.
   *
   * A cold load runs them while the vault is still locked, so they resolve to a
   * 401 and react-query holds that error until the next scheduled refetch —
   * meaning the panel says "vault locked" for up to half a minute AFTER you have
   * unlocked. The lock state is the query's real dependency; the poll interval
   * was standing in for it.
   */
  useEffect(() => {
    if (unlocked) void qc.invalidateQueries({ queryKey: ['notes'] })
  }, [unlocked, qc])

  /**
   * A NOTIFICATION TAP OPENS THE CONVERSATION, not the list behind it.
   *
   * Owner, 2026-09-18: tapping a notification opened the app and showed the
   * Agents list. The selection was right and the URL was right; the rail was
   * still covering it, because on a phone the rail IS the screen and only
   * `pushDetail()` moves it aside. Every in-app opener goes through that; the
   * push path wrote the URL directly and went around it.
   *
   * So the tap now lands on the same opener a rail tap does. The Mac's
   * `avengents:push-open` shares this callback, so it is fixed by the same line.
   */
  useEffect(
    () =>
      registerPushOpener((path: string) => {
        const next = parse(path.includes('?') ? path.slice(path.indexOf('?')) : '')
        window.history.replaceState(null, '', path)
        pushDetail()
        if (next.sel) select(next.sel)
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  /**
   * A COLD START THAT ALREADY NAMES A CONVERSATION MUST NOT OPEN THE RAIL OVER IT.
   *
   * `sbOpen` starts true — right when there is nothing to show, wrong when the
   * URL already names something, which is exactly the state a notification tap
   * produces when it LAUNCHES the app rather than resuming it. The effect below
   * only ever opens, so nothing corrected it.
   *
   * Once, on mount: later the person is in charge of the rail, and an effect
   * that kept closing it whenever something was selected would fight them.
   */
  useEffect(() => {
    // Refs and the URL only — nothing here is a reactive dependency, which is why
    // this needs no disable directive and the empty list is honest.
    if (phoneNavRef.current && parse(window.location.search).sel) setSbOpen(false)
  }, [])

  /**
   * On a phone with nothing selected there is no conversation to push, so the
   * rail is the screen. Without this a cold load — or backing out of the last
   * selection — leaves an empty stage the user cannot act on, with the way back
   * hidden behind a gesture they have no reason to try.
   *
   * `!sel`, NOT `!agent && !project && !dmPeer` — and the difference is a race
   * that reached production.
   *
   * avg-devapp-appstore, 2026-09-19, measured on a cold launch in the simulator:
   * the page took the `avengents:push-open` path every time, and in 2 of 6 runs
   * the agent still never appeared. Their guess was that the lookup drops an agent
   * whose machine's roster has not arrived. It does not — it RESOLVES, a beat
   * later. What went wrong is here.
   *
   * `sel` is the URL's selection and is set immediately; `agent` is that selection
   * RESOLVED against a roster that arrives over the network. Between those two
   * moments the old condition was true, so this effect opened the rail over a
   * conversation that was about to appear — and when it did appear, nothing closed
   * the rail again, because the only effect that closes it runs once on mount.
   * Whether it happened at all depended on which won the race, which is exactly
   * why it was 2 of 6 and not 6 of 6.
   *
   * It is the same shape as the notification-tap bug this rail already caused once
   * (pushNav.ts): the conversation WAS selected, underneath a rail still covering
   * it. So this also hits a COLD NOTIFICATION TAP, which is live on prod.
   *
   * A selection that resolves to nothing is not this effect's business either: the
   * stage has its own "that session isn't here any more" screen with a way out,
   * and showing the rail instead would replace a specific answer with a shrug.
   */
  useEffect(() => {
    if (phoneNav && !sel) setSbOpen(true)
  }, [phoneNav, sel])

  /**
   * THE RAIL IS THE SCREEN, SO PUT THE KEYBOARD AWAY.
   *
   * Owner, 2026-09-19: open a keyboard in an agent session — any keyboard,
   * English, Chinese or the Avengents one — then swipe left back to the side
   * panel, and it stays up. 主選單裡面沒有任何可以輸入文字的地方, so it is covering a
   * list with nothing to type into and he has to dismiss it by hand.
   *
   * iOS keeps a keyboard up for as long as something is focused, and nothing here
   * was telling it otherwise: the composer keeps focus while the rail slides over
   * it, because the composer is still mounted — it is merely hidden.
   *
   * AN EFFECT ON THE CONDITION, NOT A BLUR AT EACH CALL SITE. The rail opens from
   * a left swipe, the bar button, onToggleRail, the cold-start branch above, and
   * whatever is added next; patching those one at a time is how the fifth one
   * ships without it. This fires on the STATE that means "the rail is covering
   * everything", however it was reached.
   *
   * PHONE ONLY, and that is the whole reason `phoneNav` is in the condition rather
   * than just `sbOpen`. On a wide screen the rail is docked BESIDE the stage, the
   * composer stays visible and usable, and blurring it there would steal the caret
   * from someone mid-sentence who just glanced at their agent list.
   */
  useEffect(() => {
    if (!(phoneNav && sbOpen)) return
    // Guarded rather than unconditional: blur() on a non-input (the body, a div)
    // is a no-op, but calling it on whatever happens to be focused is a wider
    // promise than this effect wants to make.
    const el = document.activeElement as HTMLElement | null
    if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) {
      el.blur()
    }
  }, [phoneNav, sbOpen])

  /**
   * The left-edge guard's own drag.
   *
   * Listeners are attached natively and non-passively because React registers
   * touch events passively at the root, where preventDefault is ignored — and
   * preventDefault is the backstop if a browser honours it but not
   * touch-action. The strip drives the same sbDrag the terminal does, so the
   * gesture behaves exactly as before; the only change is WHO receives it.
   */
  useEffect(() => {
    const el = sbGuard.current
    if (!el) return
    const start = (e: TouchEvent) => {
      const t = e.touches[0]
      if (!t) return
      sbDrag.begin(t.clientX)
    }
    const move = (e: TouchEvent) => {
      const t = e.touches[0]
      if (!t || !sbDrag.isActive()) return
      sbDrag.move(t.clientX)
      e.preventDefault()
    }
    const end = () => {
      if (sbDrag.isActive()) sbDrag.end()
    }
    el.addEventListener('touchstart', start, { passive: false })
    el.addEventListener('touchmove', move, { passive: false })
    el.addEventListener('touchend', end)
    el.addEventListener('touchcancel', end)
    return () => {
      el.removeEventListener('touchstart', start)
      el.removeEventListener('touchmove', move)
      el.removeEventListener('touchend', end)
      el.removeEventListener('touchcancel', end)
    }
  }, [sbOpen])

  useEffect(() => {
    const live = trackLiveSessions(
      agentsQ.data?.agents ?? [],
      agentsQ.data?.terminals ?? [],
    )
    setGhosts(noteAbsences(notesQ.data?.notes ?? [], live))
  }, [agentsQ.data, notesQ.data])

  /*
   * Tell the edge when the user is mid-input, so a message is held rather than
   * injected into a half-written line.
   *
   * This half of the guard cannot live on the server: composer text never reaches the
   * pty until it is sent, and the microphone is a browser state. `needs_input` the
   * server detects for itself; this it has to be told.
   */
  useBusyHeartbeat({
    session: agent?.session ?? null,
    host: agent?.host ?? null,
    typedAt: typedAt.current,
  })


  /**
   * Put the theme on THIS document. Shared by the button and by the cross-document
   * listener below, so the two can never drift into applying different halves.
   *
   * Deliberately does NOT write storage or sync cursor agents: those belong to the
   * document where a PERSON pressed the button. A listener that wrote storage would
   * make every other pane write it back.
   */
  const applyTheme = (next: boolean) => {
    setThemeLight(next)
    document.documentElement.classList.toggle('light', next)
    /* Safari's toolbar, and an installed web app's chrome, follow this meta — it was
       pinned to the dark ground, so a light-themed app still had dark browser chrome.
       The iOS shell follows it as well (WKWebView.themeColor → ThemeGround.swift) for
       the ground behind the page, which a glass keyboard shows through. The hexes are theme.css's `--c1e1e2e` in each block; the
       boot script in index.html carries the same pair because it must run before the
       stylesheet, and a test pins both against the palette. */
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', next ? '#eff1f5' : '#1e1e2e')
  }

  const flipTheme = () => {
    const next = !themeLight
    applyTheme(next)
    try {
      /*
       * The write is ALSO how the other panes hear about it — owner, 2026-09-15:
       * 雙開的時候, 更改 theme color 無法更改 Agent session 的 Theme color.
       *
       * A split pane is its own DOCUMENT, so a class toggled on this documentElement
       * reaches nothing but this one; the panes kept whatever palette they booted
       * with, which is why one side of his screenshot is light and the other dark.
       * `storage` already fires in every OTHER same-origin document on this line, so
       * the channel existed all along and nobody was listening. See the effect below.
       */
      localStorage.setItem('wt_theme', next ? 'light' : 'dark')
    } catch {
      // Non-fatal: theme preference just won't persist.
    }
    // xterm follows on its own (`onThemeChange` re-applies `xtermTheme()` in both
    // terminals) because it reads the CSS variables the `.light` class just swapped.
    // Cursor agents do not: they only re-detect when told, so without this they keep
    // painting in whatever palette they saw at launch. Fire-and-forget — the theme has
    // already flipped locally, and an unreachable server must not undo that.
    void syncCursorTheme().catch(() => {})
  }

  /*
   * EVERY PANE FOLLOWS THE THEME, not just the one that was clicked — owner,
   * 2026-09-15: 雙開的時候, 更改 theme color 無法更改 Agent session 的 Theme color.
   *
   * Split panes are separate DOCUMENTS of the same origin (see `paneFrame`), so the
   * class flipTheme toggles lands on one documentElement and nowhere else. His
   * screenshot is the proof: one pane light, the other still dark, from the same
   * button.
   *
   * `storage` is the channel and it was already open — it fires in every same-origin
   * document EXCEPT the one that wrote, which is exactly the set that needs telling
   * and exactly the one that must not be told twice. Nothing new is invented here, and
   * no new message has to be kept in sync with the button.
   *
   * xterm needs no line of its own: onThemeChange watches documentElement's class with
   * a MutationObserver precisely "for every way the class can change", and this is now
   * one more of those ways.
   */
  useEffect(() => {
    const onStore = (e: StorageEvent) => {
      if (e.key !== 'wt_theme' || e.newValue === null) return
      applyTheme(e.newValue === 'light')
    }
    window.addEventListener('storage', onStore)
    return () => window.removeEventListener('storage', onStore)
    // Mounted once: `applyTheme` only calls setThemeLight and touches the DOM, so it
    // has nothing per-render to capture and the listener never needs replacing.
  }, [])

  /**
   * Cmd/Ctrl+Enter is a GLOBAL shortcut, not just a composer binding.
   *
   * After staging text you naturally look at — and often click — the terminal, which
   * moves focus into xterm. xterm swallows Cmd+Enter (Meta produces no data), so a
   * composer-only handler silently stopped working for the second press. Binding at the
   * document makes the two-stage flow independent of where focus happens to be.
   */
  /**
   * Drag a file anywhere in the app.
   *
   * Bound at the WINDOW rather than on a container, for two reasons. The layout's root
   * is a fragment, so there is no single element to hang it on; and the terminal is an
   * xterm canvas that owns its own DOM, so a React subtree handler would leave the
   * largest drop target on screen inert — which is the one place a file is most
   * obviously "for" the thing you are looking at.
   *
   * preventDefault on BOTH dragover and drop or the browser navigates away to display
   * the file, discarding whatever was in the composer.
   */
  const [dropping, setDropping] = useState(false)
  /*
   * panelProject, NOT project — a friend DM can take files too.
   *
   * Owner, 2026-09-19: 剛剛才發現 Project Chat 可以 attach 檔案, 但是 Friends Chat
   * 上面卻沒有這一個 Attach 檔案的按鈕.
   *
   * Nothing but the two gates was missing. The SEND path has always read
   * `panelProject` (planSend's inProject, and projectFileUpload's conversation id),
   * and its own comment says so: "Real projects AND the managed friend DM send
   * through the same rail — a friend DM is just a locked conversation now." The
   * chat service agrees and never asked: its attachment routes gate on
   * activeMember and read `kind` nowhere at all.
   *
   * So the machinery was complete and only the way in was hidden — which is also
   * why the FOLDER button (panelProject) already opened a DM's shared files while
   * the paperclip beside it did not. A room you can read files out of and cannot
   * put files into is the kind of half-feature that reads as a bug, and was one.
   */
  // A friend's agent takes files too — dropped or pasted as well as through its paperclip.
  const canAttach = !!panelProject || !!agent || sharedOpen
  useEffect(() => {
    // Nothing to attach to: with no conversation open a drop has no destination, and
    // a highlight promising otherwise is worse than the browser's default.
    if (!canAttach) return
    const hasFiles = (e: DragEvent) => !!e.dataTransfer?.types.includes('Files')
    /*
     * The file browser owns its drops, so this must not PROMISE them.
     *
     * The veil spans the viewport and says "Attach for <agent>". Over the drawer that
     * sentence was a lie about where the file was going — and then it came true, because
     * the window drop handler below staged it into the composer as well (owner,
     * 2026-09-14: 拖曳檔案上傳到 file browser 居然也會跑進去 Text edit 裡面). The drawer
     * now stops the drop from reaching here; this stops the message from getting there
     * first.
     *
     * Asked of the pointer's target rather than answered by the drawer calling
     * stopPropagation on `dragover`: stopping it would leave this handler's veil stuck
     * at whatever it was when the pointer crossed the edge, which is the stranded
     * overlay the capture-phase clearVeil below exists to undo.
     */
    const overFileBrowser = (e: DragEvent) =>
      e.target instanceof Element && !!e.target.closest('#fsDock')
    // A whiteboard takes its own drops too — a picture lands on the board (BoardCanvas), whether the board is the
    // session's drawer beside the composer or over the whole app; it never also goes to the agent.
    const overBoard = (e: DragEvent) => e.target instanceof Element && !!e.target.closest('.bd-screen')
    const over = (e: DragEvent) => {
      if (!hasFiles(e)) return
      e.preventDefault()
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
      if (overFileBrowser(e) || overBoard(e)) {
        setDropping(false)
        return
      }
      // NOT while the note is open: the note's own box draws a dashed outline, and a
      // second one spanning the whole viewport on top of it is the bug reported
      // (owner, 2026-08-14: 反白虛線只需要 Focus 在 Text Input 匡內就好). The note's
      // dragover fires here too, having stopPropagation'd — so this only sees the
      // events over the SHEET around the box, which must stay quiet.
      if (!noteOpen) setDropping(true)
    }
    // `relatedTarget` is null when the pointer leaves the window itself; leaving for
    // a child element fires this too, and clearing on those would flicker the overlay
    // for the whole time the pointer is moving across the page.
    const leave = (e: DragEvent) => {
      if (!e.relatedTarget) setDropping(false)
    }
    /*
     * Clearing the veil is CAPTURE-phase and separate from staging. A child that
     * owns its drop — the note's box, the project panel — calls stopPropagation on
     * the BUBBLING drop, which used to strand the overlay up forever (owner: 反白
     * 虛線會持續的存在). The capture phase runs before any child can stop the event,
     * so the veil always comes down the instant the drag ends.
     */
    const clearVeil = () => setDropping(false)
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return
      e.preventDefault()
      // The note owns its own drops (its box attaches them). Staging here would put
      // the file in the AGENT's tray instead — the wrong, silent destination the
      // note's stopPropagation already guards for drops that land ON the box.
      if (noteOpen) return
      // Belt and braces beside the drawer's own stopPropagation. If a drop over the
      // file browser ever reaches this line, the honest outcome is that it does not
      // also land in the composer: "nothing happened" is recoverable by dropping
      // again, while a file silently attached to a message is the reported bug.
      if (overFileBrowser(e) || overBoard(e)) return
      stage([...(e.dataTransfer?.files ?? [])])
    }
    window.addEventListener('dragover', over)
    window.addEventListener('dragleave', leave)
    window.addEventListener('drop', clearVeil, true)
    window.addEventListener('drop', drop)
    return () => {
      window.removeEventListener('dragover', over)
      window.removeEventListener('dragleave', leave)
      window.removeEventListener('drop', clearVeil, true)
      window.removeEventListener('drop', drop)
    }
  }, [canAttach, panelProject, agent, sharedOpen, noteOpen])

  /**
   * Cmd/Ctrl+J toggles the quick-shell drawer.
   *
   * Capture phase + preventDefault, as the original notes: it has to beat BOTH the
   * browser (Chrome binds Cmd+J to downloads) and xterm, which would otherwise swallow
   * it as terminal input.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && (e.key === 'j' || e.key === 'J')) {
        e.preventDefault()
        e.stopPropagation()
        setDrawerOpen((v) => !v)
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [])

  /**
   * Cmd/Ctrl+Shift+G toggles Source Control — VS Code's own shortcut, since the panel
   * is a copy of its SCM view. Capture phase for the same reason as Cmd+J: xterm has
   * focus most of the time and would otherwise eat it.
   *
   * `e.code` rather than `e.key`: with Shift held, `key` is 'G', and on some layouts
   * neither case matches reliably. The physical key is what the shortcut means.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey || e.altKey) return
      if (e.code !== 'KeyG') return
      e.preventDefault()
      e.stopPropagation()
      /*
       * Source Control lives INSIDE the file browser now, so the shortcut has to open
       * its container too. Without that it would flip a flag with nothing on screen to
       * show for it, which is indistinguishable from a shortcut that stopped working.
       * Turning it off leaves the drawer open on the listing.
       */
      const next = !gitOpen
      setGitOpen(next)
      if (next) setFilesOpen(true)
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
    // Re-registered on toggle: the handler reads gitOpen directly, because a
    // functional update cannot also decide whether to open the drawer.
  }, [gitOpen])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || !(e.metaKey || e.ctrlKey)) return
      // A key pressed in an open sheet is that sheet's — not a send for the agent on the
      // stage behind it (a bare Enter into its terminal, answering whatever it asked).
      if (inOpenSheet(e.target)) return
      // …nor one pressed on a whiteboard: the session's drawer sits beside the composer, not in a sheet.
      if (e.target instanceof Element && e.target.closest('.bd-screen')) return
      // Let a chat view's own composer handler deal with it when it has focus.
      e.preventDefault()
      submitFromControlRef.current()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  return (
    <>
      {wantsViewportProbe() && <ViewportProbe />}

      {/* The landing for `avengents pair`'s printed /devices?code=… link — a page
          that only ever existed on the :9443 site. See src/pair/PairApprove.tsx. */}
      <PairApprove />

      {/* Says where a dropped file will go, because that is the part the pointer
          cannot show: the same gesture over the same pixels means the project room in
          one view and this agent's machine in another. `pointer-events: none` in CSS —
          an overlay that intercepts the drop would swallow the event it exists to
          announce. */}
      {dropping && !noteOpen && (
        <div id="dropVeil" role="presentation">
          {/* A DM's `name` is the friend's own display name (useManagedFriendDm puts
              it there), so this reads "Share with Rachel" without a special case.
              The fallback is new: an untitled project used to make this say
              "Share with undefined", which nothing caught because the veil only
              appears mid-drag. */}
          <span>
            {panelProject
              ? `Share with ${panelProject.name ?? 'this chat'}`
              : `Attach for ${agent?.session ?? sharedName}`}
          </span>
        </div>
      )}

      {/* A PANE FRAME IS THE STAGE ALONE. The rail belongs to the shell around it, and
          drawing it inside every pane would be drawing it wrong — see shell/paneMode.ts
          for why a pane is the whole app rather than an extracted component. */}
      {!paneFrame && <div id="sbBackdrop" onClick={() => setSbOpen(false)} />}

      {!paneFrame && (
      <aside id="agentsSidebar" aria-label="Agents">
        {/* Machine name + the relocated header buttons. The name is a real <input> so
            a caret and the mobile keyboard work; it drives the browser tab title. */}
        <div id="sbHostRow">
          {/* The Avengents brand — owner, 2026-08-09: header bar 左邊換成
              Avengents 的 icon 並打上 "Avengents" 標題. It still opens the main
              menu (account, machines, pairing); the server's own name lives
              inside there as the menu title. */}
          <button
            id="sbName"
            type="button"
            title="Avengents — account, machines, pairing"
            aria-label="Avengents menu"
            aria-expanded={serverOpen}
            /*
             * OPEN, idempotently — not a toggle. As a toggle, a phone tap that
             * double-fires (ghost click at the same coordinates ~300ms later,
             * landing on this button because the host row stays put while the
             * menu swaps in below) opened the menu and closed it again in one
             * gesture — owner, 2026-08-09: 顯現一下就消失了. Closing belongs
             * to the ✕, Esc, and the nav tabs, none of which sit under the
             * opening finger.
             */
            onClick={() => setServerOpen(true)}
          >
            <AvengentsMark size={38} />
            <span className="sb-brand-word">Avengents</span>
          </button>
          {/* Concise — owner, 2026-11-04: 切換是否要預覽 Agent 最後一個訊息 … 可以看到更多的 agent 的數量. Its own
              component on a module store (sidebar/railConcise.ts), so a press draws the rail, not the app. */}
          <ConciseButton />
          <button
            type="button"
            title={themeLight ? 'Switch to dark' : 'Switch to light'}
            aria-label="Toggle theme"
            onClick={flipTheme}
          >
            {/* Sun/moon — owner, 2026-08-14 — showing the CURRENT theme; the title
                carries where a tap takes you. (auto_awesome ✨ stays the clean-view
                marker elsewhere.) */}
            <Icon name={themeLight ? 'light_mode' : 'dark_mode'} size={17} label="Toggle theme" />
          </button>
          {/*
            * The vault lock is gone.
            *
            *   owner, 2026-08-05: 「把 side panel 上面的 Vault Lock 移除. 用不到了.」
            *
            * It locked a vault this topology does not create — `/security/status` on the
            * owner's machine answers `configured: false, needs_setup: true` — so the
            * button was permanently disabled, and a control that can never be pressed is
            * furniture that looks like a feature. Google is the gate now, and the machine
            * lets a relayed request past on that basis.
            *
            * `unlocked` stays: other things read the vault's STATE (the notes query, the
            * status line). What is removed is the claim that anyone here can change it.
            */}
        </div>

        {/* The MAIN MENU swaps in for the list, not for the stage — its own
            layout in the rail (owner: 取代 side panel 下方的 Agent / project
            list). Everything behind the rail stays exactly where it was.

            MORE swaps in the same way, and for the same reason the owner gave
            again on 2026-09-20: 「直接讓整個面板的底板換掉…因為我們之後會在 More
            的面板裡面加上其他的功能,不只是 Artifact」. Three bodies, one slot —
            the nav pill below and the stage behind never move. */}
        {serverOpen ? (
          <ServerPanel onClose={() => setServerOpen(false)} />
        ) : navTab === 'more' ? (
          <MorePanel
            page={morePage}
            agents={agentsQ.data?.agents ?? []}
            projects={projectsQ.data?.projects ?? []}
            /* A button opens its part's list on the stage, never over an item of another
               part: an artifact left open would cover the list just asked for. */
            onOpenPage={(p) => {
              setViewArtifact(null)
              setMorePage(p)
            }}
          />
        ) : navTab === 'activity' ? (
          <ActivityPanel
            selfEmail={selfEmail}
            /* A DM row opens by peer — the SAME call the rail's friend rows make (it
               pushes the detail step itself), so both doors land on one conversation
               and neither invents its own history entry. */
            onOpenDm={openDm}
            onOpenProject={(id) => {
              const p = projectsQ.data?.projects.find((x) => x.project_id === id)
              // Open by id even when the list has not caught up: the row exists
              // because a message from it just arrived, so refusing to navigate
              // would be the app disbelieving its own feed.
              if (p) openProject(p)
              else {
                pushDetail()
                select({ kind: 'project', id })
              }
            }}
            onOpenAgent={(session, host) => {
              pushDetail()
              select({ kind: 'agent', session, host })
            }}
          />
        ) : (
          <Sidebar
            /*
             * Dragging is offered only where a drop can land: above the width gate, and
             * never inside a pane iframe (which renders no rail anyway). A row that
             * lifts and cannot land is worse than one that does not lift.
             */
            {...(canSplit(winW) && !paneFrame
              ? {
                  onDragSel: (s: Selection | null) => {
                    railDragSel.current = s
                    setRailDrag(!!s)
                    /*
                     * A drag that begins with nothing split has to make a pane out of
                     * what is already on screen, or there is nothing to drop BESIDE.
                     */
                    if (s && panes.length === 0 && url.sel) {
                      setPanes([{ id: 'p0', sel: url.sel }])
                      setActivePane('p0')
                    }
                  },
                }
              : {})}
            localName={config.data?.name || config.data?.hostname || null}
            filter={sbQuery}
            contentHits={contentHits}
            only={
              msgQuery ? (
                <MessageResults
                  query={msgQuery}
                  keywords={keywordsOf(msgQuery)}
                  pending={msgSearch.isPending}
                  error={msgSearch.error ? String((msgSearch.error as Error).message ?? msgSearch.error) : null}
                  report={msgSearch.data}
                  now={Date.now() / 1000}
                  onOpen={openMessage}
                  nameOf={(h) => agentsQ.data?.agents.find((a) => a.hash === h)?.session ?? null}
                />
              ) : undefined
            }
            scope={navTab === 'group' ? 'group' : 'home'}
            status={vault.data}
            openProjectId={project?.project_id ?? null}
            onOpenProject={openProject}
            openAgentHash={agent?.hash ?? null}
            // The device too: a same-named agent on a second machine shares this
            // hash, and the highlight must land on the row that is open, not both.
            openAgentDevice={agent?.device_id ?? null}
            onOpenAgent={openAgent}
            openDmPeer={dmPeer}
            onOpenDm={openDm}
            onOpenShared={openShared}
            openSharedId={sharedId}
            onOpenCard={setCardAgent}
            openGroupId={openGroupId}
            onGroupOpened={() => setOpenGroupId(null)}
            /* The machine section's Terminal button: a non-tmux shell ON that
               device, at home with the path shown, riding the Cmd+J drawer's
               tabs — one popup-terminal idiom, not two. */
            onOpenMachineShell={(device, name) => {
              // Dynamic import so shellTabs (and xterm behind it) is not in the
              // initial chunk — it lands when a shell is first opened (M9).
              void import('./terminal/shellTabs').then((m) =>
                m.openShellTab(null, null, { device, title: name, home: true }),
              )
              setDrawerOpen(true)
            }}
            /* The rail's friend rows and the Humans tab's badge read one list. */
            dmUnreadByEmail={dmUnreadByEmail(dmRows)}
            dmPreviewByEmail={dmPreviewByEmail(dmRows)}
          />
        )}

        <div id="sbFoot">
          <div id="sbCtl" />
        </div>
        <div
          id="sbResize"
          title="Drag to resize width"
          role="separator"
          aria-orientation="vertical"
          onPointerDown={resize.onPointerDown}
          onPointerMove={resize.onPointerMove}
          onPointerUp={resize.onPointerUp}
          onPointerCancel={resize.onPointerUp}
        />

        {/*
          The rail's bottom dock: the nav pill, the search field it reveals, and the
          ＋ floating clear of both at the bottom-right — the arrangement in the
          owner's screenshot. The ＋ is anchored to the TOP of this dock (bottom:
          100% in CSS), so revealing the search field lifts it without a second
          measurement to keep in sync.
        */}
        {/*
          SEARCH TAKES THE WHOLE BAR — owner, 2026-09-29, with Slack's search in a
          screenshot: 當使用者按下那個搜尋按鈕的時候 … 隱藏其他所有的按鈕, 擴大搜尋的框然後右邊
          增加一個取消的叉叉按鈕. The field stands where the pill stood and the ✕ where the
          search circle stood, at the same sizes, so opening search changes what the bar
          holds and not where anything is — the ✕ is under the thumb that opened it.
        */}
        <div id="sbDock">
          {!searchOpen && (
            <button
              type="button"
              id="sbNewFab"
              // It opens a menu now — a machine, an agent, a project or a human (owner,
              // 2026-10-17) — so it is named for all four, not for the agent alone.
              title="New"
              aria-label="New"
              onClick={() => setNewOpen(true)}
            >
              <Icon name="add" size={24} label="New" />
            </button>
          )}

          {searchOpen && (
            <>
            {/* Pull down to put the keyboard away, on the boundary between the field
                and the keyboard itself. Same affordance as the composer's, and here for
                the same reason: iOS keeps its only dismiss control on the accessory bar,
                and there is nowhere inert to tap in a rail that is all list. */}
            <div
              id="sbKbGrab"
              role="button"
              tabIndex={-1}
              aria-label="Pull down to hide the keyboard"
              onPointerDown={(e) => {
                sbGrabY.current = e.clientY
                try {
                  e.currentTarget.setPointerCapture(e.pointerId)
                } catch {
                  /* older engines: the drag ends at the handle's edge */
                }
              }}
              onPointerMove={(e) => {
                if (sbGrabY.current && e.clientY - sbGrabY.current > 24) {
                  sbGrabY.current = 0
                  ;(document.activeElement as HTMLElement | null)?.blur?.()
                }
              }}
              onPointerUp={() => {
                sbGrabY.current = 0
              }}
            >
              <span className="kg-bar" aria-hidden="true" />
            </div>

            {/* The way to the MESSAGES for anyone who does not know Enter does it — and
                the reminder that typing alone only filters the list below. */}
            {sbQuery.trim() && sbQuery.trim() !== msgQuery && (
              <button type="button" id="sbMsgSearchHint" onClick={() => setMsgQuery(sbQuery.trim())}>
                <Icon name="search" size={14} />
                <span>
                  Search messages on every machine for “{sbQuery.trim()}”
                </span>
              </button>
            )}
            <div id="sbSearchRow">
              <div id="sbSearchWrap">
                <Icon name="search" size={17} />
                <input
                  ref={searchEl}
                  id="sbSearchInput"
                  type="search"
                  inputMode="search"
                  enterKeyHint="search"
                  placeholder="Search"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  value={sbQuery}
                  onChange={(e) => setSbQuery(e.target.value)}
                  onKeyDown={(e) => {
                    // Escape is the keyboard's version of the ✕, for anyone on a laptop.
                    if (e.key === 'Escape') closeRailSearch()
                    // Enter (the phone keyboard's Search key) sends the query — the ↑ below.
                    if (e.key === 'Enter' && sbQuery.trim()) {
                      e.preventDefault()
                      sendRailSearch()
                    }
                  }}
                />
                {/* The ↑ sends what is typed (sendRailSearch). keepFocus holds the field
                    until the tap lands: a blur on press would put the phone keyboard
                    away and move this button out from under the finger first. */}
                <button
                  type="button"
                  id="sbSearchGo"
                  title="Search messages (Enter)"
                  aria-label="Search messages on every machine"
                  disabled={!sbQuery.trim()}
                  onMouseDown={keepFocus}
                  onClick={sendRailSearch}
                >
                  <Icon name="arrow_upward" size={18} />
                </button>
              </div>
              <button
                type="button"
                id="sbSearchClose"
                title="Close search (Esc)"
                aria-label="Close search"
                onClick={closeRailSearch}
              >
                <Icon name="close" size={20} label="Close search" />
              </button>
            </div>
            </>
          )}

          {!searchOpen && (
          <NavBar
            tab={navTab}
            onPick={pickNav}
            /*
             * ONE COUNT PER TAB, each counting exactly what that tab can show you.
             *
             *   home     the Agents tab's own lists — projects and agents
             *   group    the Humans tab — the friend DMs, PLUS the requests waiting
             *            for an answer. A request is the one thing on that tab you
             *            cannot find any other way: a DM you will eventually notice
             *            in the rail, but nothing else in the app mentions that
             *            someone added you (owner, 2026-09-22: 使用者B就很難知道自己
             *            被加好友了). It satisfies the rule rather than bending it —
             *            FriendsTab renders `incoming` at the top of that very tab,
             *            so the number still counts only what tapping it will show.
             *   activity the feed, which merges projects and agents and NOT DMs
             *
             * Activity therefore repeats home's number rather than being the grand
             * total, and that is deliberate: its badge must count what its list
             * holds. Adding DMs to the number without adding them to the feed would
             * send you to a panel that cannot show you what it just claimed.
             *
             * All of it is counted from the lists the app already polls, NOT from
             * the feed's own rows — the badges have to be right while every panel is
             * closed and their queries are not running. See activity/feed.ts.
             */
            unread={{
              home: activityUnread(projectsQ.data?.projects ?? [], [...idle.byKey.values()]),
              group: dmUnreadTotal(dmRows) + incomingFriendReqs,
              activity: activityUnread(projectsQ.data?.projects ?? [], [...idle.byKey.values()]),
            }}
          />
          )}
        </div>
      </aside>
      )}

      {/* THE SPLIT STAGE covers the stage area when there is more than one conversation
          open. The parent's own selection is cleared on the way in, so nothing is
          mounted behind it — a hidden pane is still polling, and that was the whole
          argument for closing them rather than hiding them (panes.ts, foldToOne). */}
      {split && (
        <PaneStrip
          panes={panes}
          activeId={activePane}
          dragging={railDrag}
          onActivate={setActivePane}
          onToggleRail={() => setSbOpen((v) => !v)}
          onView={(id, view) =>
            setPanes((ps) => ps.map((pane) => (pane.id === id ? { ...pane, view } : pane)))
          }
          dock={
            dockPane?.sel?.kind === 'agent' ? (
              <FileBrowser
                session={dockPane.sel.session}
                host={dockPane.sel.host}
                /* Explicit rather than passing the setter: a state setter also accepts
                   an updater FUNCTION, and handing one straight to a callback is a
                   shape that is easy to misread and impossible to observe. */
                onOpenFile={(f) => setViewing(f)}
                onStatus={status}
                onClose={() => setDockPaneId(null)}
                gitView={gitOpen}
                onGitView={setGitOpen}
              />
            ) : dockPane?.sel?.kind === 'project' ? (
              /*
               * A PROJECT'S FILES ARE A DIFFERENT MECHANISM — owner, 2026-08-23:
               * Project Chat 的 File Browser 無法打開 (機制與 Agent Session 不同).
               *
               * An agent's browser walks a MACHINE's filesystem over the relay; a
               * project's is the list of files shared into the conversation, collected
               * from its own messages. The dock rendered null for a project and the
               * button did nothing — the worst kind of working control.
               *
               * The history is the same query the panel uses, so opening the dock on a
               * project that is already on screen costs nothing new.
               */
              <ProjectDockFiles
                projectId={dockPane.sel.id}
                selfEmail={selfEmail}
                onOpenDoc={openDoc}
                onClose={() => setDockPaneId(null)}
              />
            ) : null
          }
          /*
           * TOGGLE THE DOCK, not the pane's own drawer — owner, 2026-08-22: 開啟的時候
           * 則是從右邊等比例壓縮所有的視窗. A browser drawn inside one pane cannot
           * compress the others.
           *
           * Pressing it on the pane it is already showing closes it; pressing it while
           * focused elsewhere MOVES it, which is what "隨著 focus 改變內容" means when
           * the control is one button.
           */
          onFiles={(id) => setDockPaneId((cur) => (cur === id ? null : id))}
          onClose={(id) => setPanes((ps) => closePane(ps, id))}
          onDropSel={(onPaneId, side) => {
            if (!railDragSel.current) return
            const id = `p${Date.now().toString(36)}`
            const sel = railDragSel.current
            setPanes((ps) => placePane(ps, sel, onPaneId, side, id, maxPanes(window.innerWidth)))
            /*
             * FOCUS FOLLOWS THE DROP. A rail tap now replaces the focused pane, so
             * leaving focus on the pane somebody just dragged AWAY from would send the
             * next tap into the wrong half of the screen.
             */
            setActivePane(id)
          }}
        />
      )}

      {!agent && !project && !dmPeer && !shared && (
      <div id="term">
        <div className="stage">
          {sharedId && (
            <>
              <h1 className="stage-h">{agentSharesQ.data ? 'This agent is no longer shared with you' : 'Opening…'}</h1>
              {agentSharesQ.data && (
                <>
                  <p className="stage-p">It was taken back, the share ran out, or the agent was ended.</p>
                  <button type="button" className="stage-btn" onClick={closeSelection}>
                    Clear
                  </button>
                </>
              )}
            </>
          )}
          {resolving && (
            <>
              <h1 className="stage-h">Restoring your session…</h1>
              {/* Name the reason rather than spinning forever. Both of these states
                  retry on their own, so this is progress information, not a dead end. */}
              {listQ?.isError && (
                <p className="stage-p">
                  Couldn’t load the list: <code>{String(listQ.error)}</code> — retrying.
                </p>
              )}
              {/* Name the machine that has not answered. "Restoring your session" with
                  no reason is the same dead end as claiming the session is gone — one
                  of them is just quieter about it. */}
              {listLoaded && !askedEveryone && (
                <p className="stage-p">
                  Waiting for {silentName ?? 'a machine'} to answer — its agents are not
                  in the list yet.
                </p>
              )}
            </>
          )}
          {starting && (
            <>
              <h1 className="stage-h">
                Starting <code>{sel?.kind === 'agent' ? (sel.session ?? '') : ''}</code>…
              </h1>
              <p className="stage-p">
                It was just created here. It opens on its own as soon as{' '}
                {sel?.kind === 'agent' && sel.host ? <code>{sel.host}</code> : 'its machine'} lists
                it — usually within a few seconds.
              </p>
              <button type="button" className="stage-btn" onClick={closeSelection}>
                Cancel
              </button>
            </>
          )}
          {missing && (
            <>
              <h1 className="stage-h">That session isn’t here any more</h1>
              <p className="stage-p">
                The link points at{' '}
                <code>
                  {sel?.kind === 'agent'
                    ? (sel.session ?? sel.hash ?? '')
                    : sel?.kind === 'project'
                      ? sel.id
                      : sel?.kind === 'dm'
                        ? (sel.peer ?? sel.key ?? '')
                        : ''}
                </code>
                , which isn’t
                in the current list — it may have been closed, or the vault may still be
                locked.
              </p>
              <button type="button" className="stage-btn" onClick={closeSelection}>
                Clear
              </button>
            </>
          )}
          {!resolving && !starting && !missing && !sharedId && (
            /*
             * The signed-in welcome — what a desktop browser sees before anything is
             * selected (owner, 2026-08-19: 把這個 Legacy 的登入頁面給改了 … 讓使用者
             * 知道他成功的登入, 並且有些簡單的提示語). It replaced a Phase-1 dev note
             * about xterm mounting that had been the app's whole first impression on
             * a wide screen. The chips reuse the launchpad's exact classes so the
             * stage and the rail teach the same vocabulary in the same colours.
             */
            <div className="stage-welcome">
              <img className="sw-mark" src="/icon.svg" alt="" width={64} height={64} />
              <h1 className="sw-h">Welcome to Avengents</h1>
              <p className="sw-signed">
                <span className="sw-dot" aria-hidden="true" />
                {selfEmail ? (
                  <>
                    Signed in as <strong>{selfEmail}</strong>
                  </>
                ) : (
                  'Signed in'
                )}
              </p>
              <div className="sw-tips">
                <div className="sw-tip">
                  <span className="lp-chip lp-green">
                    <Icon name="smart_toy" size={15} />
                  </span>
                  <span className="sw-tip-txt">
                    <strong>Open an agent</strong>
                    <span>
                      Pick one from the rail on the left — its terminal opens right here,
                      live from the machine it runs on.
                    </span>
                  </span>
                </div>
                <div className="sw-tip">
                  <span className="lp-chip lp-mauve">
                    <Icon name="chat" size={15} />
                  </span>
                  <span className="sw-tip-txt">
                    <strong>Talk in projects</strong>
                    <span>
                      Rooms where people and their agents work together — share files,
                      assign work, read every agent’s replies.
                    </span>
                  </span>
                </div>
                <div className="sw-tip">
                  <span className="lp-chip lp-blue">
                    <Icon name="add" size={15} />
                  </span>
                  <span className="sw-tip-txt">
                    <strong>Build your crew</strong>
                    <span>
                      The ＋ button pairs a new machine, starts an agent on one you have,
                      or creates a project chat.
                    </span>
                  </span>
                </div>
              </div>
              {/* The install manual, right where a fresh account is standing
                  (owner, 2026-08-31: 加入安裝手冊 … welcome 畫面). The SAME
                  component the New-agent sheet mounts — one three-step guide,
                  two doors — expanded here because on this screen it IS the
                  content, not a side errand. Desktop-first by the owner's call;
                  the narrow layout keeps whatever the flex column does. */}
              <div className="sw-install">
                <InstallSection defaultOpen />
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      <div ref={stageRef} className="stage-drag">
      {/* One provider over BOTH surfaces: the chat's Message (project AND friend
          rooms render the same component) and Clean View's transcript. */}
      <PingContext.Provider value={pingCtx}>
      {panelProject && (
        <ProjectPanel
          project={panelProject}
          kind={project ? 'project' : 'friend'}
          locked={panelLocked}
          selfEmail={selfEmail}
          serverName={serverName}
          onOpenDoc={openDoc}
          filesOpen={projFilesOpen}
          onCloseFiles={() => setProjFilesOpen(false)}
          onFileCount={setProjFileCount}
          onDropFiles={stage}
          rosterOpen={rosterOpen}
          onCloseRoster={() => setRosterOpen(false)}
          onLeft={() => {
            // We are no longer a member: deselect (staying would poll a chat that
            // now 403s) and let the projects list drop the row.
            select(null)
            void qc.invalidateQueries({ queryKey: ['projects'] })
          }}
          onReply={(t) => {
            setReplyTo({ ...t, conv: panelProject.project_id })
            document.getElementById('composer')?.focus()
          }}
          onJumpAgent={(session, jumpHost) => {
            // Busy-chip jump: leave the project for that agent's conversation — the
            // original's jumpToAgent closes the project view the same way, and our
            // host-routed /ws replaces its switchBackend dance.
            setRosterOpen(false)
            select({ kind: 'agent', session, host: jumpHost })
          }}
        />
      )}
      {agent && (
        /* The pictures in Clean View open the cover-panel viewer (owner, 2026-09-13:
           點下圖片要成為蓋板的屏幕, 不是下載). The viewer is this component's, five
           levels above the tiles, so it is handed down as a context rather than
           threaded through every renderer between — see files/openDoc.ts. */
        <OpenDocContext.Provider value={openDoc}>
          <AgentPanel
            agent={agent}
            mode={effectiveMode}
            socketRef={termSock}
            onLink={setLink}
            asking={swallowsTyping(agentNeedsInput, effectiveMode)}
            onPeek={() => setPromptOpen(true)}
            localMachine={config.data?.name ?? config.data?.hostname ?? null}
            ownerName={vault.data?.email ?? null}
            fleet={fleetForIdentity(agentList, fleetIdQ.data?.agents)}
            /* So a chat/project message in Clean View can say WHICH room it came out
               of, using the same titles the rail shows. Resolved here because both
               lists already live in this component. */
            rooms={roomTitles(projectsQ.data?.projects, dmRows)}
            working={agentWorking}
            {...(agentIsTerminal && terminalRow?.plain
              ? {
                  onTerminalEnded: () => {
                    status('Terminal closed')
                    leaveTerminal(agent.session)
                  },
                }
              : {})}
          />
        </OpenDocContext.Provider>
      )}
      {shared && (
        /* Neither of the stage's contexts: a ping in a friend's conversation names THEIR agents
           (this one resolves, and jumps to, the reader's own by a hash that is only a name), and a
           file card in it names a path on THEIR machine (openDoc reads the reader's own). */
        <PingContext.Provider value={null}>
          <SharedAgentPanel
            key={shared.share_id}
            share={shared}
            selfEmail={myEmail}
            selfLabel={myName || myEmail?.split('@')[0] || 'you'}
          />
        </PingContext.Provider>
      )}
      </PingContext.Provider>
      </div>

      {/* Floating agent@server chip. One tap flips terminal ↔ clean view, and that
          is all it does. */}
      {agent && !agentIsTerminal && (
        <div id="aclBar">
        <div
          id="agentContextLabel"
          role="button"
          tabIndex={0}
          aria-live="polite"
          /*
           * ONE GESTURE: TAP TOGGLES THE VIEW — owner, 2026-10-14: 取消目前長按長型小藥丸,
           * 來達到設定的功能. 讓長型小藥丸專門負責 Clean View / Terminal 的切換就好.
           *
           * A 500ms hold here used to open this agent's card (2026-08-09, and from
           * 2026-08-21 the merged card), and a right-click did the same on a desktop.
           * Nothing on screen said so. The card has a door that does: the gear beside
           * Files in this bar, and the gear on the agent's face in the rail.
           */
          onClick={() => {
            const next: ViewMode = viewMode === 'terminal-view' ? 'auto-detect' : 'terminal-view'
            // setViewMode already persists (and, in a pane, deliberately does not touch
            // the shared key) — the second write here was a duplicate that reached past
            // that rule.
            setViewMode(next)
          }}
          // role="button" promises Enter and Space, and a div keeps neither unless asked:
          // from a keyboard this switch never worked. One press, one switch (no repeat).
          onKeyDown={(e) => {
            if ((e.key !== 'Enter' && e.key !== ' ') || e.repeat) return
            e.preventDefault()
            e.currentTarget.click()
          }}
          title={viewMode === 'terminal-view' ? 'Show the conversation' : 'Show the terminal'}
        >
          {/* The agent's own face, where a fixed sparkle used to be.
           *
           * That icon carried no state — it was the same auto_awesome whichever view
           * was active — so nothing is lost, and the chip now identifies WHICH agent
           * you are looking at the same way the rail does. On a phone the rail is a
           * separate screen, so this was the only place the two could disagree about
           * who you are talking to and the only one that could not say. */}
          <span className={`acl-view${agentWorking ? ' busy' : ''}`} aria-hidden="true">
            {/*
              18, not 22 — the geometry, with the cause corrected.

              Measured against the real stylesheets: chip height 37.9px, pill radius
              18.9px, and the pet's ink corner sits +2.18px OUTSIDE the rounded cap at
              22px, +0.60px at 20px, -0.19px at 19px, -0.97px at 18px. Those numbers
              are why the size changed and they still hold.

              This comment used to say backdrop-filter CLIPS descendants to the
              rounded border box. It does not, at least where it matters:
              avengents-app-builder measured `backdrop-filter: blur(6px)` +
              `border-radius: 999px` + `overflow: visible` on an iOS 26.3 simulator
              and the overflowing child painted in full. Nothing in this chain sets
              overflow: hidden, and .pixelpad-svg sets overflow: visible on purpose —
              so nothing here clips anything. What the owner saw as a cut was the
              pet's ink crossing the pill's 1px border and cap, which looks like one.

              19 would fit and is not chosen: the chip's height comes from two lines
              of text, so a different font metric moves it, and 0.19px of clearance is
              not clearance. 18 keeps five times the margin for the same "slightly
              smaller" the owner asked for.
            */}
            <PixelPet
              name={agent.session}
              seed={agent.hash}
              size={18}
              active={agentWorking}
            />
          </span>
          {/* Two lines. On one, the name and "@ host" competed for the same
              horizontal run and left no room for anything else — which is what
              pushed the note count out of this chip and under it. Stacked, the
              chip is barely taller and has room to spare. */}
          <span className="acl-id">
            <span className="acl-name">{agent.session}</span>
            {/* The ACCOUNT's name for the machine, not the edge's word for a
                relationship. This read `agent.host ?? 'Origin'` and printed "@ Origin"
                while the rail beside it printed "aowl" for the same agent — the rule
                existed, in the sidebar, as a local helper this file could not see. */}
            <span className="acl-at">@ {machineNameOf(agent, config.data?.name ?? config.data?.hostname ?? null)}</span>
          </span>
          {/* Display only, deliberately not a button: the panel already has three
              ways in (swipe, corner button, and the chip is not one of them), and
              a fourth would be a control competing with this chip's own tap
              gesture — which toggles the view. This just answers "how many". */}
          {notePending > 0 && (
            <span className="acl-notes" title={`${notePending} note${notePending === 1 ? '' : 's'} waiting`}>
              <Icon name="edit" size={12} />
              {notePending}
            </span>
          )}
          {/* The bell that used to sit here is gone — owner, 2026-08-07: 右上角那個
              message queue 的按鈕可以移除了. 相同的功能只需要一個就夠了.

              It was a decorative <span>: aria-hidden, no handler, no state, drawing a
              notifications glyph that never meant anything. Harmless while nothing else
              in the app was a bell; actively misleading now that held mail has a real,
              counted control in the composer. Two bells where one is fake is worse than
              no bell — you cannot tell by looking which one would have told you. */}
        </div>
        {/* The peek button lives in the Clean View now — bottom-left, round, badged.
            An earlier edit meant to move it and only moved half: this copy stayed in
            the title bar, unstyled (its CSS went with the move) but rendered and
            clickable. Two doors to one sheet, one of them invisible. */}
        </div>
      )}

      {/*
        A BARE TERMINAL'S PILL — owner, 2026-10-18: Session Name 只需要給出 Session 的 folder path
        以及機器名稱就好了. Display only: a terminal has no conversation view to switch to.
      */}
      {agent && agentIsTerminal && (
        <div id="aclBar">
          <div
            id="agentContextLabel"
            className="acl-term"
            aria-live="polite"
            title={terminalRow?.cwd ?? agent.session}
          >
            <span className="acl-view" aria-hidden="true">
              <Icon name="terminal" size={18} />
            </span>
            <span className="acl-id">
              <span className="acl-name">{terminalRow?.path ?? terminalRow?.cwd ?? agent.session}</span>
              <span className="acl-at">@ {machineNameOf(agent, config.data?.name ?? config.data?.hostname ?? null)}</span>
            </span>
          </div>
        </div>
      )}
      {/*
        THE PILL, FOR A FRIEND'S AGENT — owner, 2026-10-17: 不過 Agent 小藥丸需要下標 Agent 的 owner.
        Your own agent's chip, with whose it is under the name, first on that line so a narrow
        screen cuts the machine before the owner. Display only: there is no terminal to flip to.
      */}
      {!agent && shared && (
        <div id="aclBar">
          <div
            id="agentContextLabel"
            className="acl-shared"
            aria-live="polite"
            title={`${sharedName}, shared by ${sharedOwner} · ${shared.agent.machine || 'their machine'}`}
          >
            <span className={`acl-view${sharedLive.working ? ' busy' : ''}`} aria-hidden="true">
              <PixelPet
                name={sharedName}
                seed={shared.agent.hash || sharedName}
                size={18}
                active={sharedLive.working}
              />
            </span>
            <span className="acl-id">
              <span className="acl-name">{sharedName}</span>
              <span className="acl-at">
                <span className="acl-owner" title={`Shared by ${sharedOwner}`}>
                  <Icon name="share" size={10} />
                  {sharedOwner}
                </span>
                {` · @ ${shared.agent.machine || 'their machine'}`}
              </span>
            </span>
          </div>
        </div>
      )}

      {drawerOpen && (
        <ShellDrawer
          session={agent?.session ?? null}
          host={agent?.host ?? null}
          onClose={() => setDrawerOpen(false)}
        />
      )}

      {filesOpen && (
        <FileBrowser
          session={agent?.session ?? null}
          host={agent?.host ?? null}
          onOpenFile={setViewing}
          onStatus={status}
          onClose={() => {
            setFilesOpen(false)
            // Closing the drawer resets the view, so the Files button always means
            // files. Left sticky, someone who last used Source Control would press
            // "Files" and get a commit box.
            setGitOpen(false)
          }}
          gitView={gitOpen}
          onGitView={setGitOpen}
        />
      )}

      {/* The agent's whiteboard, its session's drawer (owner, 2026-11-01). Never out with the file browser.
          Its key is its own: the tab corner is keyed by the same agent among these same children, and two children with one
          key are reconciled as one — measured on app-dev, every render placed another drawer and removed none. */}
      {wbOpen && wbAgent && <BoardDrawer key={`wb|${wbKey}`} hash={wbAgent.hash} rooms={wbRooms} onClose={() => setWbFor(null)} />}
      {/* The agent's pages, its session's other drawer (owner, 2026-11-03) — keyed apart from the tab corner as the board's is. */}
      {pgOpen && wbAgent && <AgentPagesDrawer key={`pg|${wbKey}`} hash={wbAgent.hash} rooms={wbRooms} onClose={() => setPgFor(null)} />}

      {/* A project room's whiteboard and pages, its drawers (owner, 2026-11-02), opened from its column of tools. */}
      {roomDrawerOpen && project && roomDrawer.tool === 'board' && (
        <RoomBoardDrawer key={`rwb|${project.project_id}`} convId={project.project_id} title={project.name || 'Project'} board={roomDrawer.board} onClose={() => setRoomDrawer(null)} />
      )}
      {roomDrawerOpen && project && roomDrawer.tool === 'pages' && pagesOn() && (
        <PagesDrawer
          key={`rpg|${project.project_id}|${roomDrawer.n ?? 0}`}
          convId={project.project_id}
          title={project.name || 'Project'}
          {...(roomDrawer.page ? { page: roomDrawer.page } : {})}
          onClose={() => setRoomDrawer(null)}
        />
      )}

      {sharedFilesOpen && shared?.files && sharedOpen && (
        <SharedFilesDock id={shared.share_id} onClose={() => setSharedFilesOpen(false)} />
      )}
      {sharedCardOpen && shared && (
        <SharedAgentCard
          share={shared}
          onClose={() => setSharedCardOpen(false)}
          /* Taken off the friend's list: back to it — the rail on a phone, the welcome on a wide screen. */
          onRemoved={() => {
            setSharedCardOpen(false)
            closeSelection()
            if (phoneNavRef.current) setSbOpen(true)
          }}
        />
      )}

      {viewing?.youtube && <YouTubeViewer video={viewing.youtube} onClose={() => setViewing(null)} />}
      {viewing && !viewing.youtube && (
        <FileViewer
          {...(viewing.path ? { path: viewing.path } : {})}
          {...(viewing.url ? { url: viewing.url } : {})}
          name={viewing.name}
          kind={viewing.kind}
          host={agent?.host ?? null}
          onClose={() => setViewing(null)}
        />
      )}

      {/*
        * The + button asks WHERE before WHAT — pick a paired server, then attach one
        * of the sessions it reports. Wired to /account/* on the BFF as of 2026-08-04.
        *
        * ATTACH, not create: the edge is pure-outbound, so "start a new agent there"
        * needs a command queue the server side has not built yet. NewAgentSheet — the
        * old create form — is deliberately NOT rendered anywhere right now; its every
        * query targets an edge proxy that no longer exists. It returns when the
        * create contract does. (Stated because this codebase has already shipped one
        * correct-component-nobody-invokes; the difference here is it is on purpose,
        * and written down.)
        */}
      {/*
        ONE sheet, not two. The ＋ used to open "Choose a server", then "Start a new
        agent on X", then a directory browser that replaced that form — three screens
        to answer four questions. The owner's instruction: 全部都可以設在同一個界面，
        不要用那麼多個彈出視窗跳過來跳過去. The sheet asks for the machine itself now,
        so there is no chosen-server state between the button and the form; the sheet
        reports the device it used, which is what the cache write below needs.
      */}
      {/* A game, when the URL names one (?game=<id>) — over everything, under any sheet (games/GameHost). */}
      <GameHost />
      {boardConv && (
        <BoardScreen
          convId={boardConv}
          title={projectsQ.data?.projects.find((p) => p.project_id === boardConv)?.name ?? 'Project'}
          onClose={closeBoard}
        />
      )}
      {pagesConv && pagesOn() && (
        <PageScreen
          convId={pagesConv}
          title={projectsQ.data?.projects.find((p) => p.project_id === pagesConv)?.name ?? 'Project'}
          onClose={closePages}
        />
      )}

      {morePage && (
        <MoreStage
          page={morePage}
          onClose={() => setMorePage(null)}
          railOpen={sbOpen}
          onToggleRail={() => setSbOpen((v) => !v)}
          onOpenArtifact={(row) => setViewArtifact({ row })}
          /* A friend's artifact is a different SUBJECT, never a row of this account's
             — the viewer draws none of the owner's controls for it and frames it only
             from the artifacts' own host (owner, 2026-10-05). */
          onOpenShared={(shared) => setViewArtifact({ shared })}
          /* A restored backup opens the way a fork does: the new agent is expected
             before the roster lists it, so the stage waits for it instead of saying
             it is not there (owner, 2026-09-29: restore to any machine/folder/name). */
          onOpenAgent={(a) => {
            // Off the stage first: an agent that is already the one selected moves no URL.
            setMorePage(null)
            openAgent({ session: a.session, host: null, hash: a.hash, device_id: a.device, onboarded: true } as AgentRow)
            setExpecting({ session: a.session, host: null, since: Date.now() })
            void settleRoster(qc, agentPresent(a.session), { tries: 40, gapMs: 1500 }).then(() =>
              setExpecting((e) => (e?.session === a.session ? null : e)),
            )
            void qc.invalidateQueries({ queryKey: ['agents'] })
          }}
          /* More › Chrome tabs › a tab › its agent's face (owner, 2026-10-14): into that
             agent's session — the rail's own row when the roster has it, so the stage
             resolves it by (hash, device) like any rail press. */
          onGoToAgent={(a) => {
            setMorePage(null)
            openAgent(
              agentsQ.data?.agents?.find((x) => x.hash === a.hash && (x.device_id ?? null) === a.device) ??
                ({ session: a.session, host: null, hash: a.hash, device_id: a.device, onboarded: true } as AgentRow),
            )
          }}
          /* More › Bookmarks and More › Whiteboards (owner, 2026-11-01): every bookmark and every board in one place, and
             a press goes there — the agent's conversation, the project's message, the project's board. */
          agents={agentsQ.data?.agents ?? []}
          projects={projectsQ.data?.projects ?? []}
          onGoAgentBookmark={(a, b) => {
            setMorePage(null)
            openAgent(a)
            // The message is in the conversation: a terminal view switches once this agent is the one selected.
            setCleanViewFor(a.session)
            jumpTo({
              device: a.device_id ?? null,
              session: a.session,
              file: b.file,
              index: b.index,
              onResult: (r) => {
                if (r === 'elsewhere') status('That bookmark is from an earlier conversation of this agent.')
                if (r === 'unreachable') status('That part of the conversation could not be opened on its machine just now — its words are in the bookmark.')
              },
            })
          }}
          onGoRoomBookmark={(p, b) => {
            setMorePage(null)
            openProject(p)
            jumpTo({
              device: null,
              session: roomJumpSession(p.project_id),
              file: p.project_id,
              index: b.index,
              onResult: (r) => {
                if (r !== 'shown') status('That message is no longer in this project — its words are gone with it.')
              },
            })
          }}
          onOpenBoard={(p, board) => {
            setMorePage(null)
            openProject(p)
            openBoardOn(p.project_id, board)
          }}
          /* More › Pages (owner, 2026-11-03: 如同 whiteboard 一般): the project, its pages over it on that page. */
          onOpenPage={(p, page) => {
            setMorePage(null)
            openProject(p)
            openPages(p.project_id, page)
          }}
        />
      )}

      {viewArtifact && (
        <ArtifactViewer
          /* An IDENTITY, not a snapshot: for an own row the viewer subscribes to
             the same ['artifacts'] cache the rail draws from, so its permission
             dial can actually move when a write lands. Handing it a frozen row is
             what made the three buttons look dead (owner, 2026-09-20). The spread
             is the subject itself: `row`, or `shared` for a friend's artifact. */
          {...viewArtifact}
          onClose={() => setViewArtifact(null)}
          railOpen={sbOpen}
          onToggleRail={() => setSbOpen((v) => !v)}
        />
      )}

      {cardAgent && (
        <AgentCard
          agent={cardAgent}
          machine={machineNameOf(cardAgent, config.data?.name ?? config.data?.hostname ?? null)}
          product={productForKind(idle.byKey.get(idleKey(cardAgent))?.kind)}
          // The device to browse for the fork's folder: the machine this agent
          // runs on. Its own stamp when it has one; the relay's current target is
          // only right for a local row, which is exactly when the stamp is missing.
          deviceId={cardAgent.device_id ?? relayTarget() ?? ''}
          deleting={killAgent.isPending}
          endError={killAgent.error ? whyRestartFailed(killAgent.error) : null}
          onClose={() => {
            // The error belongs to THIS card's attempt; the next card starts clean.
            killAgent.reset()
            setCardAgent(null)
          }}
          onDelete={() => {
            killAgent.mutate(
              {
                session: cardAgent.session,
                host: cardAgent.host,
                // The card opens from the rail for an agent on ANY machine, and
                // opening a card does not point the relay anywhere.
                ...(cardAgent.device_id ? { device: cardAgent.device_id } : {}),
              },
              { onSuccess: () => setCardAgent(null) },
            )
          }}
          onRenamed={(newName) => {
            /*
             * Optimistically rename the row in the list so the rail doesn't show the
             * old name for the beat before the refetch confirms it. The hash follows
             * the name (agentColor re-derives), and invalidate reconciles with the
             * server's truth.
             */
            const host = cardAgent.host
            qc.setQueryData(
              ['agents'],
              (prev: { agents?: AgentRow[]; terminals?: unknown[] } | undefined) =>
                prev
                  ? {
                      ...prev,
                      agents: (prev.agents ?? []).map((a) =>
                        a.session === cardAgent.session && (a.host ?? null) === (host ?? null)
                          ? { ...a, session: newName, hash: newName }
                          : a,
                      ),
                    }
                  : prev,
            )
            /*
             * Re-point the SELECTION only when the agent that was renamed is the one
             * on stage — the selection holds the old name and would resolve to
             * nothing. This sheet now opens from the rail as well as the header, so
             * renaming some OTHER agent must not yank the stage away from what you
             * are reading; that was the one behaviour the merge could have broken.
             */
            const onStage =
              agent?.session === cardAgent.session && (agent?.host ?? null) === (host ?? null)
            if (onStage) select({ kind: 'agent', session: newName, host })
            void qc.invalidateQueries({ queryKey: ['agents'] })
          }}
          onForked={(newName, host) => {
            /*
             * The fork is a NEW session on the SAME device. Drop a synthetic row in
             * so the stage can open it before the refetch confirms, then select it —
             * a fork is asked for in order to work in it, whichever agent it came
             * from, so this one DOES take the stage.
             */
            qc.setQueryData(
              ['agents'],
              (prev: { agents?: AgentRow[]; terminals?: unknown[] } | undefined) =>
                prev
                  ? {
                      ...prev,
                      agents: [
                        ...(prev.agents ?? []),
                        { session: newName, host, hash: newName, onboarded: true },
                      ],
                    }
                  : prev,
            )
            select({ kind: 'agent', session: newName, host })
            /*
             * TELL THE STAGE THIS ONE IS EXPECTED. The invalidation below replaces the
             * synthetic row with the machine's real roster, which will not list the
             * fork until its CLI has started — and without this the stage read that
             * gap as "That session isn't here any more" (owner's phone, 2026-09-06).
             * Re-asked until the roster agrees; the expectation is dropped either way
             * when the asking stops, so a fork that truly never starts still ends on
             * the honest sentence.
             */
            setExpecting({ session: newName, host: host ?? null, since: Date.now() })
            void settleRoster(qc, agentPresent(newName), { tries: 40, gapMs: 1500 }).then(() =>
              setExpecting((e) => (e?.session === newName ? null : e)),
            )
            void qc.invalidateQueries({ queryKey: ['agents'] })
          }}
        />
      )}

      {groupLinkCode && (
        <GroupLinkSheet
          code={groupLinkCode}
          onClose={() => setGroupLinkCode(null)}
          onJoined={(gid) => {
            setGroupLinkCode(null)
            setOpenGroupId(gid)
            pickNav('group')
          }}
        />
      )}

      {inviteCode && (
        <InviteWelcome
          code={inviteCode}
          onClose={() => setInviteCode(null)}
          onOpenHumans={() => {
            setInviteCode(null)
            pickNav('group')
          }}
        />
      )}

      {newOpen && (
        <StartAgentSheet
          onClose={() => setNewOpen(false)}
          onStatus={status}
          // The signed-in email comes from the Google session (whoami), not the vault —
          // this vaultless deployment leaves vault.email empty, which is why "My code"
          // read as signed-out. myEmail is null only when actually signed out.
          selfEmail={myEmail}
          onScanOpen={setScanOpen}
          onOpenShell={(device, name, command) => {
            // The tmux-install fallback: a shell on THAT machine with the install
            // command pre-typed, NOT auto-run — sudo may ask for a password.
            void import('./terminal/shellTabs').then((m) =>
              m.openShellTab(null, null, { device, title: name, home: true, initialCommand: command }),
            )
            setDrawerOpen(true)
          }}
          onTerminalCreated={(session, device, path) => {
            setNewOpen(false)
            status(`Terminal opened in ${path}`)
            /*
             * The machine named it and it already exists; the stage resolves a terminal
             * from the rail's list, so the list is asked until it has the row, then the
             * terminal opens — the same "straight into what you made" as an agent.
             */
            setExpecting({ session, host: null, since: Date.now() })
            void settleRoster(qc, terminalPresent(session, device), { tries: 10, gapMs: 800 }).then((listed) => {
              setExpecting((e) => (e?.session === session ? null : e))
              if (!listed) return
              const t = qc
                .getQueryData<Everywhere>(['agents'])
                ?.terminals.find((x) => x.session === session && (!x.device_id || x.device_id === device))
              openAgent({
                session,
                host: t?.host ?? null,
                hash: session,
                onboarded: false,
                cwd: t?.cwd ?? null,
                device_id: device,
                ...(t?.device_name ? { device_name: t.device_name } : {}),
              })
            })
          }}
          onGroupCreated={(gid, name) => {
            setNewOpen(false)
            status(`${name} created`)
            // Straight to it, in Humans — where its owner invites people (groups/GroupsSection).
            setOpenGroupId(gid)
            pickNav('group')
          }}
          onProjectCreated={(p) => {
            setNewOpen(false)
            status(`${p.name} created`)
            void qc.invalidateQueries({ queryKey: ['projects'] })
            // Straight into the room that was just made — the same "the thing you
            // asked for is now in front of you" contract as agent creation.
            openProject(p)
          }}
          onCreated={(session: string, device: string, where: string | null) => {
            setNewOpen(false)
            // `where` names where it ACTUALLY started — and calls out the fallback
            // when the asked-for folder could not be used. afae062's contract.
            status(where ? `${session} ${where}` : `${session} started`)
            /*
             * ASK, don't fabricate. The previous version pushed the session NAME into
             * `sv.agents` — an AgentRef[] whose elements carry channel_hash. It
             * compiled because setQueryData infers its type from the return value
             * rather than checking it against the cache's, so the corruption was
             * silent. And it could never have been right: the channel hash is minted
             * on the machine, so there is nothing truthful this side can write.
             *
             * Invalidating is one round trip through the same fan-out every other
             * update uses — and the row it returns carries the device stamp, which is
             * what makes clicking the new session open a terminal on the machine that
             * actually runs it. The sheet reports which device it used, but the
             * fan-out asks all of them anyway, so the id is not needed here.
             */
            /*
             * STRAIGHT INTO THE SESSION THAT WAS JUST MADE — owner, 2026-08-24:
             * 當創造一個新的 Agent 之後應該要直接進入該 Agent 的 session 而非讓
             * 使用者自己從 side panel 來點擊進入.
             *
             * The project branch of this very sheet has carried that contract all
             * along (openProject right above), and its comment even cites "the
             * same … contract as agent creation" — a contract the agent branch
             * never actually fulfilled, because it CANNOT act at create-return
             * time: everything openAgent needs (the channel hash, the device
             * stamp that points the relay at the right machine) is minted on the
             * machine and only arrives with the roster row.
             *
             * So the settle below — which already existed to stop the rail from
             * showing a terminal where an agent belongs — now also hands back the
             * row, and entering the conversation is the same openAgent the rail
             * click would have done. If the machine never lists it, nothing opens
             * and the status line above remains the honest report.
             */
            // Expected by name only: the machine's host label is not known here, and
            // the row that arrives carries it. A slow machine gets the same patience
            // as a fork (the previous six tries gave up on anything over ~4 s and
            // left the person to find the new session in the rail by hand).
            setExpecting({ session, host: null, since: Date.now() })
            void settleRoster(qc, agentPresent(session), { tries: 40, gapMs: 1500 }).then((listed) => {
              setExpecting((e) => (e?.session === session ? null : e))
              if (!listed) return
              const e = qc.getQueryData<Everywhere>(['agents'])
              const row = e && newlyCreatedRow(e, session, device)
              if (row) openAgent(row)
            })
            void qc.invalidateQueries({ queryKey: ['idle-status'] })
          }}
        />
      )}

      {promptOpen && agent && (
        <PromptSheet agent={agent} onClose={() => setPromptOpen(false)} />
      )}

      {dpad.hint && (
        <div id="dpadHint" aria-hidden="true">
          {{ up: '↑', down: '↓', left: '←', right: '→' }[dpad.hint]}
        </div>
      )}

      <div id="scrollbar">
        <div id="scrollthumb" style={{ top: 8, height: 64 }} />
      </div>

      <div id="bar">
        {/* On a phone this is a BACK control, because that is what it does there:
            the rail is the root screen and the conversation is pushed over it.
            Calling it "Agents" and drawing a hamburger would describe a drawer,
            which is the thing this navigation stopped being. */}
        <button
          type="button"
          id="sbToggle"
          className={sbOpen ? 'on' : undefined}
          title={phoneNav ? 'Back to the list' : 'Agents'}
          aria-label={phoneNav ? 'Back to the list' : 'Agents'}
          onClick={() => setSbOpen((v) => !v)}
        >
          {/* The icon set has no chevron_left; flipped in CSS rather than added
              to the generated file, which is regenerated from source elsewhere. */}
          <Icon
            name={phoneNav ? 'chevron_right' : 'menu'}
            size={phoneNav ? 22 : 18}
            label={phoneNav ? 'Back' : 'Agents'}
          />
        </button>
        {panelProject && (
          <div id="projHead">
            <button
              type="button"
              id="projTitle"
              title="Members · invite · attach"
              aria-expanded={rosterOpen}
              onClick={() => setRosterOpen((v) => !v)}
            >
              <ProjectAvatar projectId={panelProject.project_id} label={panelProject.name ?? panelProject.project_id} size={18} />
              <span className="proj-title-name">{panelProject.name ?? panelProject.project_id}</span>
              <span className="proj-count">
                {typeof panelProject['member_count'] === 'number' ? panelProject['member_count'] : ''}
              </span>
            </button>
          </div>
        )}
        {/* In a project the folder button opens the PROJECT's shared files; otherwise
            it opens the filesystem browser. Matches #projFilesBtn vs the fs dock.

            ONLY WHERE THERE ARE FILES TO BROWSE — owner, 2026-09-22: 在 welcome 頁面,
            右上角那個 File browser icon 不應該出現。file browser icon 出現的場合只有
            Agent session / Project chat / Friend chat.

            The header bar renders above the stage whatever the stage is showing, so
            this button came with it onto the welcome screen, where it offered the
            filesystem of whichever machine the relay happened to point at — a machine
            the person has not chosen and is not looking at.

            Gated on `agent || panelProject`, which is exactly his three: an agent
            session (a bare terminal included — App resolves those to the same stage),
            a project chat, and a friend chat, since `panelProject` is
            `project ?? dmProject`. Deliberately NOT `dmPeer`: that turns true while
            the DM's row is still resolving, and the button would be on screen for a
            moment still wired to the filesystem branch — visible, and wrong. */}
        {/*
          THE AGENT'S SETTINGS, FROM INSIDE ITS SESSION — owner, 2026-10-14: 在 Agent session
          右上角 File folder icon 的旁邊. 設置一個齒輪 icon. 讓使用者也可以直接在 session 裡面
          點擊該齒輪達到設定的功能.

          The same card the gear on the agent's face opens in the rail. For an agent, not a
          bare terminal: a terminal has no card (the pill's hold opened one for it anyway,
          offering Model and Fork to a shell). In a split pane as well, where the Files
          button beside it is hidden (shell.css, body.pane-frame): the hold this replaces
          was that pane's only way in. Left of Files, so the folder stays where hands
          already find it.
        */}
        {agent && !agentIsTerminal && (
          <button
            type="button"
            id="agentSettingsBtn"
            className="right"
            title="Agent settings"
            aria-label={`Settings for ${agent.session}`}
            aria-haspopup="dialog"
            onClick={() => setCardAgent(agent)}
          >
            <Icon name="settings" size={17} />
          </button>
        )}
        {!agent && shared && (
          <button
            type="button"
            id="agentSettingsBtn"
            className="right"
            title="About this shared agent"
            aria-label={`About ${sharedName}, shared by ${sharedOwner}`}
            aria-haspopup="dialog"
            onClick={() => setSharedCardOpen(true)}
          >
            <Icon name="settings" size={17} />
          </button>
        )}
        {!agent && !panelProject && shared?.files && sharedOpen && (
          <button
            type="button"
            id="filesBtn"
            className={`right${sharedFilesOpen ? ' on' : ''}`}
            title="Files"
            aria-label="Files"
            onClick={() => setSharedFilesOpen((v) => !v)}
          >
            <Icon name="folder" size={17} label="Files" />
          </button>
        )}
        {(agent || panelProject) && (
        <button
          type="button"
          id="filesBtn"
          className={`right${(panelProject ? projFilesOpen : filesOpen) ? ' on' : ''}`}
          title={panelProject ? 'Project files' : 'Files'}
          aria-label={panelProject ? 'Project files' : 'Files'}
          onClick={() => {
            if (panelProject) setProjFilesOpen((v) => !v)
            else setFilesOpen((v) => !v)
          }}
        >
          <Icon name="folder" size={17} label={project ? 'Project files' : 'Files'} />
          {project && projFileCount > 0 && <span className="bar-badge">{projFileCount}</span>}
        </button>
        )}
        {/* A bare terminal closes from here as well as from its row in the rail — owner,
            2026-10-18: 使用者可以隨時將這個 Terminal 關閉. Rightmost, where a close lives. */}
        {agent && agentIsTerminal && (
          <CloseTerminalButton
            key={`${agent.device_id ?? ''}:${agent.session}`}
            label={terminalRow?.path ?? agent.session}
            onClose={() => closeTerminal(agent, terminalRow?.plain === true)}
          />
        )}
      </div>

      {/* The Chrome tab this agent holds, in the corner of its session (owner, 2026-10-15):
          over the conversation and the terminal alike; a tap opens it. A bare terminal holds none. */}
      {agent && !agentIsTerminal && <AgentTabCorner key={tabHolderKey(agent)} agent={agent} onScreen={stageOnScreen} />}
      {/* …and a friend's agent's, when the share shows them (owner, 2026-10-17). */}
      {!agent && shared?.tabs && sharedOpen && <SharedTabCorner key={shared.share_id} share={shared} />}

      {/* A send that failed keeps its reason on screen. Unlike #status this does NOT
          auto-hide: the user has to be able to read it after looking away, and it is
          the only thing telling them the text is still unsent. */}
      {sendError && (
        <div id="sendErr" role="alert">
          <Icon name="warning" size={14} />
          <span>{sendError}</span>
          <button type="button" onClick={() => setSendError(null)} aria-label="Dismiss">
            <Icon name="close" size={12} />
          </button>
        </div>
      )}

      {/* Transient: fades ~3s after each message, so it never sits over live output. */}
      <div
        id="status"
        className={statusShown ? 'show' : undefined}
        role="status"
        aria-live="polite"
      >
        {statusMsg}
      </div>

      {/* Desktop way in: a swipe is not discoverable with a mouse, and a wide
          screen has room the phone does not. Carries the pending count, because
          moving the list into a panel made "how many are waiting" invisible. */}
      {/* Not on a bare terminal: a note is for an agent, and a shell takes nothing delivered. */}
      {/* Icon only, named by a tip after half a second's hover (owner, 2026-11-01: 寬螢幕 … 把上面的文字拿掉, 只使用一個
          圓弧方框的 icon … mouse over 大概 0.5 秒左右就會跳出來提示字). The tip is CSS (.corner-fab), not `title`: the
          browser picks a title's delay, and both would show. */}
      {!!agent && !agentIsTerminal && (
        <button
          type="button"
          id="noteFab"
          className="corner-fab"
          data-tip="Notes"
          aria-label={notePending > 0 ? `Notes, ${notePending} waiting` : 'Notes'}
          onClick={() => setNoteOpen(true)}
        >
          <Icon name="edit" size={17} />
          {notePending > 0 && <span className="nt-count">{notePending}</span>}
        </button>
      )}

      {/* The bookmarks' corner button, over the notes' (owner, 2026-10-31: 寬螢幕的時候 … 右下角點擊展開書籤 List). */}
      {(bmOffered || roomBmOffered) && (
        <button
          type="button"
          id="bmFab"
          className={roomBmOffered ? 'corner-fab room' : 'corner-fab'}
          data-tip="Bookmarks"
          aria-label={bmCount > 0 ? `Bookmarks, ${bmCount} kept` : 'Bookmarks'}
          onClick={() => setBmOpen(true)}
        >
          <Icon name="bookmarks" size={17} />
          {bmCount > 0 && <span className="bm-count">{bmCount}</span>}
        </button>
      )}

      {/* The whiteboard's corner button, over the bookmarks' (or in their place): it opens the session's whiteboard
          drawer, which covers the session here, the rail beside it (owner, 2026-11-02). */}
      {wbOffered && (
        <button
          type="button"
          id="wbFab"
          className={['corner-fab', bmOffered ? '' : 'low', wbOpen ? 'on' : ''].filter(Boolean).join(' ')}
          data-tip={wbOpen ? 'Close the whiteboard' : 'Whiteboard'}
          aria-label={wbOpen ? 'Close the whiteboard' : 'Whiteboard'}
          aria-expanded={wbOpen}
          onClick={() => (wbOpen ? setWbFor(null) : openWb())}
        >
          <Icon name="schema" size={17} />
        </button>
      )}

      {/* The pages' corner button, over the whiteboard's — the room's Pages button's blue (owner, 2026-11-03: 如同
          whiteboard icon). */}
      {pgOffered && (
        <button
          type="button"
          id="pgFab"
          className={['corner-fab', bmOffered ? '' : 'low', pgOpen ? 'on' : ''].filter(Boolean).join(' ')}
          data-tip={pgOpen ? 'Close the pages' : 'Pages'}
          aria-label={pgOpen ? 'Close the pages' : 'Pages'}
          aria-expanded={pgOpen}
          onClick={() => (pgOpen ? setPgFor(null) : openPg())}
        >
          <Icon name="description" size={17} />
        </button>
      )}

      {/* A project room's column of tools over its Bookmarks — the whiteboard, then the pages (owner, 2026-11-02: 都放在
          Bookmark 的上方 … a column of buttons on the right hand side). Corner buttons on a wide screen, handles on a phone. */}
      {roomToolsConv && !roomDrawerOpen && (
        <RoomCorner
          convId={roomToolsConv}
          pages={pagesOn()}
          low={!roomBmOffered}
          handles={!bmOpen && !noteOpen}
          onOpen={(tool, board) => setRoomDrawer({ conv: roomToolsConv, tool, board: board ?? MAIN_BOARD })}
        />
      )}

      {/* Left-edge guard.
          Swiping right from the very edge of an iPhone screen is Safari's BACK
          gesture, and it replaced the whole page — the app vanishing mid-session
          for a swipe the user meant for the sidebar. An element that owns the
          touch is what stops it, which is the same thing touch-action:none on
          the note handle already does for the forward gesture on the right.
          Invisible on purpose: this is protection, not an affordance, and the
          rail already has a button. It drives the same drag the terminal does,
          so the gesture itself is unchanged.

          It is only needed while the rail is CLOSED. Once open, the rail itself
          is what the finger lands on at the edge, and covering its left 20px
          would take a strip out of every row's tap target — so the rail defends
          itself with touch-action:pan-y instead (see shell.css). */}
      {!sbOpen && <div id="sbEdgeGuard" ref={sbGuard} aria-hidden="true" />}

      {/* Phone: the handle that teaches the swipe.
          A gesture nobody can see is a gesture nobody finds, so this is always
          there while an agent is open — not only when something is queued —
          because its job is to say the panel EXISTS. It is grabbable for the
          same reason: an affordance shaped like a handle that ignores being
          pulled is a broken promise, so a leftward drag on it opens the panel
          exactly as a drag on the terminal does, and a plain tap works too. */}
      {!!agent && !agentIsTerminal && !noteOpen && (
        <button
          type="button"
          id="noteHandle"
          className={notePending > 0 ? 'has' : undefined}
          aria-label={
            notePending > 0 ? `Notes, ${notePending} waiting — pull to open` : 'Notes — pull to open'
          }
          title="Pull out for notes"
          onClick={() => setNoteOpen(true)}
          onTouchStart={(e) => {
            handleX.current = e.touches[0]?.clientX ?? 0
          }}
          onTouchMove={(e) => {
            const x = e.touches[0]?.clientX ?? 0
            if (handleX.current - x > 8) {
              handleX.current = Number.POSITIVE_INFINITY // fire once per gesture
              setNoteOpen(true)
            }
          }}
        >
          <span className="nh-grip" aria-hidden="true" />
        </button>
      )}

      {/* Phone: the bookmarks' handle under the notes' — its own colour, pulled from right to left the same way
          (owner, 2026-10-31: 從右向左滑的時候 … 就會跳出來書籤的瀏覽 list). */}
      {(bmOffered || roomBmOffered) && !noteOpen && !bmOpen && (
        <button
          type="button"
          id="bmHandle"
          className={[roomBmOffered ? 'room' : '', bmCount ? 'has' : ''].filter(Boolean).join(' ') || undefined}
          aria-label={bmCount ? `Bookmarks, ${bmCount} — pull to open` : 'Bookmarks — pull to open'}
          title="Pull out for bookmarks"
          onClick={() => setBmOpen(true)}
          onTouchStart={(e) => {
            bmHandleX.current = e.touches[0]?.clientX ?? 0
          }}
          onTouchMove={(e) => {
            const x = e.touches[0]?.clientX ?? 0
            if (bmHandleX.current - x > 8) {
              bmHandleX.current = Number.POSITIVE_INFINITY // fire once per gesture
              setBmOpen(true)
            }
          }}
        >
          <Icon name="bookmark_filled" size={11} />
        </button>
      )}

      {/* Phone: the whiteboard's handle over the notes', in the whiteboards' green — pulled from right to left, or tapped,
          like theirs (owner, 2026-11-01: 新增一個抽屜, 使用者拉出來的時候可以看到這個 Agent 他所在的白板). */}
      {wbOffered && !wbOpen && !noteOpen && !bmOpen && (
        <button
          type="button"
          id="wbHandle"
          aria-label="Whiteboard — pull to open"
          title="Pull out the whiteboard"
          onClick={openWb}
          onTouchStart={(e) => {
            wbHandleX.current = e.touches[0]?.clientX ?? 0
          }}
          onTouchMove={(e) => {
            const x = e.touches[0]?.clientX ?? 0
            if (wbHandleX.current - x > 8) {
              wbHandleX.current = Number.NEGATIVE_INFINITY // fire once per gesture
              openWb()
            }
          }}
        >
          <Icon name="schema" size={11} />
        </button>
      )}

      {/* Phone: the pages' handle over the whiteboard's, in the pages' blue — pulled or tapped like theirs. */}
      {pgOffered && !pgOpen && !wbOpen && !noteOpen && !bmOpen && (
        <button
          type="button"
          id="pgHandle"
          aria-label="Pages — pull to open"
          title="Pull out the pages"
          onClick={openPg}
          onTouchStart={(e) => {
            pgHandleX.current = e.touches[0]?.clientX ?? 0
          }}
          onTouchMove={(e) => {
            const x = e.touches[0]?.clientX ?? 0
            if (pgHandleX.current - x > 8) {
              pgHandleX.current = Number.NEGATIVE_INFINITY // fire once per gesture
              openPg()
            }
          }}
        >
          <Icon name="description" size={11} />
        </button>
      )}

      <div id="footbar" ref={footbar}>
        {searchMode && <SearchStrip onDone={exitSearch} />}
        {/* The extra-keys row sits ABOVE the text box (owner, 2026-09-13: 放到 TextEdit
            的上方, 這樣子對於使用者而言會比較方便 — it was docked beneath it). #footbar is a
            column, so DOM order is the placement; --foot-total measures the whole bar
            either way, so the terminal refits exactly as before. */}
        {/* A bare terminal has no composer and so no ⌨ toggle: on a phone its keys row is
            simply there (the soft keyboard has no Tab, arrows, Esc or Ctrl-C), and on a
            wide screen the hardware keyboard is the whole input. */}
        {(agentIsTerminal ? narrow : keysOpen) && (!shared || sharedKeys) && (
          <KeysMenu
            socketRef={termSock}
            // A friend's agent: by name, through the share (they hold no pty).
            {...(shared
              ? { onKey: (k: SharedKey) => void pressShared(shared.share_id, k).catch((e) => status(shareErrText(e))) }
              : {})}
            /* Narrow screens get their arrows from the composer's swipe d-pad, so the key
               row would be offering the same keys twice on the surface with the least
               room. Keyed to `narrow` — the same breakpoint the d-pad itself uses — and
               NOT to whether the d-pad is momentarily armed: that flips as you open a
               chat or type an @mention, and keys appearing and vanishing under your
               thumb is worse than one redundant row.
               SINCE 2026-10-07 THE SWIPE NO LONGER SENDS ← (and sends nothing while Clean
               View is open — useDpad.ts), so on a phone this row and the swipe together
               offer no Left arrow. That is the owner's ruling, not an oversight: at an
               empty Claude prompt Left moves the conversation to the background. A
               prompt sheet still offers ← → where a prompt needs them. */
            // A friend has no swipe d-pad (it writes to a pty), so the row keeps its arrows.
            hideArrows={narrow && !shared && !agentIsTerminal}
            shellKeys={agentIsTerminal}
          />
        )}
        {replying && (
          <div className="reply-chip" role="status">
            <Icon name="reply" size={14} />
            <span className="reply-chip-text">
              Replying to <b>{replying.name}</b>
              {replying.text ? <> — “{replying.text}”</> : null}
            </span>
            <button type="button" className="reply-chip-x" aria-label="Cancel reply" onClick={() => setReplyTo(null)}>
              <Icon name="close" size={14} />
            </button>
          </div>
        )}
        <div id="footrow">
          <div
            id="composerWrap"
            className={hasText ? 'has-text' : undefined}
          >
            {/*
              GATED AT THE RENDER, not at the two ways in. Typing `@` and tapping the
              button both end up setting `mention`, so checking here covers both — and
              covers the third entry point nobody has written yet, which is the one that
              would otherwise ship ungated.

              `read_guide === false` explicitly, not `!read_guide`: an older edge omits
              the field, and treating absent as "has not read" would make the picker
              unusable against an edge that simply predates this.
            */}
            {/* The guide gate is the TERMINAL flow's (the sender is my agent, who must
                know the protocol). In a chat the sender is the human — gating a room's
                picker on whichever terminal agent happens to be selected would block
                friends on a fact about somebody else's pane. */}
            {mention && !panelProject && guideUnread && (
              <GuideGate
                session={agent?.session ?? 'This agent'}
                pending={askGuide.isPending}
                error={askGuide.error ? String((askGuide.error as Error).message || askGuide.error) : null}
                onAgree={(auto) => {
                  if (auto) setGuideAutoApprove(true)
                  askGuide.mutate()
                }}
                onDecline={() => setMention(null)}
              />
            )}
            {/*
              THE ARRIVAL SHEET, same decision at a different moment. It yields to the
              `@` one above: both would name the same agent and ask the same question,
              and the `@` one is answering a thing the person is doing right now.
            */}
            {!mention && !panelProject && guidePromptFor && guidePromptFor === agent?.session && (
              <GuideGate
                arrival
                session={agent.session}
                pending={askGuide.isPending}
                error={askGuide.error ? String((askGuide.error as Error).message || askGuide.error) : null}
                onAgree={(auto) => {
                  if (auto) setGuideAutoApprove(true)
                  askGuide.mutate()
                }}
                onDecline={() => {
                  ignoreGuide(agent.session, agent.host, agent.guide_hash)
                  setGuidePromptFor(null)
                }}
              />
            )}
            {mention && (!!panelProject || !guideUnread) && (
              <AgentMention
                agents={mentionAgents}
                query={mention.query}
                onPick={insertMention}
                onDismiss={() => setMention(null)}
              />
            )}
            {/* The waiting-for-input banner used to sit here, above the composer, and
                it pushed the composer down the moment it appeared — under the user's
                hands, mid-sentence. The state is announced on the peek button
                instead, which is beside the agent chip and moves nothing. */}
            {staged.length > 0 && (
              <div id="projUploadTray" aria-label="Files to share">
                {staged.map((s2) => (
                  <div className={`pup-card${s2.err ? ' err' : ''}`} key={s2.id}>
                    <div className="pup-thumb">
                      {s2.url ? (
                        <img src={s2.url} alt="" />
                      ) : (
                        <Icon name="attach_file" size={16} />
                      )}
                    </div>
                    <div className="pup-meta">
                      <div className="pup-name" title={s2.file.name}>
                        {s2.file.name}
                      </div>
                      <div className="pup-sub">
                        {s2.err ?? formatBytes(s2.file.size)}
                      </div>
                      {!s2.err && s2.pct > 0 && (
                        <div className="pup-prog">
                          <div className="pup-prog-fill" style={{ width: `${s2.pct}%` }} />
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="pup-x"
                      aria-label={`Remove ${s2.file.name}`}
                      onMouseDown={keepFocus}
                      onClick={() => dropStaged(s2.id)}
                    >
                      <Icon name="close" size={12} label="Remove" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <textarea
              ref={composerEl}
              id="composer"
              // ONE line at rest (owner, 2026-09-12: 預設高度設定為一排 + 按鈕排就好); it grows
              // with the text (growComposer) up to the ceiling in shell.css.
              rows={1}
              onChange={(e) => {
                // UNCONTROLLED on purpose: NO setText here, so a keystroke does not
                // re-render App — that per-keystroke re-render of the whole shell
                // was the mobile "輸入超卡". We touch only cheap things:
                //  · typedAt stamp (a ref) — the input half of the busy heartbeat.
                //    Stamped on the keystroke, not on focus: a composer left focused
                //    while somebody reads is not input.
                //  · the throttled busy lease (composer text reaches the pty only on
                //    send, so its keystrokes have to claim here; terminal mode only).
                //  · an emptiness flip for the Send button (re-renders at most once
                //    per message, on empty↔non-empty).
                //  · grow the box + scan for an @mention (bails to null for ordinary
                //    text, so no render there either).
                if (searchModeRef.current) {
                  // The keyword box: grow, reflect emptiness, hand the query on — and
                  // nothing else (no typing lease, no @-scan, no draft save).
                  reflectHasText(e.currentTarget.value)
                  growComposer(e.currentTarget)
                  pushQuerySoon()
                  return
                }
                typedAt.current = Date.now()
                if (agent) termSock.current?.claimTyping()
                if (sendError) setSendError(null)
                reflectHasText(e.currentTarget.value)
                growComposer(e.currentTarget)
                syncMention(e.currentTarget)
                //  · remember it for THIS conversation, at most once per window, so
                //    leaving mid-sentence and coming back finds the sentence.
                saveDraftSoon()
              }}
              onSelect={(e) => {
                syncMention(e.currentTarget)
              }}
              onPaste={(e) => {
                /*
                 * PASTE A PHOTO ANYWHERE YOU CAN ATTACH ONE — owner, 2026-09-19.
                 *
                 * This read `if (!project) return`, so pasting a screenshot worked
                 * in a project chat and silently did nothing in a friend DM or an
                 * agent's composer — the same gate mistake the paperclip carried
                 * until this morning, in a control nobody thinks to test because
                 * pasting text still worked. `canAttach` is the one predicate the
                 * button and the drag-veil already use.
                 *
                 * And the files come from filesFromClipboard, not from
                 * `clipboardData.files` alone: on the phone he actually asked
                 * about, a pasted image can arrive through `items` with `files`
                 * empty.
                 */
                if (!canAttach) return
                const files = filesFromClipboard(e.clipboardData)
                if (files.length) {
                  e.preventDefault()
                  stage(files)
                }
              }}
              onBlur={() => setMention(null)}
              placeholder={
                // No 🎤 anywhere: the mic and the whole voice stack were deleted on
                // 2026-09-02 (owner: 語音先幫我完全刪除…以後我們會徹頭徹尾的設計一個新的),
                // and a placeholder inviting dictation would advertise a control that is
                // not there — the same rule the old VOICE_API_WIRED gate applied, now
                // unconditional. A future voice feature restores the phrasing WITH the button.
                searchMode
                  ? 'Search this conversation'
                  : project
                  ? `Message “${project.name ?? project.project_id}” — type, then Send`
                  : dmPeer
                    ? `Message ${dmProject?.name ?? (dmPeer.includes('@') ? dmPeer : 'them')} — Cmd+Enter to send`
                    : shared
                      ? // Speaking AS the sharer is the one thing a friend must not forget here.
                        shared.identity === 'owner'
                        ? narrow
                          ? `Type as ${sharedOwner}, then Send`
                          : `Message ${sharedName} as ${sharedOwner} — Cmd+Enter to send · Ctrl+M mic`
                        : narrow
                          ? 'Type, then Send'
                          : `Message ${sharedName} — Cmd+Enter to send · Ctrl+M mic`
                    : agent
                      ? narrow
                        ? // Kept to ONE line at phone width. The old text wrapped to two
                          // and the composer rests at that height, so a hint about a key
                          // combination no soft keyboard has was costing a row of terminal
                          // on every screen. ↵ is right there and does the same thing.
                          // The swipe is advertised only where it exists: it is off while
                          // Clean View is open (owner, 2026-10-07 — see the useDpad call).
                          cleanViewOpen
                          ? 'Type, then Send'
                          : 'Type · 🕹 swipe for arrows'
                          : // The two toggles ride the WIDE line ONLY — owner, 2026-09-15:
                            // 幫我在這一個 Text Editor 後面的提示文字裡面再加上一個
                            // `ctrl + m`: toggle microphone, `ctrl + j`: toggle terminal.
                            // Not in the narrow branch above, for the reason it already
                            // gives: no soft keyboard has a chord, and that hint was
                            // costing a row of terminal on every phone screen. Both chords
                            // take Ctrl OR Cmd (voice/micHotkey isChord; the Cmd/Ctrl+J
                            // handler) — one spelling is named because a placeholder that
                            // lists every alternative stops being read.
                            stagesBeforeSending(effectiveMode)
                          ? `${agent.session} — Cmd+Enter to stage, again to send · Ctrl+M mic · Ctrl+J terminal`
                          : `${agent.session} — Cmd+Enter to send · Ctrl+M mic · Ctrl+J terminal`
                      : 'Type, then Send'
              }
              {...(searchMode ? {} : dpad.handlers)}
              onKeyDown={(e) => {
                if (searchModeRef.current) {
                  // Enter steps to an older match, Shift+Enter to a newer one, Esc
                  // gives the box back. Nothing typed here reaches the agent.
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    stepSearch(e.shiftKey ? 1 : -1)
                    return
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault()
                    exitSearch()
                    return
                  }
                }
                // Cmd/Ctrl+Enter is handled by the document-level listener, so this
                // only stops the textarea from ALSO inserting a newline for it.
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault()
                  return
                }
                /*
                 * PLAIN ENTER IS A NEWLINE, in every view.
                 *
                 * It briefly sent, because I read the owner's "Enter should go straight
                 * to the terminal" as "the Enter key sends" when it meant "Cmd+Enter
                 * should reach the session without staging". Their default is the
                 * ordinary one: Enter breaks a line, and sending is a deliberate
                 * chord.
                 *
                 * So the only send gesture from the keyboard is Cmd/Ctrl+Enter, handled
                 * by the document-level listener above, and what it DOES still depends
                 * on the view: one press outside the terminal, stage-then-send inside
                 * it. That half was the actual request and is unchanged.
                 */
              }}
              title={
                shared
                  ? 'Enter: newline \u00b7 Cmd/Ctrl+Enter: send'
                  : agent && !inChat && !stagesBeforeSending(effectiveMode)
                  ? 'Enter: newline \u00b7 Cmd/Ctrl+Enter: send to the agent'
                  : 'Enter: newline \u00b7 Cmd/Ctrl+Enter: stage into the agent\u2019s input (again to send)'
              }
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
            />
            <button
              type="button"
              id="composerClear"
              title="Clear input"
              aria-label="Clear input"
              onMouseDown={keepFocus}
              onClick={() => {
                if (searchModeRef.current) {
                  setBoxOnly('')
                  setSearchQuery('')
                  return
                }
                setComposerValue('')
                setMention(null)
              }}
            >
              <Icon name="close" size={13} label="Clear input" />
            </button>
          </div>

          {/* ⌨ and @ on the left; ➤ is pushed right so it falls under the thumb —
              the auto margin sits on whichever cluster member renders first (see
              #footbtns in shell.css; #mic and #micMute were deleted with the voice
              stack on 2026-09-02 and the margin rule is written to survive that).
              Every button preventDefaults mousedown so tapping never steals focus
              from the composer (§5.1). */}
          <div id="footbtns">
            {/*
              ON A FRIEND'S AGENT ONLY WHEN THE SHARER GAVE THE KEYS (owner, 2026-10-17: 小鍵盤 B —
              an option, off): ⌨ types into the CLI itself — ⇧Tab switches Claude into accepting
              edits without asking, / opens its commands, ← parks the conversation — and a friend
              cannot see the terminal those keys act on. `@` is the friend's own agents (phase 2:
              the cloud links each one the friend pings with the shared agent).
            */}
            {(!shared || sharedKeys) && (
              <button
                type="button"
                id="keysBtn"
                className={keysOpen ? 'on' : undefined}
                title="Extra keys — Tab, ⇧Tab, /, arrows"
                aria-label="Toggle the extra-keys row"
                aria-expanded={keysOpen}
                onMouseDown={keepFocus}
                onPointerDown={(e) => {
                  keysSwiped.current = false
                  keysStartY.current = e.clientY
                  // Capture, so the swipe keeps reporting once the finger leaves the
                  // button — which it does immediately, the travel being upward.
                  try {
                    e.currentTarget.setPointerCapture(e.pointerId)
                  } catch {
                    /* older engines: the gesture just ends at the button's edge */
                  }
                }}
                onPointerMove={(e) => {
                  if (keysSwiped.current) return
                  const dy = e.clientY - keysStartY.current
                  // Two icon-heights, measured from the button itself rather than a fixed
                  // pixel count, so the threshold scales with the control.
                  const travel = 2 * e.currentTarget.getBoundingClientRect().height
                  if (-dy > travel) {
                    keysSwiped.current = true
                    // The shell drawer opens on the READER's machine — never from a friend's agent.
                    if (!shared) setDrawerOpen(true)
                    return
                  }
                  /**
                   * Down: put the keyboard away.
                   *
                   * iOS's accessory bar carries the only system control for this, and
                   * hiding that bar — which we want, it is a wasted strip — takes the
                   * dismiss with it. Nothing else on a phone puts the keyboard down
                   * except tapping somewhere inert, and on this screen everything is
                   * live: the terminal takes the tap, the composer takes it back.
                   *
                   * The keyboard follows focus, so this is a blur. It reads as pushing
                   * the keyboard down because the gesture goes the same way it does.
                   */
                  if (dy > travel) {
                    keysSwiped.current = true
                    ;(document.activeElement as HTMLElement | null)?.blur?.()
                  }
                }}
                onClick={() => {
                  if (keysSwiped.current) {
                    keysSwiped.current = false
                    return
                  }
                  toggleKeys()
                }}
                onDoubleClick={() => {
                  // Double-tap pins the key row so it survives keyboard toggles.
                  const next = !keysLocked
                  setKeysLocked(next)
                  if (next) setKeysOpen(true)
                  try {
                    localStorage.setItem('wt_keys_locked', next ? '1' : '0')
                  } catch {
                    /* preference just won't persist */
                  }
                }}
              >
                <Icon name="keyboard" size={19} label="Extra keys" />
              </button>
            )}
            {/* ONE gesture: tap types `@`. The long-press used to open the "Agent
                messaging" sheet (Onboarding guide / This agent) — owner, 2026-09-13:
                那兩份文件都已經是過期的, 沒有什麼作用, 可以直接從 UI 上面拿掉. The sheet,
                its hold timer and its two fetches went with it. On an edge that says
                `guide_on_ping` the guide needs no door here at all — the first `@`
                carries it (agents/guidePrompt.ts); the arrival prompt and the `@` gate
                (GuideGate) remain only for an edge too old to. */}
            {(!shared || sharedOpen) && (
              <button
                type="button"
                id="atBtn"
                title="Mention an agent"
                aria-label="Mention an agent"
                onMouseDown={keepFocus}
                onClick={() => {
                  const el = composerEl.current
                  const next = `${el?.value ?? ''}@`
                  setComposerValue(next)
                  // Focus + caret so the picker opens exactly as if you had typed it.
                  requestAnimationFrame(() => {
                    if (!el) return
                    el.focus()
                    el.setSelectionRange(next.length, next.length)
                    setMention(findMention(next, next.length))
                  })
                }}
              >
                @
              </button>
            )}
            {/* Attaching is not a project feature. A terminal agent reads files off
                this machine, so the same tray and the same button serve both — what
                differs is only what pressing send does with them, and that lives in
                the two submit paths rather than in two buttons. */}
            {(panelProject || agent || shared) && (
              <>
                <button
                  type="button"
                  id="projAttachFileBtn"
                  title="Attach files"
                  aria-label="Attach files"
                  onMouseDown={keepFocus}
                  onClick={() => projFileInput.current?.click()}
                >
                  <Icon name="attach_file" size={18} label="Attach files" />
                </button>
                <input
                  ref={projFileInput}
                  id="projFileInput"
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    stage([...(e.target.files ?? [])])
                    e.target.value = ''
                  }}
                />
                {/* Held mail, beside the other thing you can put into a message.
                    Hidden at zero (owner, 2026-08-07), so it is a NOTIFICATION rather
                    than a readout — this row is where you are already looking when you
                    write, so appearing is enough to be seen, which is not true of a
                    chip parked in a corner. */}
                {/* SEARCH — owner, 2026-09-11: 在 text input 下方新增一個搜尋按鈕, 就放在
                    上傳檔案按鈕的右邊. The corpus is the blocks Clean View holds, so the
                    button exists for every agent that HAS a Clean View — and pressed in
                    Terminal View it switches there first (owner, 2026-09-12: 讓搜尋按鈕也
                    出現在 Terminal View 下面 … 按下去就會自動跳入 Clean View 的狀態). An
                    agent whose kind has no rich view (auto-detect resolves to the
                    terminal) gets no button: there would be nothing to search. */}
                {searchAvailable && (
                  <button
                    type="button"
                    id="searchBtn"
                    className={searchMode ? 'on' : undefined}
                    title={
                      searchMode
                        ? 'Close search (Esc)'
                        : viewMode === 'terminal-view'
                          ? 'Search this conversation (Cmd/Ctrl+F · opens Clean View)'
                          : 'Search this conversation (Cmd/Ctrl+F)'
                    }
                    aria-label={searchMode ? 'Close search' : 'Search this conversation'}
                    aria-pressed={searchMode}
                    onMouseDown={keepFocus}
                    onClick={() => (searchModeRef.current ? exitSearch() : startSearch())}
                  >
                    <Icon name="search" size={18} label={searchMode ? 'Close search' : 'Search this conversation'} />
                  </button>
                )}
                {/* The held mail of THIS account's agents — nothing of a friend's. */}
                {!shared && <QueueBell />}
              </>
            )}
            {/* STOP — owner, 2026-09-13: a button that appears while the agent works and
                does what Escape does. First placed in the text box's top-right corner;
                moved into this row, LEFT of the microphone, the same day (owner: 放在右上角
                並不是一個很好的設計, 放到下面的按鍵列裡, 就放在麥克風的左邊, 這樣不會跟文字互相
                打架). Shown only while the pushed busy switch says the agent is working
                (presenceFor — never the fetched row's status), with an agent open outside
                a chat; one press writes Escape to the agent's own pty, the byte the ⌨
                row's Esc writes. It takes the thumb cluster's auto margin when it is
                the first member; the mic's reset below hands it on (see #footbtns). */}
            {agent && !inChat && agentWorking && (
              <button
                type="button"
                id="stopBtn"
                title={`Stop what the agent is doing (sends ${agentKind === 'hermes' ? 'Ctrl+C' : 'Escape'})`}
                aria-label="Stop the agent"
                disabled={stopHeld}
                onMouseDown={keepFocus}
                onClick={() => {
                  // Per kind (stopKey.ts): Hermes ignores Escape and stops on ONE Ctrl+C —
                  // two within two seconds would make it exit, so the button holds after a press.
                  const key = stopKeyFor(agentKind)
                  termSock.current?.write(key.bytes)
                  status(key.said)
                  if (key.holdMs > 0) {
                    setStopHeld(true)
                    window.setTimeout(() => setStopHeld(false), key.holdMs)
                  }
                }}
              >
                {/* Just the red square (owner: 我只需要那個紅色的方框); the word is the label. */}
                <span className="stop-glyph" aria-hidden="true" />
              </button>
            )}
            {/* The same Stop for a friend's agent: the cloud asks their machine for the owner's
                own key for its kind, and only while it works (agentInterrupt on the edge). */}
            {shared && sharedOpen && sharedLive.working && (
              <button
                type="button"
                id="stopBtn"
                title="Stop what the agent is doing"
                aria-label="Stop the agent"
                disabled={stopHeld}
                onMouseDown={keepFocus}
                onClick={() => void stopShared()}
              >
                <span className="stop-glyph" aria-hidden="true" />
              </button>
            )}
            {/* The microphone, immediately LEFT of send (owner's placement). It joins
                the thumb cluster on the right — see the #micBtn rules in shell.css for
                why the auto margin moves onto it. The clock replaces the icon while
                recording, so the button itself says how long the clip is. */}
            {(() => {
              const rec = voice.state === 'recording'
              // 'stopping' is busy too: the press has been accepted and the browser
              // has not finished yet. Leaving it out is the bug this state was added
              // for — the button would stay live and swallow the next press.
              const busy =
                voice.state === 'starting' || voice.state === 'stopping' || voice.state === 'uploading'
              const secs = Math.floor(voice.elapsedMs / 1000)
              const clock = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`
              return (
                <button
                  type="button"
                  id="micBtn"
                  className={rec ? 'rec' : busy ? 'busy' : undefined}
                  title={
                    rec
                      ? `Stop recording and insert the words (${MIC_HOTKEY_LABEL})`
                      : voice.state === 'stopping'
                        ? 'Finishing the recording…'
                        : voice.state === 'uploading'
                          ? 'Transcribing…'
                        : `Record (up to ${Math.round(MAX_RECORD_MS / 60000)} minutes), then insert the words at the caret (${MIC_HOTKEY_LABEL})`
                  }
                  aria-label={rec ? 'Stop recording' : 'Record voice input'}
                  aria-pressed={rec}
                  disabled={busy}
                  onMouseDown={keepFocus}
                  onClick={micPress}
                >
                  {rec ? (
                    <span className="mic-clock" aria-live="off">{clock}</span>
                  ) : (
                    <Icon
                    name="mic"
                    size={19}
                    label={
                      voice.state === 'stopping'
                        ? 'Finishing the recording'
                        : busy
                          ? 'Transcribing'
                          : 'Record voice input'
                    }
                  />
                  )}
                </button>
              )
            })()}
            {(() => {
              const hasContent = hasText
              return (
                <button
                  type="button"
                  id="sendBtn"
                  /*
                   * The label is DERIVED from the same predicate as the behaviour,
                   * rather than describing it separately.
                   *
                   * It used to say "Stage into the agent's input (Cmd+Enter)" in every
                   * view. In Clean View that is two claims and both are false: nothing
                   * is staged there, and Cmd+Enter does not stage either — it goes to
                   * the same `terminalSubmit`, which sends in one press. The owner read
                   * the label and reported that only the button sent directly, which is
                   * exactly what a caption that contradicts its button produces.
                   *
                   * A separately-worded label is a second implementation of the rule,
                   * and it drifted the moment the rule changed. Sharing
                   * `stagesBeforeSending` is what stops that happening again.
                   */
                  title={
                    inChat || shared
                      ? 'Send message'
                      : !stagesBeforeSending(effectiveMode)
                        ? hasContent
                          ? 'Send to the agent (Cmd+Enter)'
                          : 'Send Enter to the terminal (Cmd+Enter)'
                        : hasContent
                          ? 'Stage into the agent’s input (Cmd+Enter)'
                          : 'Send Enter to the terminal (Cmd+Enter)'
                  }
                  aria-label={
                    inChat || shared
                      ? 'Send message'
                      : !hasContent
                        ? 'Send Enter'
                        : stagesBeforeSending(effectiveMode)
                          ? 'Stage into the agent’s input'
                          : 'Send to the agent'
                  }
                  onMouseDown={keepFocus}
                  onClick={submitFromControl}
                  disabled={sending || ((inChat || !!shared) && !hasContent && staged.length === 0)}
                >
                  <Icon
                    name={hasContent || shared ? 'send' : 'keyboard_return'}
                    size={18}
                      label={
                        !hasContent
                          ? 'Send Enter'
                          : stagesBeforeSending(effectiveMode)
                            ? 'Stage into terminal'
                            : 'Send to the agent'
                      }
                  />
                </button>
              )
            })()}
          </div>
        </div>
        {/* Pull down to put the keyboard away.
         *
         * iOS puts the only system dismiss control on the accessory bar, so hiding
         * that bar takes the dismiss with it — and on this screen there is nowhere
         * inert to tap instead: the terminal takes the tap, the composer takes it
         * straight back. This is the replacement, in the place the old control was,
         * and it is a handle rather than a button because the gesture says which way
         * the keyboard is going.
         *
         * Only while the keyboard is up. A handle that dismisses nothing is worse
         * than no handle. */}
        <div
          id="kbGrab"
          role="button"
          tabIndex={-1}
          aria-label="Pull down to hide the keyboard"
          onPointerDown={(e) => {
            grabStartY.current = e.clientY
            try {
              e.currentTarget.setPointerCapture(e.pointerId)
            } catch {
              /* older engines: the drag ends at the handle's edge */
            }
          }}
          onPointerMove={(e) => {
            // Short on purpose: the handle sits at the very bottom, so there is not
            // much room left to drag into before the finger is off the screen.
            if (grabStartY.current && e.clientY - grabStartY.current > 24) {
              grabStartY.current = 0
              ;(document.activeElement as HTMLElement | null)?.blur?.()
            }
          }}
          onPointerUp={() => {
            grabStartY.current = 0
          }}
        >
          <span className="kg-bar" aria-hidden="true" />
        </div>
      </div>

      {/*
        * The way in is Google, and it is the ONLY gate.
        *
        * This replaces the vault's three-way box — needs_setup / locked / unknown —
        * with one question: is there a session. avengents-homepage's breakdown is why
        * that is a simplification rather than a loss: the vault did three jobs, and
        * OAuth cleanly takes two (who you are, and may you in). The third — holding an
        * Ed25519 identity key and the chat keys — it does not do and cannot, but the
        * browser does not need those here: it talks to AWS, and AWS talks to the router.
        *
        * WIRED HERE, WHICH IS THE PART I MISSED. The component existed and its tests
        * passed, and the app went on rendering `VaultGate` because nothing called it —
        * the owner opened the page and saw the old box. A correct component nobody
        * invokes is the same defect as a correct function nobody invokes, which I
        * fixed twice today before repeating it.
        */}
      {/*
        * The gate moved OUT of here, into `Authed` around this whole component.
        *
        * Rendering it as an overlay meant the app behind it mounted and queried while
        * the user was still signed out — a console full of 404s about a surface nobody
        * had reached yet. Holding it back is the fix; leaving a second copy here would
        * put a sign-in dialog on top of an app that only renders when signed in.
        */}
      {noteOpen && (
        <NotePanel
          agentRef={agent && !agentIsTerminal ? { host: agent.host, session: agent.session } : null}
          agents={rosterList}
          draft={noteDraft}
          onDraft={setNoteDraft}
          ghosts={ghosts}
          onStatus={status}
          onClose={() => setNoteOpen(false)}
        />
      )}
      {bmOpen && bmRoomConv && roomBmOffered && (
        <BookmarkPanel
          agentName={project?.name ?? 'This project'}
          list={roomBm.list.map((b) => toPanelRow(bmRoomConv, b))}
          loading={roomBm.loading}
          error={roomBm.error}
          errorText="Your bookmarks in this project could not be read. Try again in a moment."
          currentFile={bmRoomConv}
          onOpen={roomBm.refetch}
          onRemove={(id) => roomBm.letGo(Number(id))}
          onClose={() => setBmOpen(false)}
          onGo={(b) => {
            setBmOpen(false)
            jumpTo({
              device: null,
              session: roomJumpSession(bmRoomConv),
              file: bmRoomConv,
              index: b.index,
              onResult: (r) => {
                if (r !== 'shown') status('That message is no longer in this project — its words are gone with it.')
              },
            })
          }}
        />
      )}
      {bmOpen && bmAgent && (
        <BookmarkPanel
          agentName={bmAgent.session}
          list={bookmarks.list}
          loading={bookmarks.loading}
          error={bookmarks.error}
          currentFile={shownFile(bmAgent.device_id ?? null, bmAgent.session)}
          onOpen={bookmarks.refetch}
          onRemove={(id) => bookmarks.remove(id)}
          onClose={() => setBmOpen(false)}
          onGo={(b) => {
            setBmOpen(false)
            // From the terminal: the message is in the conversation, which takes the jump once it has drawn.
            if (viewMode === 'terminal-view') setViewMode('auto-detect')
            jumpTo({
              device: bmAgent.device_id ?? null,
              session: bmAgent.session,
              file: b.file,
              index: b.index,
              onResult: (r) => {
                if (r === 'elsewhere') status('That bookmark is from an earlier conversation of this agent.')
                if (r === 'unreachable') status('That part of the conversation could not be opened on its machine just now — its words are in the bookmark.')
              },
            })
          }}
        />
      )}
      {/* "Today's voice allowance is gone" — summoned by either microphone
          (billing/quotaPrompt), answered here, once, above everything. */}
      <QuotaSheet />
    </>
  )
}
